import "./styles.css";
import "./section-header.css";
import { createSectionSettingsDock } from './section-settings';
import { parseChatSearchEntry } from './chat-search';
import { RESTAURANTS_START_PARAMETER, restaurantEntryCopy } from './business/navigation';
import { IS_ADMIN_PREVIEW } from './admin-preview';
import { installBackNavigation, setBackMenu } from './back-navigation';
import { businessPreviewAllowed } from './business/access';
import { websiteProductPath } from "./website-routes";
import { matchesWebsiteProductGroup, websiteProductGroup } from "./website-product-groups";
import { websiteOfferActive } from "./website-offers";
import { mealIdeasAllowed } from "./meal-ideas-access";
import { CATALOG_INTERACTIONS_ENABLED, catalogProductsAtPoint, CatalogTapTracker } from "./catalog-interactions";
import type { MealPilot } from "./meals/view";
import { fuelEntryCopy, fuelAvailable } from "./fuel-access";
import type { FuelPanel } from "./fuel/view";
import { parseFuelNavigation } from "./fuel/favorites";
import { calculateShoppingSavings, SHOPPING_SAVINGS_ENABLED, type ShoppingSavingsSummary } from "./shopping-savings";
import { requiredCardText, conditionalPriceText, productDisplayPrice, productDisplayNote,
  type RequiredCard } from "./price-conditions";

import { recordAdCampaignOpen } from "./ad-attribution";
import {
  ActivityAccumulator,
  type ActivityCounterKey,
} from "./activity";

import {
  activeCatalogDataset,
  catalogExpiresTodayOrTomorrow,
  dateKeyInMoldova,
  filterProducts,
  flattenProducts,
  formatCompactCatalogDate,
  formatPrice,
  formatValidity,
  nextVisibleCount,
  uniqueStores,
} from "./catalog";
import {
  buildComparisonCatalog,
  comparisonFamiliesForSearch,
  comparisonFamiliesWithMatchesFirst,
  comparisonStarterShelfEnabledForSurface,
  orderComparisonFamilies,
  type ComparisonStarterFamily,
  type ComparisonStarterFamilyId,
} from "./comparison-starter-shelf";
import { COMPARISON_PAGE_SIZE, comparisonShelfMarkup, comparisonShelfGridMarkup,
  type ComparisonShelfMode, type ComparisonShelfView } from "./comparison-shelf-view";
import { discoveryFeedContext } from "./feed-ranking";
import { createDiscoveryFeedLifecycle, createDiscoveryFeedVisit, type DiscoveryLifecycleSource } from "./feed-visit";
import { createVisitFilterHydration, filtersForNewVisit } from "./filter-visit";
import {
  buildSearchRecovery,
  SEARCH_RECOVERY_PREVIEW_SIZE,
  type SearchRecovery,
  type SearchRecoveryLabel,
} from "./search-recovery";
import { orderCatalogLibraryCatalogs } from "./catalog-library";
import { prepareProductSearch } from "./product-search";
import { MAX_SEARCH_ITEMS, MAX_SEARCH_TEXT, parseProductSearchQuery, productSearchPasteText } from "./search-query";
import { qualifiedBrandQuery } from "./search-brands";
import { buildMultiSearchGroups, multiSearchDistinctProducts, multiSearchMarkup, multiSearchPreview,
  MULTI_SEARCH_PAGE, type MultiSearchGroup } from "./multi-product-search";
import {
  createFilterPanelStabilityCheck,
  validatedFilterPanelHeights,
} from "./filter-panel-stability";
import {
  CATALOG_ZOOM_BUTTON_SCALE,
  CATALOG_ZOOM_MIN,
  catalogPinchZoomScale,
  catalogOverlayGeometry,
  catalogPageImage,
  productCatalogPage,
  catalogProductMarkerGroups,
  catalogProductsOnPage,
  clampCatalogZoomScale,
  clampCatalogPage,
  separateCatalogMarkerAnchors,
} from "./catalog-browser";
import {
  checkTelegramHomeScreenStatus,
  hapticSuccess,
  hapticTap,
  initializeTelegram,
  onTelegramActivated,
  openTelegramShareLink,
  onTelegramHomeScreenAdded,
  openTelegramBotStart,
  requestTelegramHomeScreenShortcut,
  requestTelegramWriteAccess,
  sharePreparedTelegramMessage,
  telegramHomeScreenPlatform,
  telegramStartParameter,
  telegramUserAllowsWrite,
  telegramUserIdentifier,
  telegramUserLanguageCode,
  type TelegramHomeScreenStatus,
} from "./telegram";
import { AppShareApiError, prepareAppShare } from "./app-share";
import {
  APP_INVITE_START_PARAMETER,
  initialAppEntrySource,
} from "./share-entry";
import { connectBotAccess, loadBotAccessStatus } from "./bot-access";
import {
  deletePriceWatch,
  loadPriceWatches,
  markPriceAlertsRead,
  PriceWatchApiError,
  savePriceWatch,
  togglePriceWatch,
} from "./price-watch-api";
import {
  productPriceWatchDraft,
  newPriceWatchStores,
  priceWatchBadgeCounts,
  priceAlertImage,
  groupPriceWatchResults,
  MAX_PRICE_WATCH_ALERTS,
  queryPriceWatchDraft,
  refreshProductPriceWatchDraft,
  type PriceAlertEvent,
  type PriceWatchRule,
  type PriceWatchRuleDraft,
  type PriceWatchState,
} from "./price-watch-model";
import {
  adjustShoppingQuantity,
  calculateShoppingMoneySummary,
  EXPIRED_OFFER_RETENTION_DAYS,
  mergeShoppingLists,
  parseShoppingList,
  pruneExpiredShoppingItems,
  removeExpiredShoppingItems,
  serializeShoppingList,
  setShoppingQuantity,
  SHOPPING_LIST_STORAGE_KEY,
  shoppingCatalogDisplayTitle,
  shoppingItemsByLifecycle,
  shoppingItemsByStoreAndCatalog,
  shoppingOfferExpired,
  shoppingQuantityTotal,
  type ShoppingCatalogGroup,
  type ShoppingListItem,
  type ShoppingMoneyTotal,
  type ShoppingStoreGroup,
  type ShoppingStoreMoneyTotal,
} from "./shopping-list";
import {
  clampQuantityControlAnchor,
  productQuantityControlMarkup,
  updateProductQuantityControl,
  type QuantityControlKind,
  type QuantityControlView,
} from "./product-quantity-control";
import { productWatchControlMarkup, updateProductWatchControl, type ProductWatchControlView } from "./product-watch-control";
import { createStoreButtonGlow } from "./store-button-glow";
import { FuelDiscovery, FUEL_DISCOVERY_KEY } from "./fuel/discovery";
import {
  loadSharedShoppingList,
  parseSharedListItemStartParameter,
  parseSharedListStartParameter,
  prepareShoppingListShare,
  resolveSharedListItemLanding,
  SharedListApiError,
  type SharedListItemStartParameter,
  type SharedShoppingList,
} from "./shared-lists";
import { scopedShoppingShareItems, shoppingSharingAvailable } from "./shopping-sharing";
import {
  createSharedProductSnapshot,
  loadSharedProduct,
  parseDirectProductStartParameter,
  parseSharedProductStartParameter,
  prepareProductShare,
  productViewFromSharedSnapshot,
  SharedProductApiError,
} from "./shared-products";
import {
  createSharedCatalogSnapshot,
  isCatalogLibraryStartParameter,
  parseSharedCatalogStartParameter,
  prepareCatalogShare,
  SharedCatalogApiError,
  sharedCatalogTelegramUrl,
} from "./shared-catalogs";
import {
  hasPreferenceDraftChanges,
  isStorePreferencesStartParameter,
  parsePersonalFilterModes,
  parsePersonalFilterPreferences,
  PERSONAL_CATEGORIES_FILTER,
  PERSONAL_FILTER_MODES_STORAGE_KEY,
  PERSONAL_FILTERS_STORAGE_KEY,
  PERSONAL_STORES_FILTER,
  selectedOptionsFirst,
  serializePersonalFilterModes,
  serializePersonalFilterPreferences,
  storeDirectory,
  storeFilterOptions,
  type PersonalFilterMode,
  type PersonalFilterModes,
  type PersonalFilterPreferences,
} from "./personal-filters";
import {
  applyUserStateMutation,
  mergeUserStateMutations,
  shoppingMutation,
  type UserFilterSelection,
  type UserState,
  type UserStateMutation,
} from "./user-state";
import {
  isTerminalUserStateSyncError,
  parsePendingUserStateMutation,
  syncUserState,
  userStateSyncRetryDelay,
  USER_STATE_BOOTSTRAP_KEY,
  USER_STATE_PENDING_KEY,
} from "./user-state-sync";
import type {
  Catalog,
  CatalogDataset,
  GridMode,
  ProductCategory,
  ProductFilters,
  ProductView,
  SortMode,
  UnitPriceBasis,
} from "./types";
import {
  AUTO_THEME_FALLBACK_COLORS,
  FIXED_THEME_COLORS,
  initialThemePreference,
  normalizeThemePreference,
  resolveEffectiveTheme,
  type EffectiveTheme,
  type ThemePreference,
} from "./theme";
import {
  activateLocale,
  applyDocumentTranslations,
  catalogWord,
  formatUiNumber,
  offerWord,
  productWord,
  preloadLocale,
  t,
} from "./i18n";
import {
  detectAppLocale,
  formatWeightOrVolume,
  localized,
  localeTag,
  normalizeAppLocale,
  type AppLocale,
} from "./locale";
import { sourceLanguageBucket } from "./language";
import { enableHorizontalWheelScroll } from "./horizontal-scroll";
import { createDocumentScrollLock } from "./document-scroll-lock";
import {
  dismissHomeScreenPromptProgress,
  homeScreenAutomaticEligible,
  HOME_SCREEN_EXPERIMENT_TEST_START_PARAMETER,
  homeScreenExperimentIsStarted,
  homeScreenExperimentVariant,
  markHomeScreenAdded,
  markHomeScreenPromptShown,
  markHomeScreenUnsupported,
  parseHomeScreenPromptProgress,
  type HomeScreenExperimentVariant,
  type HomeScreenPromptProgress,
} from "./home-screen-experiment";
import {
  isHomeScreenShortcutStartParameter,
  isPriceAlertsStartParameter,
  isShoppingListStartParameter,
  parsePriceWatchSettingsStartParameter,
} from "./bot-command-routes";
import {
  buildProductRelationshipIndex,
  productAnalogues,
  relatedProductKey,
  similarProducts,
  type ProductAnalogue,
} from "./related-products";
import {
  publicAssetUrl,
  readWebsiteRouteContext,
  expireWebsiteOffers,
  websiteHomeUrl,
  websiteLocalizedUrl,
} from "./site-runtime";
import {
  catalogBrowsingQualifiesForStorePrompt,
  dismissStorePrompt,
  extendStoreSavePromptCooldown,
  parseStorePromptDismissals,
  parseStoreSavePromptNextEligibleAt,
  savedStoreCountAllowsStorePrompt,
  STORE_SAVE_PROMPT_DISMISS_COOLDOWN_MS,
  STORE_SAVE_PROMPT_SHOW_COOLDOWN_MS,
  storeSavePromptGlobalCooldownIsActive,
  storePromptIsCoolingDown,
  storeSavePromptVariant,
  type StoreSavePromptVariant,
} from "./store-save-prompt";

const PAGE_SIZE = 24;
const SIMILAR_PRODUCTS_MAX_COUNT = 12;
const GRID_MODE_KEY = "kiloprice.grid-mode.v1";
const SORT_MODE_KEY = "kiloprice.sort-mode.v1";
const FILTER_SELECTION_KEY = "kiloprice.filter-selection.v1";
const LOCALE_KEY = "kiloprice.locale.v1";
const THEME_PREFERENCE_KEY = "kiloprice.theme-preference.v1";
const USER_STATE_SYNC_DELAY_MS = 500;
const ACTIVITY_SYNC_DELAY_MS = 30_000;
const SEARCH_ACTIVITY_DELAY_MS = 700;
const USER_STATE_REFRESH_INTERVAL_MS = 30_000;
const BOT_ACCESS_PROMPT_DELAY_MS = 5_000;
const BOT_ACCESS_DISMISS_COOLDOWN_MS = 12 * 60 * 60 * 1_000;
const BOT_ACCESS_LEGACY_DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1_000;
const BOT_ACCESS_CONNECTED_KEY = "kiloprice.bot-access.connected.v1";
const BOT_ACCESS_DISMISSED_UNTIL_KEY =
  "kiloprice.bot-access.dismissed-until.v2";
const BOT_ACCESS_LEGACY_DISMISSED_UNTIL_KEY =
  "kiloprice.bot-access.dismissed-until.v1";
const HOME_SCREEN_EXPERIMENT_ENABLED = true;
const HOME_SCREEN_PROMPT_PROGRESS_KEY =
  "kiloprice.home-screen-prompt.progress.v1";
const HOME_SCREEN_USEFUL_VISIT_KEY = "kiloprice.home-screen-prompt.useful-visit.v2";
const STORE_SAVE_PROMPT_EXPERIMENT_ENABLED = true;
const STORE_SAVE_PROMPT_DISMISSALS_KEY =
  "kiloprice.store-save-prompt.dismissals.v1";
const STORE_SAVE_PROMPT_NEXT_ELIGIBLE_AT_KEY =
  "kiloprice.store-save-prompt.next-eligible-at.v1";
type ChoiceKind = "sort";
type PreferenceKind = "stores" | "categories";
type CatalogViewerReturn =
  | { kind: "library" }
  | {
      kind: "product";
      product: ProductView;
      productHistory: readonly ProductView[];
    }
  | null;

const SORT_MODES: readonly SortMode[] = [
  "default",
  "price-asc",
  "discount-desc",
  "catalog-order",
];
const telegram = initializeTelegram();
if (IS_ADMIN_PREVIEW) installBackNavigation(telegram.webApp?.BackButton, document, () => location.assign('/' + location.hash));
const homeScreenPlatform = telegramHomeScreenPlatform(telegram);
const telegramUserId = telegramUserIdentifier(telegram);
const initialStartParameter = telegramStartParameter(telegram);
const storeButtonGlow = createStoreButtonGlow({
  preferencesReady: !telegram.webApp?.initData,
});
const homeScreenLocalPreview =
  !__KILOPRICE_WEBSITE__ &&
  ["127.0.0.1", "localhost"].includes(window.location.hostname) &&
  new URLSearchParams(window.location.search).get("home-screen") ===
    "preview";
const homeScreenTesterPilot =
  homeScreenLocalPreview ||
  (telegram.isTelegram &&
    initialStartParameter === HOME_SCREEN_EXPERIMENT_TEST_START_PARAMETER);
const homeScreenCommandLaunch =
  telegram.isTelegram &&
  isHomeScreenShortcutStartParameter(initialStartParameter);
const homeScreenDirectLaunch =
  homeScreenTesterPilot || homeScreenCommandLaunch;
const homeScreenVariant: HomeScreenExperimentVariant =
  homeScreenExperimentVariant({
    enabled:
      HOME_SCREEN_EXPERIMENT_ENABLED && homeScreenExperimentIsStarted(),
    userId: telegramUserId,
    startParameter: initialStartParameter,
  });
const storeSavePromptLocalPreview =
  !__KILOPRICE_WEBSITE__ &&
  ["127.0.0.1", "localhost"].includes(window.location.hostname) &&
  new URLSearchParams(window.location.search).get("store-save-prompt") ===
    "preview";
const storeSavePromptExperimentVariant: StoreSavePromptVariant =
  storeSavePromptVariant({
    enabled: STORE_SAVE_PROMPT_EXPERIMENT_ENABLED,
    userId: telegramUserId,
    startParameter: initialStartParameter,
    localPreview: storeSavePromptLocalPreview,
  });
const comparisonStarterShelfEnabled = comparisonStarterShelfEnabledForSurface({
  website: __KILOPRICE_WEBSITE__,
});
let mealPilot: MealPilot | null = null;
let mealPilotLoading = false;
let fuelPanel: FuelPanel | null = null;
let fuelPanelLoading = false;
const fuelDiscovery = new FuelDiscovery({
  read: () => localStorage.getItem(userScopedStorageKey(FUEL_DISCOVERY_KEY)),
  write: () => localStorage.setItem(userScopedStorageKey(FUEL_DISCOVERY_KEY), "1"),
}, !telegram.webApp?.initData);

function updateFuelDiscovery(): void {
  const button = select<HTMLButtonElement>("#fuel-button");
  button.classList.toggle("fuel-discovery-cue", fuelAvailable(__KILOPRICE_WEBSITE__) && fuelDiscovery.highlight);
  button.classList.toggle("fuel-discovery-paused", document.visibilityState !== "visible");
}

function recordFuelActivity(key: ActivityCounterKey, amount = 1): void {
  // FuelActivity emits this only after showModal succeeds, including deep links.
  if (key === "fuelSectionOpens" && fuelDiscovery.opened()) {
    updateFuelDiscovery();
    queueUserStateMutation({ fuelVisited: true });
  }
  queueActivity(key, amount);
  if (["fuelStationOpens", "fuelRouteOpens", "fuelNearbyUses", "fuelFavoriteAdds", "fuelAlertEnables"].includes(key)) {
    signalHomeScreenUsefulAction();
  }
}

let businessOwnerAccess = false;
let businessPublicAccess = false;
let nextBusinessAvailabilityCheck=0;
async function refreshPublicBusinessAccess():Promise<void>{
  if(__KILOPRICE_WEBSITE__||IS_ADMIN_PREVIEW||Date.now()<nextBusinessAvailabilityCheck)return;
  nextBusinessAvailabilityCheck=Date.now()+30_000;
  try{
    const response=await fetch('/api/business/public/status',{signal:AbortSignal.timeout(8000)});
    if(!response.ok)return;
    businessPublicAccess=(await response.json()).available===true;
    updateBusinessAccess();
  }catch{/* Business availability never blocks groceries or fuel. */}
}
document.addEventListener('visibilitychange',()=>{if(document.visibilityState==='visible')void refreshPublicBusinessAccess();});
let businessPanel: ReturnType<typeof import("./business/view").createBusinessPanel> | null = null;
let businessLoading = false;
function updateBusinessAccess(): void {
  const copy = restaurantEntryCopy[appLocale];
  select<HTMLElement>("#business-entry").hidden = __KILOPRICE_WEBSITE__ || !(businessOwnerAccess||businessPublicAccess);
  select<HTMLElement>("#business-entry-title").textContent = copy.title;
  select<HTMLElement>("#business-entry-subtitle").textContent = copy.subtitle;
}
async function openBusinessPanel(slug?: string): Promise<void> {
  if (__KILOPRICE_WEBSITE__ || !(businessOwnerAccess||businessPublicAccess) || businessLoading) return;
  businessLoading = true;
  const button = select<HTMLButtonElement>("#business-button");
  button.disabled = true;
  try {
    const { createBusinessPanel } = await import("./business/view");
    businessPanel ??= createBusinessPanel({ publicDirectory:!IS_ADMIN_PREVIEW,locale: () => appLocale, initData: () => telegram.webApp?.initData ?? "", mountSettings:host=>sectionSettings.mount(host), onClose: () => { button.setAttribute("aria-expanded", "false"); button.focus(); } });
    await businessPanel.open(slug); button.setAttribute("aria-expanded", "true");
  } catch {
    select<HTMLElement>("#business-entry-status").textContent = { ru: "Не удалось открыть раздел. Попробуйте ещё раз.", ro: "Secțiunea nu s-a deschis. Încearcă din nou.", uk: "Не вдалося відкрити розділ. Спробуйте ще раз." }[appLocale];
  } finally { businessLoading = false; button.disabled = false; }
}
function updateFuelAccess(): void {
  updateBusinessAccess();
  const allowed = fuelAvailable(__KILOPRICE_WEBSITE__);
  select<HTMLElement>("#fuel-entry").hidden = !allowed;
  document.documentElement.classList.toggle("has-fuel", allowed);
  select<HTMLElement>("#fuel-entry-title").textContent = fuelEntryCopy[appLocale].title;
  select<HTMLElement>("#fuel-entry-subtitle").textContent = fuelEntryCopy[appLocale].subtitle;
  if (!allowed) fuelPanel?.close();
  updateFuelDiscovery();
}

async function openFuelPanel(stationId?: string, mine = false, settings = false): Promise<void> {
  if (!fuelAvailable(__KILOPRICE_WEBSITE__) || fuelPanelLoading) return;
  const button = select<HTMLButtonElement>("#fuel-button");
  const status = select<HTMLElement>("#fuel-entry-status");
  fuelPanelLoading = true; button.disabled = true; status.textContent = "";
  try {
    const { createFuelPanel } = await import("./fuel/view");
    fuelPanel ??= createFuelPanel({ locale: () => appLocale, dark: () => effectiveTheme === "dark",
      initData: () => telegram.webApp?.initData ?? "",
      allowed: () => fuelAvailable(__KILOPRICE_WEBSITE__),
      onActivity: recordFuelActivity,
      onClose: () => { button.setAttribute("aria-expanded", "false"); scheduleHomeScreenOffer(); } });
    button.setAttribute("aria-expanded", "true");
    await fuelPanel.open(stationId, mine, settings);
  } catch { status.textContent = fuelEntryCopy[appLocale].error; button.setAttribute("aria-expanded", "false"); }
  finally { fuelPanelLoading = false; button.disabled = false; }
}
const systemThemeQuery = window.matchMedia("(prefers-color-scheme: dark)");
function newDiscoverySeed(): number {
  try {
    if (window.crypto?.getRandomValues) return window.crypto.getRandomValues(new Uint32Array(1))[0]!;
  } catch { /* An embedded client may restrict crypto; local ordering must still work. */ }
  return Math.floor(Math.random() * 0x1_0000_0000);
}

// New documents and safe minimized-home returns get a fresh selection. Opening
// products, translating, filtering and paginating never renew a visit.
const discoveryFeedVisit = !__KILOPRICE_WEBSITE__
  ? createDiscoveryFeedVisit({
      seed: newDiscoverySeed(),
      storage: {
        getItem: (key) => window.localStorage.getItem(key),
        setItem: (key, value) => window.localStorage.setItem(key, value),
      },
    })
  : null;
const discoveryFeedLifecycle = createDiscoveryFeedLifecycle();
const filterVisitLifecycle = createDiscoveryFeedLifecycle();
const visitFilterHydration = createVisitFilterHydration();
const websiteRouteContext = __KILOPRICE_WEBSITE__
  ? readWebsiteRouteContext()
  : null;
const websiteGroupKeys = websiteRouteContext?.productGroupKeys
  ? new Set(websiteRouteContext.productGroupKeys) : null;
let appLocale = websiteRouteContext?.locale ?? readLocale();

function sortChoices(): ReadonlyArray<{ value: SortMode; label: string }> {
  return [
    { value: "default", label: t("sortDefault") },
    { value: "price-asc", label: t("sortPrice") },
    { value: "discount-desc", label: t("sortDiscount") },
    { value: "catalog-order", label: t("sortCatalog") },
  ];
}

function select<T extends Element>(selector: string): T {
  const value = document.querySelector<T>(selector);
  if (!value) {
    throw new Error(`Не найден элемент интерфейса: ${selector}`);
  }
  return value;
}

const datasetMeta = select<HTMLElement>("#dataset-meta");
const storeChips = select<HTMLElement>("#store-chips");
const categoryControls = select<HTMLElement>("#category-controls");
const categoryChips = select<HTMLElement>("#category-chips");
const filtersAnchor = select<HTMLElement>("#filters-anchor");
const filtersPanel = select<HTMLElement>("#filters-panel");
const filterSurface = select<HTMLElement>("#filter-surface");
const filterToolbar = select<HTMLElement>(".filter-toolbar");
const filterDetails = select<HTMLElement>("#filter-details");
const activeFilterDock = select<HTMLElement>("#active-filter-dock");
const activeFilterDockTrack = select<HTMLElement>("#active-filter-dock-track");
const appShareButton = select<HTMLButtonElement>("#app-share-button");
const themeControl = select<HTMLElement>("#theme-control");
const themeToggle = select<HTMLButtonElement>("#theme-toggle");
const themeMenu = select<HTMLElement>("#theme-menu");
const themeOptions = [
  ...themeMenu.querySelectorAll<HTMLButtonElement>("button[data-theme-option]"),
];
const botAccessPrompt = select<HTMLElement>("#bot-access-prompt");
const botAccessSave = select<HTMLButtonElement>("#bot-access-save");
const botAccessLater = select<HTMLButtonElement>("#bot-access-later");
const botAccessClose = select<HTMLButtonElement>("#bot-access-close");
const botAccessStatus = select<HTMLElement>("#bot-access-status");
const homeScreenBackdrop = select<HTMLButtonElement>("#home-screen-backdrop");
const homeScreenPrompt = select<HTMLElement>("#home-screen-prompt");
const homeScreenAdd = select<HTMLButtonElement>("#home-screen-add");
const homeScreenLater = select<HTMLButtonElement>("#home-screen-later");
const homeScreenClose = select<HTMLButtonElement>("#home-screen-close");
const homeScreenPlatformNote = select<HTMLElement>("#home-screen-platform-note");
const homeScreenStatus = select<HTMLElement>("#home-screen-status");
const languageControl = select<HTMLElement>("#language-control");
const languageToggle = select<HTMLButtonElement>("#language-toggle");
const languageCurrentLabel = select<HTMLElement>("#language-current-label");
const languageMenu = select<HTMLElement>("#language-menu");
const languageOptions = [
  ...languageMenu.querySelectorAll<HTMLButtonElement>(
    "button[data-language-option]",
  ),
];
const sectionSettings = createSectionSettingsDock(select<HTMLElement>("#section-settings"), () => {
  closeThemeMenu();
  closeLanguageMenu();
});
if(__KILOPRICE_WEBSITE__){
  // A standalone website has no native Telegram bar carrying our brand.
  const title=select<HTMLElement>('.compact-header .website-brand, .compact-header h1');
  title.removeAttribute('data-i18n');
  title.textContent='KiloPrice';
}
const gridToggle = select<HTMLButtonElement>("#grid-toggle");
const searchForm = select<HTMLFormElement>("#search-form");
const searchInput = select<HTMLInputElement>("#search-input");
const searchClear = select<HTMLButtonElement>("#search-clear");
const catalogPicker = select<HTMLButtonElement>("#catalog-picker");
const catalogPickerValue = select<HTMLElement>("#catalog-picker-value");
const catalogPickerCount = select<HTMLElement>("#catalog-picker-count");
const sortPicker = select<HTMLButtonElement>("#sort-picker");
const sortPickerValue = select<HTMLElement>("#sort-picker-value");
const resultTitle = select<HTMLElement>("#result-title");
const resultCount = select<HTMLElement>("#result-count");
const statusPanel = select<HTMLElement>("#status-panel");
const comparisonStarterShelf = select<HTMLElement>(
  "#comparison-starter-shelf",
);
const productGrid = select<HTMLElement>("#product-grid");
const feedSentinel = select<HTMLElement>("#feed-sentinel");
const dialog = select<HTMLDialogElement>("#product-dialog");
const dialogCloseButton = select<HTMLButtonElement>("#dialog-close");
const dialogImage = select<HTMLImageElement>("#dialog-image");
const dialogStore = select<HTMLElement>("#dialog-store");
const dialogTitle = select<HTMLElement>("#dialog-title");
const dialogMeta = select<HTMLElement>("#dialog-meta");
const dialogShoppingControl = select<HTMLElement>("#dialog-shopping-control");
const dialogActions = select<HTMLElement>(".dialog-actions");
const dialogShareButton = select<HTMLButtonElement>("#dialog-share-button");
const dialogShareLabel = select<HTMLElement>("#dialog-share-label");
const dialogShareStatus = select<HTMLElement>("#dialog-share-status");
const productDialogScrollLock = createDocumentScrollLock();
const choiceDialog = select<HTMLDialogElement>("#choice-dialog");
const choiceDialogEyebrow = select<HTMLElement>("#choice-dialog-eyebrow");
const choiceDialogTitle = select<HTMLElement>("#choice-dialog-title");
const choiceOptions = select<HTMLElement>("#choice-options");
const preferenceDialog = select<HTMLDialogElement>("#preference-dialog");
const preferenceDialogEyebrow = select<HTMLElement>("#preference-dialog-eyebrow");
const preferenceDialogTitle = select<HTMLElement>("#preference-dialog-title");
const preferenceDialogDescription = select<HTMLElement>(
  "#preference-dialog-description",
);
const preferenceOptions = select<HTMLElement>("#preference-options");
const preferenceDialogClose = select<HTMLButtonElement>(
  "#preference-dialog-close",
);
const preferenceClear = select<HTMLButtonElement>("#preference-clear");
const preferenceApply = select<HTMLButtonElement>("#preference-apply");
const preferenceUnsavedOverlay = select<HTMLElement>(
  "#preference-unsaved-overlay",
);
const preferenceUnsavedDescription = select<HTMLElement>(
  "#preference-unsaved-description",
);
const preferenceUnsavedDiscard = select<HTMLButtonElement>(
  "#preference-unsaved-discard",
);
const preferenceUnsavedSave = select<HTMLButtonElement>(
  "#preference-unsaved-save",
);
const shoppingListButton = select<HTMLButtonElement>("#shopping-list-button");
const shoppingListCount = select<HTMLElement>("#shopping-list-count");
const priceWatchButton = select<HTMLButtonElement>("#price-watch-button");
const priceWatchTotalCount = select<HTMLElement>("#price-watch-total-count");
const priceWatchResultCount = select<HTMLElement>("#price-watch-result-count");
const priceWatchListDialog = select<HTMLDialogElement>(
  "#price-watch-list-dialog",
);
const priceWatchListContent = select<HTMLElement>(
  "#price-watch-list-content",
);
const priceWatchEditorDialog = select<HTMLDialogElement>(
  "#price-watch-editor-dialog",
);
const priceWatchForm = select<HTMLFormElement>("#price-watch-form");
const priceWatchTargetTitle = select<HTMLElement>("#price-watch-target-title");
const priceWatchTargetSummary = select<HTMLElement>(
  "#price-watch-target-summary",
);
const priceWatchMatchFieldset = select<HTMLElement>(
  "#price-watch-match-fieldset",
);
const priceWatchBelowOption = select<HTMLElement>(
  "#price-watch-below-option",
);
const priceWatchBelowHelp = select<HTMLElement>("#price-watch-below-help");
const priceWatchPersonalStores = select<HTMLInputElement>(
  "#price-watch-personal-stores",
);
const priceWatchDefaultStoresInput = select<HTMLInputElement>("#price-watch-default-stores");
const priceWatchDefaultOption = select<HTMLElement>("#price-watch-default-option");
const priceWatchPersonalStoresHelp = select<HTMLElement>(
  "#price-watch-personal-stores-help",
);
const priceWatchMaxPriceRow = select<HTMLElement>(
  "#price-watch-max-price-row",
);
const priceWatchMaxPrice = select<HTMLInputElement>("#price-watch-max-price");
const priceWatchMaxUnitPriceRow = select<HTMLElement>(
  "#price-watch-max-unit-price-row",
);
const priceWatchMaxUnitLabel = select<HTMLElement>(
  "#price-watch-max-unit-label",
);
const priceWatchMaxUnitPrice = select<HTMLInputElement>(
  "#price-watch-max-unit-price",
);
const priceWatchMinDiscount = select<HTMLInputElement>(
  "#price-watch-min-discount",
);
const priceWatchNotifyNote = select<HTMLElement>("#price-watch-notify-note");
const priceWatchFormStatus = select<HTMLElement>("#price-watch-form-status");
const priceWatchDelete = select<HTMLButtonElement>("#price-watch-delete");
const priceWatchSave = select<HTMLButtonElement>("#price-watch-save");
const shoppingListDialog = select<HTMLDialogElement>("#shopping-list-dialog");
const shoppingListContent = select<HTMLElement>("#shopping-list-content");
const shoppingSharePanel = select<HTMLElement>("#shopping-share-panel");
const shoppingShareButton = select<HTMLButtonElement>("#shopping-share-button");
const shoppingShareLabel = select<HTMLElement>("#shopping-share-label");
const shoppingShareStatus = select<HTMLElement>("#shopping-share-status");
const shoppingSharePhotosOption = select<HTMLElement>("#shopping-share-photos-option");
const shoppingSharePhotos = select<HTMLInputElement>("#shopping-share-photos");
const sharedListDialog = select<HTMLDialogElement>("#shared-list-dialog");
const sharedListMeta = select<HTMLElement>("#shared-list-meta");
const sharedListContent = select<HTMLElement>("#shared-list-content");
const sharedListFooter = select<HTMLElement>("#shared-list-footer");
const sharedListImport = select<HTMLButtonElement>("#shared-list-import");
const sharedListStatus = select<HTMLElement>("#shared-list-status");
const catalogLibraryDialog = select<HTMLDialogElement>(
  "#catalog-library-dialog",
);
const catalogLibraryList = select<HTMLElement>("#catalog-library-list");
const catalogViewerDialog = select<HTMLDialogElement>("#catalog-viewer-dialog");
const catalogViewerBack = select<HTMLButtonElement>("#catalog-viewer-back");
const catalogViewerShare = select<HTMLButtonElement>("#catalog-viewer-share");
const catalogViewerShareLabel = select<HTMLElement>(
  "#catalog-viewer-share-label",
);
const catalogViewerClose = select<HTMLButtonElement>("#catalog-viewer-close");
const catalogViewerZoom = select<HTMLButtonElement>("#catalog-viewer-zoom");
const catalogViewerZoomPlus = select<SVGPathElement>("#catalog-zoom-plus");
const catalogViewerZoomMinus = select<SVGPathElement>("#catalog-zoom-minus");
const catalogViewerStore = select<HTMLElement>("#catalog-viewer-store");
const catalogViewerTitle = select<HTMLElement>("#catalog-viewer-title");
const catalogViewerValidity = select<HTMLElement>("#catalog-viewer-validity");
const catalogPageScroll = select<HTMLElement>("#catalog-page-scroll");
const catalogPageCanvas = select<HTMLElement>("#catalog-page-canvas");
const catalogPageImageElement = select<HTMLImageElement>("#catalog-page-image");
const catalogPageOverlays = select<HTMLElement>("#catalog-page-overlays");
const catalogPageCounter = select<HTMLElement>("#catalog-page-counter");
const catalogPagePrevious = select<HTMLButtonElement>("#catalog-page-previous");
const catalogPageNext = select<HTMLButtonElement>("#catalog-page-next");
const catalogShoppingButton = select<HTMLButtonElement>("#catalog-shopping-button");
const catalogShoppingCount = select<HTMLElement>("#catalog-shopping-count");
const catalogStoreSavePrompt = select<HTMLElement>(
  "#catalog-store-save-prompt",
);
const catalogStoreSaveCopy = select<HTMLElement>("#catalog-store-save-copy");
const catalogStoreSaveAction = select<HTMLButtonElement>(
  "#catalog-store-save-action",
);
const catalogStoreSaveClose = select<HTMLButtonElement>(
  "#catalog-store-save-close",
);
const catalogProductChoiceDialog = select<HTMLDialogElement>(
  "#catalog-product-choice-dialog",
);
const catalogProductChoiceDescription = select<HTMLElement>(
  "#catalog-product-choice-description",
);
const catalogProductChoiceList = select<HTMLElement>(
  "#catalog-product-choice-list",
);

let dataset: CatalogDataset | null = null;
let sourceDataset: CatalogDataset | null = null;
let products: ProductView[] = [];
let productRelationshipIndex = buildProductRelationshipIndex([]);
let matchingProducts: ProductView[] = [];
let feedProducts: ProductView[] = [];
let searchRecovery: SearchRecovery = { groups: [], totalMatches: 0 };
let multiSearchGroups: MultiSearchGroup[] = [];
let multiSearchSignature = "";
const multiSearchLimits = new Map<string, number>();
let comparisonStarterFamilies: ComparisonStarterFamily[] = [];
let activeComparisonStarterFamily: ComparisonStarterFamilyId | null = null;
let comparisonShelfMode: ComparisonShelfMode | null = null;
let comparisonShelfAll = false;
let comparisonShelfQuery = "";
let comparisonShelfLimit = COMPARISON_PAGE_SIZE;
let comparisonAutoLoadScheduled = false;
let comparisonCollapsePositionScheduled = false;
let comparisonCollapseFixedTop = 0;
let comparisonCollapseFixedBottom = 0;
let comparisonSearchReturnFamilies: ComparisonStarterFamily[] = [];
let comparisonCatalogRevision = "";
let comparisonCatalog: ComparisonStarterFamily[] = [];
let comparisonStarterShelfExposureRecorded = false;
let renderedProducts = new Map<string, ProductView>();
let visibleCount = PAGE_SIZE;
let autoLoadScheduled = false;
let activeDateKey = "";
let serverClockOffsetMs = 0;
let gridMode: GridMode = readGridMode();
let themePreference = readThemePreference();
let effectiveTheme = resolveCurrentTheme(themePreference);
paintDocumentTheme(themePreference, effectiveTheme);
let filterExpandedHeight = 0;
let filterCollapsedHeight = 0;
let filterDocumentTop = 0;
let filterRevealScrollY: number | null = null;
let filterScrollScheduled = false;
let filterMeasureScheduled = false;
let activeChoiceKind: ChoiceKind | null = null;
let activeChoiceTrigger: HTMLButtonElement | null = null;
let activePreferenceKind: PreferenceKind | null = null;
let activePreferenceTrigger: HTMLButtonElement | null = null;
let draftPreferenceValues = new Set<string>();
let draftCatalogNotificationStores = new Set<string>();
let preferenceDialogOptions: PreferenceOption[] = [];
let personalPreferences = readPersonalPreferences();
let priceWatchDefaultStores: PersonalFilterMode = readPriceWatchDefaultStores();
let priceWatchDefaultDirty = false;
let personalFilterModes = readPersonalFilterModes();
let storeSavePromptNextEligibleAt = readStoreSavePromptNextEligibleAt();
const initialFilterSelection = readFilterSelection();
let shoppingItems: ShoppingListItem[] = readStoredShoppingList();
let confirmExpiredShoppingRemoval = false;
let expiredShoppingRemovedCount = 0;
let applyingSyncedState = false;
let pendingUserStateMutation = readPendingUserStateMutation();
const activityAccumulator = new ActivityAccumulator(
  undefined,
  effectiveTheme,
  initialAppEntrySource(
    telegramStartParameter(telegram),
    window.location.search,
  ),
  sourceLanguageBucket(navigator.languages?.[0] || navigator.language),
);
telegram.webApp?.onEvent?.("themeChanged", () => {
  refreshEffectiveTheme();
});
systemThemeQuery.addEventListener?.("change", refreshEffectiveTheme);
let userStateSyncTimer: number | undefined;
let userStateSyncRequest: Promise<void> | null = null;
let userStateSyncBlockedForSession = false;
let userStateSyncFailureCount = 0;
let lastUserStateSyncAt = 0;
let searchActivityTimer: number | undefined;
let lastTrackedSearch = "";
let activeDialogProduct: ProductView | null = null;
let productDialogHistory: ProductView[] = [];
let priceWatchState: PriceWatchState = {
  rules: [],
  alerts: [],
  canNotify: false,
};
let priceWatchesLoaded = false;
let priceWatchesLoadedDate = "";
let priceWatchRequest: Promise<PriceWatchState> | null = null;
let priceWatchListOpenToken = 0;
let editingPriceWatch: PriceWatchRuleDraft | null = null;
let priceWatchMutationInProgress = false;
let priceWatchReturnToList: { scrollTop: number; ruleId: string } | null = null;
let priceWatchReturnToFeed: { key: string; scrollY: number } | null = null;
let openingProductPriceWatch = false;
let priceWatchReturnProduct: {
  product: ProductView;
  history: ProductView[];
} | null = null;
let activeSharedList: SharedShoppingList | null = null;
let activeSharedListItem: ShoppingListItem | null = null;
let appShareInProgress = false;
let botAccessPromptTimer: number | undefined;
let botAccessInProgress = false;
let botAccessResolved = false;
type HomeScreenPromptState =
  | "idle"
  | "checking"
  | "unsupported"
  | "already-added"
  | "confirm"
  | "added"
  | "unconfirmed"
  | "error";
let homeScreenPromptState: HomeScreenPromptState = "idle";
let homeScreenRequestAllowedAt = 0;
let homeScreenVerificationTimers: number[] = [];
let homeScreenSafetyTimer: number | undefined;
let homeScreenCompletionTimer: number | undefined;
let homeScreenExperimentEvaluationStarted = false;
let homeScreenExperimentActive = false;
let homeScreenUsefulActionPending = false;
let retentionPromptClaimedThisSession = false;
// Capture BEFORE this document's initial synchronization. A first visit cannot
// turn into a returning visit merely because its own bootstrap has finished.
let homeScreenReturningVisitor = userStateBootstrapComplete() || hadUsefulHomeScreenVisit();
let homeScreenOfferTimer: number | undefined;
let homeScreenPromptGeneration = 0;
let homeScreenAutoStatus: TelegramHomeScreenStatus | null | undefined;
let storePromptEvaluatedThisSession = false;
let shareInProgress = false;
let shoppingSharingEnabled = false;
let sharingStore: string | undefined;
let productShareInProgress = false;
let catalogShareInProgress = false;
let activeCatalogViewerCatalog: Catalog | null = null;
let activeCatalogViewerPage = 1;
let activeCatalogQuantityKey: string | null = null;
let catalogStorePromptVisitedPages = new Set<number>();
let catalogStorePromptCatalogId: string | null = null;
let catalogStorePromptStore: string | null = null;
let catalogStorePromptHideTimer: number | undefined;
let activeCatalogHighlightKey: string | null = null;
let catalogViewerReturn: CatalogViewerReturn = null;
let pendingCatalogViewerReturn: CatalogViewerReturn = null;
let catalogViewerScale = CATALOG_ZOOM_MIN;
let catalogViewerTransitioning = false;
let catalogSwipeStart: { x: number; y: number; pointerId: number } | null = null;
let activeCatalogProductGroup: ProductView[] = [];
const catalogInteractionsEnabled = CATALOG_INTERACTIONS_ENABLED;
let shoppingListFromCatalog = false;
let productFromCatalog = false;
let catalogProductChoiceMode: "quantity" | "open" = "quantity";
const catalogTap = new CatalogTapTracker();
interface CatalogReturnFrame {
  catalogId: string; page: number; scale: number; top: number; left: number;
  aspectRatio: string;
  highlight: string | null; returnTarget: CatalogViewerReturn;
  shopping: boolean; shoppingTop: number;
}
const catalogReturnFrames: CatalogReturnFrame[] = [];
let catalogPinchState: {
  distance: number;
  scale: number;
} | null = null;
const filters: ProductFilters = {
  query: "",
  store: websiteRouteContext?.store ??
    (websiteRouteContext?.productGroupId ? "all" : personalFilterModes.stores === "personal" && personalPreferences.stores.length > 0
      ? PERSONAL_STORES_FILTER : initialFilterSelection.store),
  catalogId: websiteRouteContext?.catalogId ?? "all",
  categoryId: websiteRouteContext?.categoryId ??
    (websiteRouteContext?.productGroupId ? "all" : personalFilterModes.categories === "personal" && personalPreferences.categoryIds.length > 0
      ? PERSONAL_CATEGORIES_FILTER : initialFilterSelection.categoryId),
  sort: readSortMode(),
  personalStores: personalPreferences.stores,
  personalCategoryIds: personalPreferences.categoryIds,
};
const chatSearchEntry = !__KILOPRICE_WEBSITE__ ? parseChatSearchEntry(window.location.search) : null;
if (!__KILOPRICE_WEBSITE__) {
  Object.assign(filters, filtersForNewVisit(filters));
  personalFilterModes = {
    stores: filters.store === PERSONAL_STORES_FILTER ? "personal" : "all",
    categories: filters.categoryId === PERSONAL_CATEGORIES_FILTER ? "personal" : "all",
  };
  // Persist the safe local view without queuing a mutation ahead of the initial
  // cloud read: another device may have a valid, broader personal selection.
  applyingSyncedState = true;
  try {
    saveFilterSelection();
    saveSortMode(filters.sort);
    setPersonalFilterMode("stores", personalFilterModes.stores);
    setPersonalFilterMode("categories", personalFilterModes.categories);
  } finally {
    applyingSyncedState = false;
  }
}

// A deliberate chat query takes precedence over old personal filters/cloud
// hydration for this visit only; preferences themselves are not overwritten.
if (chatSearchEntry) {
  Object.assign(filters, { query: chatSearchEntry.query, store: chatSearchEntry.store,
    categoryId: 'all', catalogId: 'all', sort: 'default', includeAlcohol: true });
  searchInput.value = chatSearchEntry.query;
  visitFilterHydration.keepCurrent();
  updateSearchClearButton();
}

const htmlCharacters: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

function escapeHtml(value: unknown): string {
  return String(value ?? "").replace(/[&<>"']/g, (character) => htmlCharacters[character] ?? "");
}

function appAssetUrl(value: string): string {
  return __KILOPRICE_WEBSITE__ ? publicAssetUrl(value) : value;
}

function updateCatalogShoppingButton(): void {
  const quantity = shoppingQuantityTotal(shoppingItems);
  catalogShoppingButton.classList.toggle("is-empty", quantity === 0);
  catalogShoppingButton.setAttribute("aria-label", quantity === 0
    ? t("shoppingListEmptyAria") : t("shoppingListCountAria", { count: quantity }));
  catalogShoppingCount.textContent = quantity > 99 ? "99+" : String(quantity);
  catalogShoppingCount.hidden = quantity === 0;
}

function canUseMealPilot(): boolean {
  return mealIdeasAllowed(dataset !== null);
}

function updateMealPilotAccess(): void {
  select<HTMLElement>("#meal-entry").hidden = !canUseMealPilot();
  select<HTMLElement>("main").classList.toggle("has-meal-pilot", canUseMealPilot());
  if (!canUseMealPilot()) mealPilot?.close();
}

function refreshMealPilot(): void {
  updateFuelAccess();
  fuelPanel?.refresh();
  updateMealPilotAccess();
  // Recipe failures must not reach the normal catalog/search rendering path.
  if (mealPilot) void mealPilot.refresh().catch(() => mealPilot?.close());
}

async function openMealPilot(): Promise<void> {
  if (!canUseMealPilot() || mealPilotLoading || !dataset) return;
  mealPilotLoading = true;
  const button = select<HTMLButtonElement>("#meal-ideas-button");
  const status = select<HTMLElement>("#meal-entry-status");
  button.setAttribute("aria-busy", "true");
  status.textContent = "";
  try {
    if (!mealPilot) {
      const [{ createMealPilot }, { ensureMealShoppingItems }] = await Promise.all([
        import("./meals/view"), import("./meals/shopping"),
      ]);
      if (!canUseMealPilot()) return;
      mealPilot = createMealPilot({
        context: () => ({ products, today: dateKeyInMoldova(currentReferenceTime()), locale: appLocale,
          personalStores: personalPreferences.stores, selectedStore: filters.store }),
        allowed: canUseMealPilot,
        assetUrl: appAssetUrl,
        productTitle: localizedProductTitle,
        productNote: localizedProductNote,
        quantity: (product) => shoppingItems.find((item) => item.key === productKey(product))?.quantity ?? 0,
        openProduct: (product) => openProduct(product, { source: "meal" }),
        onActivity: queueActivity,
        ensureInList: (lines) => {
          const next = ensureMealShoppingItems(shoppingItems, lines, shoppingItemFromProduct);
          const newCount = next.filter((item) => !isInShoppingList(item.key)).length;
          const previousQuantities = new Map(shoppingItems.map((item) => [item.key, item.quantity]));
          const addsQuantity = next.some((item) => item.quantity > (previousQuantities.get(item.key) ?? 0));
          if (newCount) { queueActivity("shoppingAdds", newCount); signalHomeScreenUsefulAction(); }
          persistShoppingList(next);
          // One successful set addition, not one event per ingredient or retry.
          if (addsQuantity) queueActivity("mealListAdds");
        },
        openList: openShoppingList,
      });
      select<HTMLDialogElement>("#meal-ideas-dialog").addEventListener("close", () => button.setAttribute("aria-expanded", "false"));
    }
    await mealPilot.open();
    button.setAttribute("aria-expanded", String(select<HTMLDialogElement>("#meal-ideas-dialog").open));
  } catch {
    mealPilot?.close();
    status.textContent = t("mealLoadFailed");
  } finally {
    mealPilotLoading = false;
    button.removeAttribute("aria-busy");
  }
}

function localizedProductTitle(product: ProductView): string {
  return localized(appLocale, {
    ru: product.product_ru || product.product_ro,
    ro: product.product_ro,
    uk: product.product_uk || product.product_ru || product.product_ro,
  });
}

function secondaryProductTitle(product: ProductView): string {
  const title = localizedProductTitle(product);
  return localized(appLocale, {
    ru: product.product_ro !== title ? product.product_ro : "",
    ro: "",
    uk: product.product_ro !== title ? product.product_ro : "",
  });
}

function localizedProductNote(product: ProductView): string {
  return localized(appLocale, {
    ru: product.note_ru || product.note_ro || "",
    ro: product.note_ro ?? "",
    uk: product.note_uk || product.note_ru || product.note_ro || "",
  });
}

function secondaryProductNote(product: ProductView): string {
  const note = localizedProductNote(product);
  return localized(appLocale, {
    ru: product.note_ro && product.note_ro !== note ? product.note_ro : "",
    ro: "",
    uk: product.note_ro && product.note_ro !== note ? product.note_ro : "",
  });
}

function localizedCategoryLabel(category: ProductCategory): string {
  return localized(appLocale, {
    ru: category.label_ru,
    ro: category.label_ro,
    uk: category.label_uk,
  });
}

function localizedShoppingTitle(item: ShoppingListItem): string {
  return localized(appLocale, {
    ru: item.title,
    ro: item.originalTitle || item.title,
    uk: item.titleUk || item.title,
  });
}

function secondaryShoppingTitle(item: ShoppingListItem): string {
  return localized(appLocale, {
    ru: item.originalTitle ?? "",
    ro: "",
    uk: item.originalTitle ?? "",
  });
}

function userScopedStorageKey(base: string): string {
  return telegramUserId ? `${base}.${telegramUserId}` : base;
}

function readUserStorageValue(base: string): string | null {
  const key = userScopedStorageKey(base);
  const stored = localStorage.getItem(key);
  if (!telegramUserId || stored !== null) {
    return stored;
  }
  const legacy = localStorage.getItem(base);
  if (legacy !== null) {
    localStorage.setItem(key, legacy);
    localStorage.removeItem(base);
  }
  return legacy;
}

function writeUserStorageValue(base: string, value: string): void {
  localStorage.setItem(userScopedStorageKey(base), value);
}

function locallyConnectedToBot(): boolean {
  try {
    return readUserStorageValue(BOT_ACCESS_CONNECTED_KEY) === "true";
  } catch {
    return false;
  }
}

function markLocallyConnectedToBot(): void {
  try {
    writeUserStorageValue(BOT_ACCESS_CONNECTED_KEY, "true");
    localStorage.removeItem(
      userScopedStorageKey(BOT_ACCESS_DISMISSED_UNTIL_KEY),
    );
    localStorage.removeItem(
      userScopedStorageKey(BOT_ACCESS_LEGACY_DISMISSED_UNTIL_KEY),
    );
  } catch {
    // Server state and fresh Telegram initData remain authoritative.
  }
}

function readBotAccessDismissedUntil(): number {
  const current = Number(
    readUserStorageValue(BOT_ACCESS_DISMISSED_UNTIL_KEY) ?? "0",
  );
  if (Number.isFinite(current) && current > 0) {
    return current;
  }
  const legacy = Number(
    readUserStorageValue(BOT_ACCESS_LEGACY_DISMISSED_UNTIL_KEY) ?? "0",
  );
  if (!Number.isFinite(legacy) || legacy <= 0) {
    return 0;
  }
  const dismissedAt = legacy - BOT_ACCESS_LEGACY_DISMISS_COOLDOWN_MS;
  const migratedUntil = dismissedAt + BOT_ACCESS_DISMISS_COOLDOWN_MS;
  writeUserStorageValue(
    BOT_ACCESS_DISMISSED_UNTIL_KEY,
    String(migratedUntil),
  );
  localStorage.removeItem(
    userScopedStorageKey(BOT_ACCESS_LEGACY_DISMISSED_UNTIL_KEY),
  );
  return migratedUntil;
}

function botAccessPromptIsCoolingDown(): boolean {
  try {
    const dismissedUntil = readBotAccessDismissedUntil();
    return Number.isFinite(dismissedUntil) && dismissedUntil > Date.now();
  } catch {
    return false;
  }
}

function setBotAccessPromptCooldown(): void {
  try {
    writeUserStorageValue(
      BOT_ACCESS_DISMISSED_UNTIL_KEY,
      String(Date.now() + BOT_ACCESS_DISMISS_COOLDOWN_MS),
    );
    localStorage.removeItem(
      userScopedStorageKey(BOT_ACCESS_LEGACY_DISMISSED_UNTIL_KEY),
    );
  } catch {
    // The prompt remains dismissed for this session through botAccessResolved.
  }
}

function clearBotAccessPromptCooldown(): void {
  try {
    localStorage.removeItem(userScopedStorageKey(BOT_ACCESS_DISMISSED_UNTIL_KEY));
    localStorage.removeItem(
      userScopedStorageKey(BOT_ACCESS_LEGACY_DISMISSED_UNTIL_KEY),
    );
  } catch {
    // The explicit tracking action still re-enables the prompt this session.
  }
}

function syncExistingBotWriteAccess(): void {
  const initData = telegram.webApp?.initData;
  if (!initData || !telegramUserAllowsWrite(telegram)) {
    return;
  }
  botAccessResolved = true;
  void loadBotAccessStatus(initData)
    .then((connected) => {
      if (connected) {
        markLocallyConnectedToBot();
      }
    })
    .catch(() => {
      // Retry on the next launch; Telegram already confirms access this session.
    });
}

function setBotAccessPromptStatus(
  message: string,
  state: "idle" | "success" | "error" = "idle",
): void {
  botAccessStatus.textContent = message;
  botAccessStatus.hidden = !message;
  botAccessStatus.classList.toggle("is-success", state === "success");
  botAccessStatus.classList.toggle("is-error", state === "error");
}

function hideBotAccessPrompt(): void {
  botAccessPrompt.classList.remove("is-visible");
  window.setTimeout(() => {
    if (!botAccessPrompt.classList.contains("is-visible")) {
      botAccessPrompt.hidden = true;
    }
  }, 230);
}

function dismissBotAccessPrompt(): void {
  botAccessResolved = true;
  setBotAccessPromptCooldown();
  hideBotAccessPrompt();
}

function showBotAccessPrompt(): void {
  retentionPromptClaimedThisSession = true;
  botAccessPrompt.hidden = false;
  setBotAccessPromptStatus("");
  window.requestAnimationFrame(() => {
    botAccessPrompt.classList.add("is-visible");
  });
}

function scheduleBotAccessPrompt(delay = BOT_ACCESS_PROMPT_DELAY_MS): void {
  if (
    botAccessResolved ||
    retentionPromptClaimedThisSession ||
    botAccessPromptTimer !== undefined ||
    !telegram.isTelegram ||
    !telegram.webApp?.initData
  ) {
    return;
  }
  botAccessPromptTimer = window.setTimeout(() => {
    botAccessPromptTimer = undefined;
    void maybeShowBotAccessPrompt();
  }, delay);
}

async function maybeShowBotAccessPrompt(): Promise<void> {
  const initData = telegram.webApp?.initData;
  if (!initData || botAccessResolved || retentionPromptClaimedThisSession) {
    return;
  }
  if (
    document.visibilityState !== "visible" ||
    document.querySelector("dialog[open]")
  ) {
    scheduleBotAccessPrompt(1_000);
    return;
  }
  if (
    telegramUserAllowsWrite(telegram) ||
    locallyConnectedToBot() ||
    botAccessPromptIsCoolingDown()
  ) {
    botAccessResolved = true;
    return;
  }
  try {
    const connected = await loadBotAccessStatus(initData);
    if (connected) {
      markLocallyConnectedToBot();
      botAccessResolved = true;
      return;
    }
  } catch {
    // Never risk prompting a user whose existing bot status could not be checked.
    botAccessResolved = true;
    return;
  }
  showBotAccessPrompt();
}

async function saveBotAccess(): Promise<void> {
  const initData = telegram.webApp?.initData;
  if (!initData || botAccessInProgress) {
    return;
  }
  botAccessInProgress = true;
  botAccessSave.disabled = true;
  botAccessLater.disabled = true;
  botAccessClose.disabled = true;
  botAccessSave.textContent = t("botAccessOpening");
  setBotAccessPromptStatus("");
  try {
    const allowed = await requestTelegramWriteAccess(telegram);
    if (allowed === null) {
      openTelegramBotStart(
        telegram,
        "https://t.me/KiloPrice_bot?start=save_kiloprice",
      );
      dismissBotAccessPrompt();
      return;
    }
    if (!allowed) {
      dismissBotAccessPrompt();
      return;
    }
    botAccessSave.textContent = t("botAccessSaving");
    setBotAccessPromptStatus(t("botAccessSaving"));
    await new Promise((resolve) => window.setTimeout(resolve, 250));
    await connectBotAccess(initData, appLocale);
    markLocallyConnectedToBot();
    applyPriceWatchState({ ...priceWatchState, canNotify: true });
    botAccessResolved = true;
    setBotAccessPromptStatus(t("botAccessSaved"), "success");
    window.setTimeout(hideBotAccessPrompt, 1_600);
  } catch {
    botAccessSave.textContent = t("botAccessSave");
    setBotAccessPromptStatus(t("botAccessFailed"), "error");
  } finally {
    botAccessInProgress = false;
    if (!botAccessResolved) {
      botAccessSave.disabled = false;
      botAccessLater.disabled = false;
      botAccessClose.disabled = false;
    }
  }
}

function readHomeScreenPromptProgress(): HomeScreenPromptProgress {
  try {
    return parseHomeScreenPromptProgress(
      readUserStorageValue(HOME_SCREEN_PROMPT_PROGRESS_KEY),
    );
  } catch {
    return parseHomeScreenPromptProgress(null);
  }
}

function hadUsefulHomeScreenVisit(): boolean {
  try { return readUserStorageValue(HOME_SCREEN_USEFUL_VISIT_KEY) === "1"; }
  catch { return false; }
}

function homeScreenHasPriority(): boolean {
  return !homeScreenDirectLaunch && homeScreenAutomaticEligible({
    enabled: !__KILOPRICE_WEBSITE__ && homeScreenVariant !== "off",
    mobileTelegram: telegram.isTelegram && !!telegram.webApp?.initData && !!telegramUserId && homeScreenPlatform !== "other",
    returningVisitor: homeScreenReturningVisitor,
    usefulAction: homeScreenUsefulActionPending,
    progress: readHomeScreenPromptProgress(),
  });
}

function homeScreenUiIsBusy(): boolean {
  return document.visibilityState !== "visible" || !!document.querySelector("dialog[open]") ||
    !!document.activeElement?.matches("input, textarea, [contenteditable=true]") ||
    appShareInProgress || shareInProgress || productShareInProgress || catalogShareInProgress;
}

function scheduleHomeScreenOffer(): void {
  window.clearTimeout(homeScreenOfferTimer);
  if (!homeScreenUsefulActionPending) return;
  homeScreenOfferTimer = window.setTimeout(() => {
    homeScreenOfferTimer = undefined;
    void maybeOfferHomeScreenShortcut();
  }, 800);
}

function writeHomeScreenPromptProgress(
  progress: Readonly<HomeScreenPromptProgress>,
): void {
  try {
    writeUserStorageValue(
      HOME_SCREEN_PROMPT_PROGRESS_KEY,
      JSON.stringify(progress),
    );
  } catch {
    // Session-level guards still prevent repeated prompts during this launch.
  }
}

function setHomeScreenPromptStatus(
  message: string,
  state: "idle" | "success" | "error" = "idle",
): void {
  homeScreenStatus.textContent = message;
  homeScreenStatus.hidden = !message;
  homeScreenStatus.classList.toggle("is-success", state === "success");
  homeScreenStatus.classList.toggle("is-error", state === "error");
}

function renderHomeScreenPromptState(): void {
  const platformNoteKey =
    homeScreenPlatform === "android"
      ? "homeScreenAndroidNote"
      : homeScreenPlatform === "ios"
        ? "homeScreenIosNote"
        : null;
  const shortcutIsComplete =
    homeScreenPromptState === "added" ||
    homeScreenPromptState === "already-added";
  homeScreenPrompt.classList.toggle("is-complete", shortcutIsComplete);
  homeScreenPlatformNote.textContent = platformNoteKey ? t(platformNoteKey) : "";
  homeScreenPlatformNote.hidden = platformNoteKey === null || shortcutIsComplete;
  homeScreenAdd.textContent = t(
    homeScreenPlatform === "ios" ? "homeScreenAddIos" : "homeScreenAdd",
  );
  homeScreenAdd.disabled = false;
  switch (homeScreenPromptState) {
    case "checking":
      homeScreenAdd.disabled = true;
      setHomeScreenPromptStatus(t("homeScreenChecking"));
      return;
    case "unsupported":
      homeScreenAdd.disabled = true;
      setHomeScreenPromptStatus(t("homeScreenUnsupported"), "error");
      return;
    case "already-added":
      homeScreenAdd.textContent = t("homeScreenAlreadyAddedAction");
      homeScreenAdd.disabled = true;
      setHomeScreenPromptStatus(t("homeScreenAlreadyAdded"), "success");
      return;
    case "confirm":
      homeScreenAdd.disabled = true;
      setHomeScreenPromptStatus(
        t(
          homeScreenPlatform === "ios"
            ? "homeScreenConfirmIos"
            : "homeScreenConfirm",
        ),
      );
      return;
    case "added":
      homeScreenAdd.textContent = t("homeScreenAddedAction");
      homeScreenAdd.disabled = true;
      setHomeScreenPromptStatus(t("homeScreenAdded"), "success");
      return;
    case "unconfirmed":
      homeScreenAdd.textContent = t("homeScreenCheckAction");
      homeScreenAdd.disabled = true;
      setHomeScreenPromptStatus(t("homeScreenResultUnknown"));
      return;
    case "error":
      setHomeScreenPromptStatus(t("homeScreenFailed"), "error");
      return;
    default:
      setHomeScreenPromptStatus("");
  }
}

function clearHomeScreenRequestTimers(): void {
  for (const timer of homeScreenVerificationTimers) {
    window.clearTimeout(timer);
  }
  homeScreenVerificationTimers = [];
  if (homeScreenSafetyTimer !== undefined) {
    window.clearTimeout(homeScreenSafetyTimer);
    homeScreenSafetyTimer = undefined;
  }
}

function clearHomeScreenPromptTimers(): void {
  clearHomeScreenRequestTimers();
  if (homeScreenCompletionTimer !== undefined) {
    window.clearTimeout(homeScreenCompletionTimer);
    homeScreenCompletionTimer = undefined;
  }
}

function hideHomeScreenPrompt(): void {
  homeScreenPromptGeneration += 1;
  clearHomeScreenPromptTimers();
  homeScreenPrompt.classList.remove("is-visible");
  homeScreenBackdrop.classList.remove("is-visible");
  homeScreenExperimentActive = false;
  window.setTimeout(() => {
    if (!homeScreenPrompt.classList.contains("is-visible")) {
      homeScreenPrompt.hidden = true;
      homeScreenBackdrop.hidden = true;
    }
  }, 230);
}

async function showHomeScreenPrompt(options: {
  automatic?: boolean;
  knownStatus?: TelegramHomeScreenStatus | null;
} = {}): Promise<void> {
  retentionPromptClaimedThisSession = true;
  homeScreenExperimentActive = !homeScreenLocalPreview && telegram.isTelegram && !!telegram.webApp?.initData;
  const generation = ++homeScreenPromptGeneration;
  if (homeScreenExperimentActive && !options.automatic) {
    queueActivity("homeScreenPromptEligible");
    queueActivity("homeScreenPromptTreatment");
  }
  clearHomeScreenPromptTimers();
  homeScreenBackdrop.hidden = false;
  homeScreenPrompt.hidden = false;
  homeScreenPromptState = "checking";
  renderHomeScreenPromptState();
  window.requestAnimationFrame(() => {
    homeScreenBackdrop.classList.add("is-visible");
    homeScreenPrompt.classList.add("is-visible");
  });
  try {
    const status =
      options.knownStatus === undefined
        ? await checkTelegramHomeScreenStatus(telegram)
        : options.knownStatus;
    if (generation !== homeScreenPromptGeneration) return;
    if (status === null || status === "unsupported") {
      homeScreenPromptState = "unsupported";
      if (homeScreenExperimentActive) queueActivity("homeScreenPromptUnsupported");
    } else if (status === "added") {
      homeScreenPromptState = "already-added";
      if (homeScreenExperimentActive) {
        if (!readHomeScreenPromptProgress().added) queueActivity("homeScreenPromptAlreadyAdded");
        writeHomeScreenPromptProgress(markHomeScreenAdded(readHomeScreenPromptProgress()));
      }
    } else {
      homeScreenPromptState = "idle";
      if (homeScreenExperimentActive) queueActivity("homeScreenPromptShown");
      if (options.automatic) writeHomeScreenPromptProgress(markHomeScreenPromptShown(readHomeScreenPromptProgress()));
    }
  } catch {
    if (generation !== homeScreenPromptGeneration) return;
    homeScreenPromptState = "error";
    if (homeScreenExperimentActive) queueActivity("homeScreenPromptFailed");
  }
  renderHomeScreenPromptState();
}

function completeHomeScreenShortcut(): void {
  if (homeScreenPromptState !== "confirm" && homeScreenPromptState !== "unconfirmed") {
    return;
  }
  clearHomeScreenPromptTimers();
  homeScreenPromptState = "added";
  if (homeScreenExperimentActive) {
    queueActivity("homeScreenPromptCompleted");
    writeHomeScreenPromptProgress(
      markHomeScreenAdded(readHomeScreenPromptProgress()),
    );
  }
  renderHomeScreenPromptState();
  hapticSuccess(telegram);
  homeScreenCompletionTimer = window.setTimeout(hideHomeScreenPrompt, 1_350);
}

async function verifyHomeScreenShortcut(): Promise<void> {
  if (homeScreenPromptState !== "confirm") {
    return;
  }
  try {
    const status = await checkTelegramHomeScreenStatus(telegram);
    if (status === "added" && homeScreenPromptState === "confirm") {
      completeHomeScreenShortcut();
    }
  } catch {
    // Telegram may be unable to report shortcut installation on some devices.
  }
}

function scheduleHomeScreenVerification(): void {
  clearHomeScreenRequestTimers();
  for (const delay of [650, 1_500, 3_000]) {
    homeScreenVerificationTimers.push(
      window.setTimeout(() => void verifyHomeScreenShortcut(), delay),
    );
  }
  homeScreenSafetyTimer = window.setTimeout(() => {
    if (homeScreenPromptState !== "confirm") {
      return;
    }
    clearHomeScreenRequestTimers();
    homeScreenPromptState = "unconfirmed";
    if (homeScreenExperimentActive) {
      queueActivity("homeScreenPromptFailed");
      writeHomeScreenPromptProgress(
        dismissHomeScreenPromptProgress(readHomeScreenPromptProgress()),
      );
    }
    renderHomeScreenPromptState();
    homeScreenCompletionTimer = window.setTimeout(hideHomeScreenPrompt, 1_650);
  }, 4_800);
}

function addHomeScreenShortcut(): void {
  if (homeScreenAdd.disabled || Date.now() < homeScreenRequestAllowedAt) {
    return;
  }
  homeScreenRequestAllowedAt = Date.now() + 1_000;
  homeScreenPromptState = "confirm";
  renderHomeScreenPromptState();
  if (!requestTelegramHomeScreenShortcut(telegram)) {
    if (homeScreenExperimentActive) {
      queueActivity("homeScreenPromptFailed");
    }
    homeScreenPromptState = "error";
    renderHomeScreenPromptState();
    return;
  }
  if (homeScreenExperimentActive) {
    queueActivity("homeScreenPromptRequested");
  }
  if (homeScreenPromptState === "confirm") {
    scheduleHomeScreenVerification();
  }
}

function dismissHomeScreenExperimentPrompt(): void {
  if (
    homeScreenExperimentActive &&
    homeScreenPromptState !== "added" &&
    homeScreenPromptState !== "already-added"
  ) {
    queueActivity("homeScreenPromptDismissed");
    writeHomeScreenPromptProgress(
      dismissHomeScreenPromptProgress(readHomeScreenPromptProgress()),
    );
  }
  hideHomeScreenPrompt();
}

async function maybeOfferHomeScreenShortcut(): Promise<void> {
  if (
    !homeScreenHasPriority() ||
    homeScreenExperimentEvaluationStarted ||
    retentionPromptClaimedThisSession || homeScreenUiIsBusy()
  ) {
    return;
  }
  const progress = readHomeScreenPromptProgress();
  const generation = homeScreenPromptGeneration;
  homeScreenExperimentEvaluationStarted = true;
  let status: TelegramHomeScreenStatus | null;
  try {
    status = homeScreenAutoStatus === undefined
      ? await checkTelegramHomeScreenStatus(telegram) : homeScreenAutoStatus;
    if (generation !== homeScreenPromptGeneration) return;
    homeScreenAutoStatus = status;
  } catch {
    if (generation !== homeScreenPromptGeneration) return;
    queueActivity("homeScreenPromptFailed");
    return;
  }
  // The user can open another panel while Telegram checks. Never interrupt it,
  // consume a show, or repeat a native lookup just because that check was slow.
  if (generation !== homeScreenPromptGeneration || retentionPromptClaimedThisSession) return;
  if (homeScreenUiIsBusy()) {
    homeScreenExperimentEvaluationStarted = false;
    return;
  }
  homeScreenUsefulActionPending = false;
  queueActivity("homeScreenPromptEligible");
  queueActivity("homeScreenPromptTreatment");
  if (status === null || status === "unsupported") {
    queueActivity("homeScreenPromptUnsupported");
    writeHomeScreenPromptProgress(markHomeScreenUnsupported(progress));
    return;
  }
  if (status === "added") {
    queueActivity("homeScreenPromptAlreadyAdded");
    writeHomeScreenPromptProgress(markHomeScreenAdded(progress));
    return;
  }

  await showHomeScreenPrompt({ automatic: true, knownStatus: status });
}

function signalHomeScreenUsefulAction(): void {
  homeScreenUsefulActionPending = true;
  try { writeUserStorageValue(HOME_SCREEN_USEFUL_VISIT_KEY, "1"); } catch { /* Optional storage. */ }
  scheduleHomeScreenOffer();
}

onTelegramHomeScreenAdded(telegram, () => {
  completeHomeScreenShortcut();
});

onTelegramActivated(telegram, () => {
  void verifyHomeScreenShortcut();
});

function readLocale(): AppLocale {
  try {
    const stored = normalizeAppLocale(readUserStorageValue(LOCALE_KEY));
    if (stored) {
      return stored;
    }
  } catch {
    // Telegram and browser language detection still provide a safe fallback.
  }
  return detectAppLocale(
    telegramUserLanguageCode(telegram),
    navigator.languages ?? [navigator.language],
  );
}

function readThemePreference(): ThemePreference {
  try {
    return initialThemePreference(
      readUserStorageValue(THEME_PREFERENCE_KEY),
    );
  } catch {
    return "auto";
  }
}

function resolveCurrentTheme(preference: ThemePreference): EffectiveTheme {
  return resolveEffectiveTheme(
    preference,
    telegram.webApp?.colorScheme ?? null,
    systemThemeQuery.matches,
  );
}

function paintDocumentTheme(
  preference: ThemePreference,
  effective: EffectiveTheme,
): void {
  const root = document.documentElement;
  root.dataset.themePreference = preference;
  root.dataset.effectiveTheme = effective;
  root.dataset.colorScheme = effective;
  const chromeColors =
    preference === "auto"
      ? AUTO_THEME_FALLBACK_COLORS[effective]
      : FIXED_THEME_COLORS[effective];
  document
    .querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    ?.setAttribute("content", chromeColors.background);
  try {
    if (preference === "auto") {
      telegram.webApp?.setHeaderColor("secondary_bg_color");
      telegram.webApp?.setBackgroundColor("bg_color");
    } else {
      telegram.webApp?.setHeaderColor(chromeColors.surface);
      telegram.webApp?.setBackgroundColor(chromeColors.background);
    }
  } catch {
    // Older Telegram clients can still use the in-page theme safely.
  }
}

function updateThemeControl(): void {
  themeToggle.dataset.effectiveTheme = effectiveTheme;
  themeToggle.setAttribute(
    "aria-label",
    t(effectiveTheme === "dark" ? "themeToggleDark" : "themeToggleLight"),
  );
  themeToggle.title = t("changeTheme");
  for (const option of themeOptions) {
    const selected = option.dataset.themeOption === themePreference;
    option.setAttribute("aria-checked", String(selected));
  }
}

function closeThemeMenu(restoreFocus = false): void {
  if (themeMenu.hidden) {
    return;
  }
  themeMenu.hidden = true;
  setBackMenu('theme');
  themeToggle.setAttribute("aria-expanded", "false");
  if (restoreFocus) {
    themeToggle.focus({ preventScroll: true });
  }
}

function toggleThemeMenu(): void {
  if (!themeMenu.hidden) {
    closeThemeMenu();
    return;
  }
  closeLanguageMenu();
  themeMenu.hidden = false;
  setBackMenu('theme', () => closeThemeMenu(true), document, themeControl);
  themeToggle.setAttribute("aria-expanded", "true");
  const selected = themeOptions.find(
    (option) => option.dataset.themeOption === themePreference,
  );
  selected?.focus({ preventScroll: true });
}

function applyThemePreference(
  preference: ThemePreference,
  syncSelection: boolean,
): void {
  const changed = preference !== themePreference;
  themePreference = preference;
  effectiveTheme = resolveCurrentTheme(preference);
  paintDocumentTheme(preference, effectiveTheme);
  updateThemeControl();
  activityAccumulator.setColorScheme(effectiveTheme);
  fuelPanel?.refresh();
  try {
    writeUserStorageValue(THEME_PREFERENCE_KEY, preference);
  } catch {
    // The chosen theme remains active for this session.
  }
  if (syncSelection && changed && !applyingSyncedState) {
    queueUserStateMutation({ themePreference: preference });
  }
}

function refreshEffectiveTheme(): void {
  effectiveTheme = resolveCurrentTheme(themePreference);
  paintDocumentTheme(themePreference, effectiveTheme);
  updateThemeControl();
  activityAccumulator.setColorScheme(effectiveTheme);
  fuelPanel?.refresh();
}

const LANGUAGE_CODES: Readonly<Record<AppLocale, string>> = {
  ru: "RU",
  ro: "RO",
  uk: "UA",
};

const LANGUAGE_NAME_KEYS = {
  ru: "languageRussian",
  ro: "languageRomanian",
  uk: "languageUkrainian",
} as const;

function updateLanguageToggle(): void {
  languageToggle.dataset.locale = appLocale;
  languageCurrentLabel.textContent = LANGUAGE_CODES[appLocale];
  languageToggle.setAttribute(
    "aria-label",
    t("languageCurrentAria", {
      language: t(LANGUAGE_NAME_KEYS[appLocale]),
    }),
  );
  for (const option of languageOptions) {
    option.setAttribute(
      "aria-checked",
      String(option.dataset.languageOption === appLocale),
    );
  }
}

function closeLanguageMenu(restoreFocus = false): void {
  if (languageMenu.hidden) {
    return;
  }
  languageMenu.hidden = true;
  setBackMenu('language');
  languageToggle.setAttribute("aria-expanded", "false");
  if (restoreFocus) {
    languageToggle.focus({ preventScroll: true });
  }
}

function toggleLanguageMenu(): void {
  if (!languageMenu.hidden) {
    closeLanguageMenu();
    return;
  }
  closeThemeMenu();
  languageMenu.hidden = false;
  setBackMenu('language', () => closeLanguageMenu(true), document, languageControl);
  languageToggle.setAttribute("aria-expanded", "true");
  languageOptions
    .find((option) => option.dataset.languageOption === appLocale)
    ?.focus({ preventScroll: true });
}

async function applyAppLocale(
  locale: AppLocale,
  syncSelection: boolean,
  rerender = true,
): Promise<void> {
  const changed = locale !== appLocale;
  const nextDatasetPromise =
    changed && sourceDataset && !__KILOPRICE_WEBSITE__
      ? loadLocaleDataset(locale)
      : null;
  await Promise.all([
    preloadLocale(locale),
    nextDatasetPromise ?? Promise.resolve(null),
  ]);
  await activateLocale(locale);
  const nextSourceDataset = nextDatasetPromise
    ? await nextDatasetPromise
    : null;
  appLocale = locale;
  applyDocumentTranslations();
  renderHomeScreenPromptState();
  updateLanguageToggle();
  updateThemeControl();
  updateGridToggleCopy();
  try {
    writeUserStorageValue(LOCALE_KEY, locale);
  } catch {
    // The selected language remains active for this session.
  }
  if (syncSelection && changed && !applyingSyncedState) {
    queueUserStateMutation({ locale });
  }
  if (!rerender) {
    return;
  }
  if (nextSourceDataset) {
    sourceDataset = nextSourceDataset;
    refreshActiveCatalogs(true);
  }
  if (dataset) {
    const scrollY = window.scrollY;
    if (!nextSourceDataset) {
      renderDatasetMeta(dataset);
      renderStoreChips();
      renderCategoryControls();
      renderActiveFilterDock();
      renderCatalogPicker();
      renderSortPicker();
      renderProducts();
      scheduleFilterMeasurement();
    }
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: scrollY, left: window.scrollX });
    });
  }
  updateShoppingUi();
  updatePriceWatchFab();
  if (sharedListDialog.open && activeSharedList) {
    renderLoadedSharedShoppingList(activeSharedList);
  } else if (sharedListDialog.open && activeSharedListItem) {
    renderSharedShoppingListItem(activeSharedListItem);
  }
  if (dialog.open && activeDialogProduct) {
    openProduct(activeDialogProduct, {
      preserveHistory: true,
      scrollToTop: false,
      trackActivity: false,
    });
  }
  if (catalogLibraryDialog.open) {
    renderCatalogLibrary();
  }
  if (catalogViewerDialog.open && activeCatalogViewerCatalog) {
    renderCatalogViewer();
  }
  if (priceWatchListDialog.open) {
    renderPriceWatchList();
  }
  if (priceWatchEditorDialog.open && editingPriceWatch) {
    renderPriceWatchEditor();
  }
  refreshMealPilot();
  updateBusinessAccess();
  businessPanel?.refreshLocale();
  scheduleFilterMeasurement();
}

function readPendingUserStateMutation(): UserStateMutation | null {
  try {
    return parsePendingUserStateMutation(
      localStorage.getItem(userScopedStorageKey(USER_STATE_PENDING_KEY)),
    );
  } catch {
    return null;
  }
}

function storePendingUserStateMutation(): void {
  try {
    const key = userScopedStorageKey(USER_STATE_PENDING_KEY);
    if (pendingUserStateMutation) {
      localStorage.setItem(key, JSON.stringify(pendingUserStateMutation));
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    // The in-memory queue still retries while the Mini App remains open.
  }
}

function readSortMode(): SortMode {
  try {
    const value = readUserStorageValue(SORT_MODE_KEY);
    return SORT_MODES.includes(value as SortMode)
      ? (value as SortMode)
      : "default";
  } catch {
    return "default";
  }
}

function saveSortMode(value: SortMode): void {
  try {
    writeUserStorageValue(SORT_MODE_KEY, value);
  } catch {
    // The selected sorting remains active for this session.
  }
  if (!applyingSyncedState) {
    visitFilterHydration.keepCurrent();
    queueUserStateMutation({ sort: value });
  }
}

function readGridMode(): GridMode {
  try {
    return readUserStorageValue(GRID_MODE_KEY) === "single" ? "single" : "compact";
  } catch {
    return "compact";
  }
}

function saveGridMode(value: GridMode): void {
  let changed = true;
  try {
    changed = readUserStorageValue(GRID_MODE_KEY) !== value;
    writeUserStorageValue(GRID_MODE_KEY, value);
  } catch {
    // The view still works when Telegram or the browser blocks local storage.
  }
  if (!applyingSyncedState && changed) {
    queueUserStateMutation({ gridMode: value });
  }
}

function readPersonalPreferences(): PersonalFilterPreferences {
  try {
    return parsePersonalFilterPreferences(
      readUserStorageValue(PERSONAL_FILTERS_STORAGE_KEY),
    );
  } catch {
    return { stores: [], categoryIds: [], catalogNotificationStores: [] };
  }
}

function savePersonalPreferences(): void {
  filters.personalStores = personalPreferences.stores;
  filters.personalCategoryIds = personalPreferences.categoryIds;
  try {
    writeUserStorageValue(
      PERSONAL_FILTERS_STORAGE_KEY,
      serializePersonalFilterPreferences(personalPreferences),
    );
  } catch {
    // Preferences remain active for this session when storage is unavailable.
  }
  if (!applyingSyncedState) {
    queueUserStateMutation({
      personalFilters: {
        stores: personalPreferences.stores,
        categoryIds: personalPreferences.categoryIds,
        catalogNotificationStores:
          personalPreferences.catalogNotificationStores,
      },
    });
  }
}

function readPersonalFilterModes(): PersonalFilterModes {
  try {
    return parsePersonalFilterModes(
      readUserStorageValue(PERSONAL_FILTER_MODES_STORAGE_KEY),
    );
  } catch {
    return { stores: "all", categories: "all" };
  }
}

function setPersonalFilterMode(
  kind: PreferenceKind,
  mode: PersonalFilterMode,
): void {
  personalFilterModes = { ...personalFilterModes, [kind]: mode };
  try {
    writeUserStorageValue(
      PERSONAL_FILTER_MODES_STORAGE_KEY,
      serializePersonalFilterModes(personalFilterModes),
    );
  } catch {
    // The selected mode remains active for this session.
  }
  if (!applyingSyncedState) {
    queueUserStateMutation({ personalFilterModes: { [kind]: mode } });
  }
}

function readFilterSelection(): UserFilterSelection {
  try {
    const raw = readUserStorageValue(FILTER_SELECTION_KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    const store = parsed.store;
    const categoryId = parsed.categoryId;
    return {
      store:
        typeof store === "string" && store.length <= 160 ? store : "all",
      categoryId:
        typeof categoryId === "string" && categoryId.length <= 160
          ? categoryId
          : "all",
    };
  } catch {
    return { store: "all", categoryId: "all" };
  }
}

function saveFilterSelection(): void {
  try {
    writeUserStorageValue(
      FILTER_SELECTION_KEY,
      JSON.stringify({
        store: filters.store,
        categoryId: filters.categoryId,
      }),
    );
  } catch {
    // The active filters remain available for this session.
  }
  if (!applyingSyncedState) {
    visitFilterHydration.keepCurrent();
    queueUserStateMutation({
      filterSelection: {
        store: filters.store,
        categoryId: filters.categoryId,
      },
    });
  }
}

function productKey(product: ProductView): string {
  return `${product.catalog_id}:${product.id}`;
}

function setStatus(message: string, loading = false): void {
  statusPanel.hidden = false;
  statusPanel.classList.remove("search-recovery-notice");
  statusPanel.classList.toggle("is-loading", loading);
  statusPanel.replaceChildren();
  if (loading) {
    const spinner = document.createElement("span");
    spinner.className = "loader";
    spinner.setAttribute("aria-hidden", "true");
    statusPanel.append(spinner);
  }
  const copy = document.createElement("p");
  copy.textContent = message;
  statusPanel.append(copy);
}

function hideStatus(): void {
  statusPanel.hidden = true;
}

function unitPriceLabel(basis: UnitPriceBasis): string {
  if (basis === "kg") {
    return t("pricePerKg");
  }
  if (basis === "l") {
    return t("pricePerLiter");
  }
  if (basis === "10pcs") {
    return t("pricePer10Pieces");
  }
  if (basis === "100pcs") {
    return t("pricePer100Pieces");
  }
  return t("pricePer100Sheets");
}

function unitPriceBasisSuffix(basis: UnitPriceBasis): string {
  const labels: Record<UnitPriceBasis, { ru: string; ro: string; uk: string }> = {
    kg: { ru: "кг", ro: "kg", uk: "кг" },
    l: { ru: "л", ro: "l", uk: "л" },
    "10pcs": { ru: "10 шт.", ro: "10 buc.", uk: "10 шт." },
    "100pcs": { ru: "100 шт.", ro: "100 buc.", uk: "100 шт." },
    "100sheets": { ru: "100 листов", ro: "100 foi", uk: "100 аркушів" },
  };
  return localized(appLocale, labels[basis]);
}

function watchMaxUnitPriceLabel(basis: UnitPriceBasis | null): string {
  if (basis === "kg") {
    return t("watchMaxUnitPriceKg");
  }
  if (basis === "l") {
    return t("watchMaxUnitPriceLiter");
  }
  if (basis === "10pcs") {
    return t("watchMaxUnitPrice10Pieces");
  }
  if (basis === "100pcs") {
    return t("watchMaxUnitPrice100Pieces");
  }
  if (basis === "100sheets") {
    return t("watchMaxUnitPrice100Sheets");
  }
  return t("watchMaxPrice");
}

function unitPriceMarkup(product: ProductView): string {
  const unit = product.unit_price;
  if (!unit) {
    return "";
  }
  const label = unitPriceLabel(unit.basis);
  const value = unit.is_range
    ? `${formatPrice(unit.minimum_mdl, appLocale)}–${formatPrice(unit.maximum_mdl, appLocale)}`
    : formatPrice(unit.minimum_mdl, appLocale);
  return `<div class="unit-price"><span>${label}</span><strong>${escapeHtml(value)}</strong></div>`;
}

function requiredCardMarkup(card: RequiredCard | undefined): string {
  const label = requiredCardText(card, appLocale);
  return label ? `<span class="price-requirement">${escapeHtml(label)}</span>` : "";
}

function priceMarkup(product: ProductView): string {
  const price = productDisplayPrice(product);
  if (!price) {
    return `<div class="price-missing">${escapeHtml(t("checkPriceInStore"))}</div>`;
  }
  const { current, old, discount } = price;
  return `
    ${requiredCardMarkup(product.required_card)}
    ${price.conditional ? `<span class="price-requirement">${escapeHtml(conditionalPriceText(product, appLocale))}</span>` : ""}
    <div class="prices">
      <strong>${escapeHtml(formatPrice(current, appLocale))}</strong>
      ${old === null ? "" : `<s>${escapeHtml(formatPrice(old, appLocale))}</s>`}
      ${discount === null ? "" : `<span>−${escapeHtml(discount)}%</span>`}
    </div>
  `;
}

function readStoredShoppingList(): ShoppingListItem[] {
  try {
    return parseShoppingList(readUserStorageValue(SHOPPING_LIST_STORAGE_KEY));
  } catch {
    return [];
  }
}

function isInShoppingList(key: string): boolean {
  return shoppingItems.some((item) => item.key === key);
}

function shoppingItemFromProduct(product: ProductView): ShoppingListItem {
  const title = product.product_ru || product.product_ro;
  return {
    ...(product.required_card ? { requiredCard: product.required_card } : {}),
    key: productKey(product),
    quantity: 1,
    title,
    originalTitle: product.product_ro === title ? null : product.product_ro,
    titleUk: product.product_uk || null,
    store: product.catalog.chain,
    catalogTitle: product.catalog.title,
    image: product.image,
    newPriceMdl: product.new_price_mdl,
    oldPriceMdl: product.old_price_mdl,
    weightOrVolume: product.weight_or_volume,
    unitPrice: product.unit_price
      ? {
          basis: product.unit_price.basis,
          minimumMdl: product.unit_price.minimum_mdl,
          maximumMdl: product.unit_price.maximum_mdl,
          isRange: product.unit_price.is_range,
        }
      : null,
    validUntil: product.catalog.valid_until,
  };
}

function enrichShoppingUnitPrices(): void {
  const productByKey = new Map(
    products.map((product) => [productKey(product), product] as const),
  );
  let changed = false;
  const enriched = shoppingItems.map((item) => {
    if (item.unitPrice) {
      return item;
    }
    const product = productByKey.get(item.key);
    if (!product?.unit_price) {
      return item;
    }
    changed = true;
    return {
      ...item,
      unitPrice: {
        basis: product.unit_price.basis,
        minimumMdl: product.unit_price.minimum_mdl,
        maximumMdl: product.unit_price.maximum_mdl,
        isRange: product.unit_price.is_range,
      },
    };
  });
  if (changed) {
    persistShoppingList(enriched);
  }
}

function persistShoppingList(nextItems: ShoppingListItem[]): void {
  const retainedItems = pruneExpiredShoppingItems(
    nextItems,
    currentShoppingDateKey(),
  );
  const mutation = shoppingMutation(shoppingItems, retainedItems);
  shoppingItems = retainedItems;
  try {
    writeUserStorageValue(
      SHOPPING_LIST_STORAGE_KEY,
      serializeShoppingList(shoppingItems),
    );
  } catch {
    // Keep the list in memory when Telegram or the browser blocks local storage.
  }
  updateShoppingUi();
  if (!applyingSyncedState && mutation) {
    queueUserStateMutation({ shopping: mutation });
  }
}

function currentUserState(): UserState {
  return {
    version: 1,
    shoppingItems,
    personalFilters: personalPreferences,
    personalFilterModes,
    filterSelection: {
      store: filters.store,
      categoryId: filters.categoryId,
    },
    gridMode,
    sort: filters.sort,
    locale: appLocale,
    themePreference,
    storeSavePromptNextEligibleAt,
    fuelVisited: fuelDiscovery.seen,
    priceWatchDefaultStores,
  };
}

function userStateBootstrapComplete(): boolean {
  try {
    return (
      localStorage.getItem(userScopedStorageKey(USER_STATE_BOOTSTRAP_KEY)) ===
      "done"
    );
  } catch {
    return false;
  }
}

function markUserStateBootstrapComplete(): void {
  try {
    localStorage.setItem(
      userScopedStorageKey(USER_STATE_BOOTSTRAP_KEY),
      "done",
    );
  } catch {
    // A later launch can safely repeat the non-destructive bootstrap merge.
  }
}

function scheduleUserStateSync(delayMs = USER_STATE_SYNC_DELAY_MS): void {
  if (!telegram.webApp?.initData || userStateSyncBlockedForSession) {
    return;
  }
  window.clearTimeout(userStateSyncTimer);
  userStateSyncTimer = window.setTimeout(() => {
    void flushUserStateSync();
  }, delayMs);
}

function queueUserStateMutation(mutation: UserStateMutation): void {
  if (!telegram.webApp?.initData) {
    return;
  }
  pendingUserStateMutation = mergeUserStateMutations(
    pendingUserStateMutation,
    mutation,
  );
  storePendingUserStateMutation();
  scheduleUserStateSync();
}

function queueActivity(key: ActivityCounterKey, amount = 1): void {
  if (!telegram.webApp?.initData || userStateSyncBlockedForSession) {
    return;
  }
  activityAccumulator.record(key, amount);
  if (userStateSyncTimer === undefined) {
    scheduleUserStateSync(ACTIVITY_SYNC_DELAY_MS);
  }
}

function applySyncedUserState(state: UserState): void {
  const restoreFuelVisit = fuelDiscovery.seen && !state.fuelVisited;
  fuelDiscovery.hydrate(state.fuelVisited);
  updateFuelDiscovery();
  if (restoreFuelVisit) queueUserStateMutation({ fuelVisited: true });
  let normalizedSelection = false;
  let normalizedSort = false;
  let normalizedModes = false;
  const localeNeedsSync = state.locale === null;
  const retainedShoppingItems = pruneExpiredShoppingItems(
    state.shoppingItems,
    currentShoppingDateKey(),
  );
  const expirationMutation = shoppingMutation(
    state.shoppingItems,
    retainedShoppingItems,
  );
  applyingSyncedState = true;
  try {
    if (state.locale) {
      void applyAppLocale(state.locale, false);
    }
    applyThemePreference(state.themePreference, false);
    storeSavePromptNextEligibleAt = Math.max(
      storeSavePromptNextEligibleAt,
      state.storeSavePromptNextEligibleAt,
    );
    writeStoreSavePromptNextEligibleAt(storeSavePromptNextEligibleAt);
    persistShoppingList(retainedShoppingItems);
    personalPreferences = state.personalFilters;
    savePersonalPreferences();
    persistPriceWatchDefaultStores(state.priceWatchDefaultStores, false);

    // The first cloud response may supply preferences missing on this device.
    // Once the user interacts (or a new warm visit begins), active filters are
    // session-owned: activity/list syncs cannot replace the visible search.
    if (document.querySelector("dialog[open]") || document.activeElement === searchInput) {
      visitFilterHydration.keepCurrent();
    }
    const initialRemote = !__KILOPRICE_WEBSITE__
      ? visitFilterHydration.takeInitial({
          ...filters,
          ...state.filterSelection,
          store: state.personalFilterModes.stores === "personal"
            ? PERSONAL_STORES_FILTER : state.filterSelection.store,
          categoryId: state.personalFilterModes.categories === "personal"
            ? PERSONAL_CATEGORIES_FILTER : state.filterSelection.categoryId,
          sort: state.sort,
        })
      : null;
    if (initialRemote) {
      Object.assign(filters, initialRemote);
      normalizedSelection = filters.store !== state.filterSelection.store ||
        filters.categoryId !== state.filterSelection.categoryId;
      normalizedSort = filters.sort !== state.sort;
      normalizedModes = state.personalFilterModes.stores !==
        (filters.store === PERSONAL_STORES_FILTER ? "personal" : "all") ||
        state.personalFilterModes.categories !==
        (filters.categoryId === PERSONAL_CATEGORIES_FILTER ? "personal" : "all");
    }

    const availableStores = new Set(
      dataset?.catalogs.map((catalog) => catalog.chain) ?? [],
    );
    const remoteStore = __KILOPRICE_WEBSITE__ ? state.filterSelection.store : filters.store;
    filters.store =
      remoteStore === PERSONAL_STORES_FILTER &&
      personalPreferences.stores.length === 0
        ? "all"
        : remoteStore !== "all" &&
            remoteStore !== PERSONAL_STORES_FILTER &&
            !availableStores.has(remoteStore)
          ? "all"
          : remoteStore;
    normalizedSelection ||= filters.store !== remoteStore;

    const availableCategories = new Set(
      dataset?.categories?.map((category) => category.id) ?? [],
    );
    const remoteCategory = __KILOPRICE_WEBSITE__ ? state.filterSelection.categoryId : filters.categoryId;
    filters.categoryId =
      remoteCategory === PERSONAL_CATEGORIES_FILTER &&
      personalPreferences.categoryIds.length === 0
        ? "all"
        : remoteCategory !== "all" &&
            remoteCategory !== PERSONAL_CATEGORIES_FILTER &&
            !availableCategories.has(remoteCategory)
          ? "all"
          : remoteCategory;
    normalizedSelection ||= filters.categoryId !== remoteCategory;
    if (normalizedSelection) {
      normalizedModes ||= state.personalFilterModes.stores !==
        (filters.store === PERSONAL_STORES_FILTER ? "personal" : "all") ||
        state.personalFilterModes.categories !==
        (filters.categoryId === PERSONAL_CATEGORIES_FILTER ? "personal" : "all");
    }
    if (__KILOPRICE_WEBSITE__) {
      filters.catalogId = "all";
      filters.sort = state.sort;
    }
    setPersonalFilterMode("stores", filters.store === PERSONAL_STORES_FILTER ? "personal" : "all");
    setPersonalFilterMode("categories", filters.categoryId === PERSONAL_CATEGORIES_FILTER ? "personal" : "all");
    saveFilterSelection();
    saveSortMode(filters.sort);
    applyGridMode(state.gridMode, false);
    enrichShoppingUnitPrices();
    resetAndRender();
  } finally {
    applyingSyncedState = false;
  }
  if (normalizedSelection) {
    saveFilterSelection();
  }
  if (normalizedSort) saveSortMode(filters.sort);
  if (normalizedModes) queueUserStateMutation({ personalFilterModes });
  if (localeNeedsSync) {
    queueUserStateMutation({ locale: appLocale });
  }
  if (expirationMutation) {
    queueUserStateMutation({ shopping: expirationMutation });
  }
}

async function flushUserStateSync(): Promise<void> {
  const initData = telegram.webApp?.initData;
  if (!initData || userStateSyncBlockedForSession) {
    return;
  }
  if (userStateSyncRequest) {
    return userStateSyncRequest;
  }
  window.clearTimeout(userStateSyncTimer);
  userStateSyncTimer = undefined;
  const mutation = pendingUserStateMutation;
  pendingUserStateMutation = null;
  storePendingUserStateMutation();
  const activity = activityAccumulator.take();
  const bootstrap = userStateBootstrapComplete()
    ? undefined
    : currentUserState();

  const request = (async () => {
    try {
      const result = await syncUserState(initData, {
        bootstrapState: bootstrap,
        mutation: mutation ?? undefined,
        activity,
      });
      markUserStateBootstrapComplete();
      businessOwnerAccess = businessPreviewAllowed(IS_ADMIN_PREVIEW, result.isTester);
      updateFuelAccess();
      userStateSyncFailureCount = 0;
      lastUserStateSyncAt = Date.now();
      const protectedState = pendingUserStateMutation
        ? applyUserStateMutation(result.state, pendingUserStateMutation)
        : result.state;
      storeButtonGlow.setPreferencesReady(true);
      applySyncedUserState(protectedState);
      if (result.priceWatches) {
        applyPriceWatchState(result.priceWatches);
      }
    } catch (error) {
      if (activity) {
        activityAccumulator.restore(activity);
      }
      pendingUserStateMutation = mergeUserStateMutations(
        mutation,
        pendingUserStateMutation,
      );
      storePendingUserStateMutation();
      if (isTerminalUserStateSyncError(error)) {
        updateFuelAccess();
        userStateSyncBlockedForSession = true;
        storeButtonGlow.setPreferencesReady(false);
        updateStoreButtonGlow();
      } else {
        userStateSyncFailureCount += 1;
      }
    }
  })();
  userStateSyncRequest = request;
  try {
    await request;
  } finally {
    userStateSyncRequest = null;
    if (
      (pendingUserStateMutation || activityAccumulator.hasPending()) &&
      !userStateSyncBlockedForSession
    ) {
      const delay =
        userStateSyncFailureCount > 0 || pendingUserStateMutation
          ? userStateSyncRetryDelay(userStateSyncFailureCount)
          : ACTIVITY_SYNC_DELAY_MS;
      scheduleUserStateSync(delay);
    }
  }
}

function refreshUserStateIfNeeded(): void {
  if (
    document.visibilityState !== "visible" ||
    Date.now() - lastUserStateSyncAt < USER_STATE_REFRESH_INTERVAL_MS
  ) {
    return;
  }
  void flushUserStateSync();
}

function changeProductShoppingQuantity(product: ProductView, delta: -1 | 1): void {
  const key = productKey(product);
  const before = shoppingItems.find((item) => item.key === key)?.quantity ?? 0;
  const next = adjustShoppingQuantity(shoppingItems, key, delta, shoppingItemFromProduct(product));
  if (next === shoppingItems) return;
  if (!before) {
    queueActivity("shoppingAdds");
    signalHomeScreenUsefulAction();
  } else if (before === 1 && delta === -1) {
    queueActivity("shoppingRemoves");
  }
  persistShoppingList(next);
}

function productQuantityView(product: ProductView, kind: QuantityControlKind): QuantityControlView {
  const key = productKey(product);
  return {
    key, kind,
    title: localizedProductTitle(product),
    quantity: shoppingItems.find((item) => item.key === key)?.quantity ?? 0,
    expanded: kind !== "catalog" || activeCatalogQuantityKey === key,
  };
}

function handleProductQuantityClick(
  event: MouseEvent,
  lookup: (key: string) => ProductView | undefined,
): boolean {
  const target = event.target as Element;
  const control = target.closest<HTMLElement>("[data-quantity-key]");
  if (!control) return false;
  // A tap on the count/checkmark must never open the underlying product card.
  const button = target.closest<HTMLButtonElement>("button[data-quantity-action]");
  const product = lookup(control.dataset.quantityKey ?? "");
  if (!button || button.disabled || !product) return true;
  const action = button.dataset.quantityAction;
  if (action !== "add" && action !== "increase" && action !== "decrease" && action !== "expand") return true;
  hapticTap(telegram);
  if (control.dataset.quantityKind === "catalog") activeCatalogQuantityKey = productKey(product);
  if (action === "expand") updateCatalogShoppingControls();
  else changeProductShoppingQuantity(product, action === "decrease" ? -1 : 1);
  return true;
}

function localizedWatchTitle(rule: PriceWatchRuleDraft): string {
  return localized(appLocale, { ru: rule.titleRu, ro: rule.titleRo, uk: rule.titleUk });
}

function watchRuleForProduct(product: ProductView): PriceWatchRule | undefined {
  const key = productKey(product);
  return priceWatchState.rules.find(
    (rule) =>
      rule.targetKind === "product" &&
      (rule.sourceProductKey === key ||
        (Boolean(product.product_id) && rule.productId === product.product_id)),
  );
}

function productWatchView(product: ProductView): ProductWatchControlView {
  const rule = watchRuleForProduct(product);
  return {
    key: productKey(product),
    title: localizedProductTitle(product),
    state: rule ? rule.enabled ? "active" : "paused" : "none",
  };
}

function updateFeedWatchControls(): void {
  productGrid.querySelectorAll<HTMLButtonElement>("button[data-price-watch-product]").forEach(button => {
    const product = renderedProducts.get(button.dataset.priceWatchProduct ?? "");
    if (product) updateProductWatchControl(button, productWatchView(product));
  });
}

function defaultPriceWatchStores(): string[] {
  return newPriceWatchStores(priceWatchDefaultStores, personalPreferences.stores);
}

function readPriceWatchDefaultStores(): PersonalFilterMode {
  try {
    return readUserStorageValue("kiloprice:watch-default-stores:v1") === "personal" ? "personal" : "all";
  } catch { return "all"; }
}

function persistPriceWatchDefaultStores(scope: PersonalFilterMode, sync = true): void {
  const changed = priceWatchDefaultStores !== scope;
  priceWatchDefaultStores = scope;
  try { writeUserStorageValue("kiloprice:watch-default-stores:v1", scope); } catch { /* Cloud sync still works. */ }
  if (changed && sync) queueUserStateMutation({ priceWatchDefaultStores: scope });
}

function updatePriceWatchDefaultOption(): void {
  // Keep a saved opt-in reachable even on an all-store rule or after stores were removed.
  priceWatchDefaultOption.hidden = radioValue("price-watch-stores") !== "personal" && !priceWatchDefaultStoresInput.checked;
  priceWatchDefaultStoresInput.disabled = personalPreferences.stores.length === 0 && !priceWatchDefaultStoresInput.checked;
}

function currentPriceWatchBaselineCatalogs(): string[] {
  return dataset?.catalogs.map((catalog) => catalog.id) ?? [];
}

function priceWatchRuleSummary(rule: PriceWatchRuleDraft): string {
  const target =
    rule.targetKind === "query"
      ? t("watchQuerySummary", { query: rule.query ?? localizedWatchTitle(rule) })
      : rule.matchMode === "analogues"
        ? t("watchAnaloguesSummary")
        : t("watchExactSummary");
  const stores =
    rule.stores.length === 0
      ? t("watchAllStoresSummary")
      : t("watchMyStoresSummary", { count: rule.stores.length });
  return `${target} ${stores}.`;
}

function priceWatchConditionSummary(rule: PriceWatchRuleDraft): string {
  if (rule.priceMode !== "below") {
    return t("watchAnySummary");
  }
  if (rule.maxUnitPriceMdl !== null && rule.unitBasis) {
    return t("watchBelowUnitHelp", {
      price: formatUiNumber(rule.maxUnitPriceMdl),
      unit: unitPriceBasisSuffix(rule.unitBasis),
    });
  }
  if (rule.maxPriceMdl !== null) {
    return t("watchBelowItemHelp", {
      price: formatUiNumber(rule.maxPriceMdl),
    });
  }
  return t("watchAnySummary");
}

function updatePriceWatchFab(): void {
  const { tracked, matching } = priceWatchBadgeCounts(priceWatchState);
  priceWatchTotalCount.hidden = tracked === 0;
  priceWatchTotalCount.textContent = tracked > 99 ? "99+" : String(tracked);
  priceWatchResultCount.hidden = matching === 0;
  priceWatchResultCount.textContent = matching > 99 ? "99+" : String(matching);
  priceWatchButton.setAttribute(
    "aria-label",
    tracked === 0
      ? t("priceWatches")
      : t("priceWatchesCountAria", { count: tracked, matches: matching }),
  );
}

function applyPriceWatchState(state: PriceWatchState): void {
  priceWatchState = state;
  priceWatchesLoaded = true;
  priceWatchesLoadedDate = dateKeyInMoldova(currentReferenceTime());
  updatePriceWatchFab();
  updateFeedWatchControls();
  if (priceWatchListDialog.open) {
    renderPriceWatchList();
  }
  if (dialog.open && activeDialogProduct) {
    dialogMeta.innerHTML = fullProductMarkup(activeDialogProduct);
  }
  maybeOfferCatalogStoreSave();
}

async function ensurePriceWatchesLoaded(): Promise<PriceWatchState> {
  if (priceWatchesLoaded && priceWatchesLoadedDate === dateKeyInMoldova(currentReferenceTime())) {
    return priceWatchState;
  }
  if (priceWatchRequest) {
    return priceWatchRequest;
  }
  const initData = telegram.webApp?.initData;
  if (!initData) {
    throw new PriceWatchApiError("unauthorized", 401);
  }
  priceWatchRequest = loadPriceWatches(initData);
  try {
    const state = await priceWatchRequest;
    applyPriceWatchState(state);
    return state;
  } finally {
    priceWatchRequest = null;
  }
}

function localizedAlertTitle(alert: PriceAlertEvent): string {
  return localized(appLocale, {
    ru: alert.product.titleRu,
    ro: alert.product.titleRo,
    uk: alert.product.titleUk,
  });
}

function compactPriceWatchDate(value: string): string {
  const parsed = new Date(`${value}T12:00:00`);
  return Number.isNaN(parsed.getTime())
    ? value
    : new Intl.DateTimeFormat(localeTag(appLocale), {
        day: "2-digit",
        month: "2-digit",
      }).format(parsed);
}

function priceAlertCardMarkup(alert: PriceAlertEvent): string {
  const title = localizedAlertTitle(alert);
  const product = productRelationshipIndex.byKey.get(alert.product.productKey);
  const price =
    alert.product.newPriceMdl === null
      ? t("priceInStore")
      : formatPrice(alert.product.newPriceMdl, appLocale);
  const unit =
    alert.product.unitPriceMdl !== null && alert.product.unitBasis
      ? `${formatPrice(alert.product.unitPriceMdl, appLocale)} ${unitPriceLabel(alert.product.unitBasis)}`
      : "";
  const content = `
    <span class="price-alert-image"><img src="${escapeHtml(appAssetUrl(priceAlertImage(alert, product)))}" alt="" loading="lazy" /></span>
    <span class="price-alert-copy">
      <strong>${escapeHtml(title)}</strong>
      <p>${escapeHtml(t("watchOfferUntil", {
        store: alert.product.store,
        date: compactPriceWatchDate(alert.product.validUntil),
      }))}</p>
      <span class="price-alert-prices"><b>${escapeHtml(price)}</b>${unit ? `<span>${escapeHtml(unit)}</span>` : ""}</span>
    </span>
  `;
  return product
    ? `<button class="price-alert-card${alert.readAt ? "" : " is-unread"}" type="button" data-alert-product-key="${escapeHtml(alert.product.productKey)}" aria-label="${escapeHtml(t("watchOpenProduct", { title }))}">${content}</button>`
    : `<article class="price-alert-card${alert.readAt ? "" : " is-unread"}">${content}</article>`;
}

function priceWatchRuleCardMarkup(rule: PriceWatchRule, alerts: readonly PriceAlertEvent[] = []): string {
  const title = localizedWatchTitle(rule);
  return `
    <article class="price-watch-rule-card${alerts.length ? " has-results" : ""}${rule.enabled ? "" : " is-disabled"}" data-watch-rule-id="${escapeHtml(rule.id)}">
      <div class="price-watch-rule-head">
        <div class="price-watch-rule-copy">
          <strong>${escapeHtml(title)}</strong>
          <p>${escapeHtml(rule.enabled ? priceWatchConditionSummary(rule) : t("watchPaused"))}</p>
          ${alerts.length ? `<span class="price-watch-result-count">${escapeHtml(t("watchProductsCount", { count: alerts.length }))}</span>` : ""}
        </div>
        <div class="price-watch-rule-actions">
          <button type="button" data-watch-toggle="${escapeHtml(rule.id)}" data-watch-enabled="${String(!rule.enabled)}" aria-label="${escapeHtml(t(rule.enabled ? "watchDisableAria" : "watchEnableAria", { title }))}">${rule.enabled ? "Ⅱ" : "▶"}</button>
          <button type="button" data-watch-edit="${escapeHtml(rule.id)}" aria-haspopup="dialog" aria-controls="price-watch-editor-dialog" aria-label="${escapeHtml(t("watchEditAria", { title }))}">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M10 2h4l.6 2.4 2 1.2 2.4-.7 2 3.5-1.8 1.7v3.8l1.8 1.7-2 3.5-2.4-.7-2 1.2L14 22h-4l-.6-2.4-2-1.2-2.4.7-2-3.5 1.8-1.7v-3.8L3 8.4l2-3.5 2.4.7 2-1.2L10 2Z"/><circle cx="12" cy="12" r="3"/></svg>
          </button>
        </div>
      </div>
      ${alerts.length ? `<div class="price-watch-rule-results" role="group" aria-label="${escapeHtml(t("watchResultsAria", { title }))}">${alerts.map(priceAlertCardMarkup).join("")}</div>` : ""}
    </article>
  `;
}

function replacePriceWatchListContent(markup: string): void {
  const scrollTop = priceWatchListContent.scrollTop;
  const focused = document.activeElement instanceof HTMLButtonElement &&
    priceWatchListContent.contains(document.activeElement) ? document.activeElement : null;
  const focusAttribute = focused?.hasAttribute("data-watch-edit") ? "data-watch-edit"
    : focused?.hasAttribute("data-watch-toggle") ? "data-watch-toggle"
    : focused?.hasAttribute("data-alert-product-key") ? "data-alert-product-key" : null;
  const focusValue = focusAttribute ? focused?.getAttribute(focusAttribute) : null;
  priceWatchListContent.innerHTML = markup;
  if (focusAttribute && focusValue && !priceWatchEditorDialog.open) {
    priceWatchListContent.querySelector<HTMLButtonElement>(
      `button[${focusAttribute}="${CSS.escape(focusValue)}"]`,
    )?.focus({ preventScroll: true });
  }
  // State refreshes can replace a tall list with a short intermediate layout.
  // Preserve its position after replacement, clamped naturally after deletion.
  priceWatchListContent.scrollTop = scrollTop;
}

function renderPriceWatchList(): void {
  const { withResults, waiting, paused } = groupPriceWatchResults(priceWatchState, dateKeyInMoldova(currentReferenceTime()));
  if (priceWatchState.rules.length === 0) {
    replacePriceWatchListContent(`
      <div class="shopping-list-empty">
        <span class="shopping-list-empty-icon" aria-hidden="true">🔔</span>
        <h3>${escapeHtml(t("watchEmptyTitle"))}</h3>
        <p>${escapeHtml(t("watchEmptyHelp"))}</p>
      </div>
    `);
    return;
  }
  replacePriceWatchListContent([
    withResults.length
      ? `<section class="price-watch-section" data-watch-section="results"><div class="price-watch-section-heading"><h3>${escapeHtml(t("watchRecentSection"))}</h3><span>${escapeHtml(t("watchRulesCount", { count: withResults.length }))}</span></div><div class="price-watch-cards">${withResults.map(({ rule, alerts }) => priceWatchRuleCardMarkup(rule, alerts)).join("")}</div></section>`
      : `<section class="price-watch-section" data-watch-section="results"><div class="price-watch-section-heading"><h3>${escapeHtml(t("watchRecentSection"))}</h3></div><p class="catalog-library-description">${escapeHtml(t("watchNoRecentMatches"))}</p></section>`,
    waiting.length
      ? `<section class="price-watch-section" data-watch-section="waiting"><div class="price-watch-section-heading"><h3>${escapeHtml(t("watchActiveSection"))}</h3><span>${escapeHtml(t("watchActiveCount", { count: waiting.length }))}</span></div><div class="price-watch-cards">${waiting.map(rule => priceWatchRuleCardMarkup(rule)).join("")}</div></section>`
      : "",
    paused.length
      ? `<section class="price-watch-section" data-watch-section="paused"><div class="price-watch-section-heading"><h3>${escapeHtml(t("watchPausedSection"))}</h3><span>${paused.length}</span></div><div class="price-watch-cards">${paused.map(rule => priceWatchRuleCardMarkup(rule)).join("")}</div></section>`
      : "",
  ].join(""));
}

async function openPriceWatchList(editRuleId?: string): Promise<void> {
  if (!telegram.webApp?.initData) {
    window.alert(t("watchTelegramOnly"));
    return;
  }
  const openToken = ++priceWatchListOpenToken;
  if (!priceWatchListDialog.open) {
    priceWatchButton.setAttribute("aria-expanded", "true");
    priceWatchListContent.innerHTML = `<div class="price-watch-loading"><span class="loader"></span><span>${escapeHtml(t("preparingProducts"))}</span></div>`;
    priceWatchListDialog.showModal();
  }
  try {
    const state = await ensurePriceWatchesLoaded();
    if (!priceWatchListDialog.open || openToken !== priceWatchListOpenToken) return;
    renderPriceWatchList();
    if (editRuleId) {
      // The API only returns this authenticated user's rules. Never fetch a
      // foreign rule by the link identifier or create a replacement for it.
      if (document.querySelector('dialog[open]:not(#price-watch-list-dialog)')) return;
      const rule = state.rules.find(candidate => candidate.id === editRuleId);
      if (rule) {
        priceWatchReturnToList = { scrollTop: priceWatchListContent.scrollTop, ruleId: rule.id };
        showPriceWatchEditor(rule);
      } else {
        priceWatchListContent.insertAdjacentHTML("afterbegin",
          `<p id="price-watch-link-status" class="catalog-library-description" role="status">${escapeHtml(t("watchLinkUnavailable"))}</p>`);
      }
      // Opening settings is not reading every matched offer in the account.
      return;
    }
    const unread = groupPriceWatchResults(state, dateKeyInMoldova(currentReferenceTime())).withResults
      .flatMap(group => group.alerts).filter((alert) => !alert.readAt)
      .slice(0, MAX_PRICE_WATCH_ALERTS).map((alert) => alert.id);
    if (unread.length) {
      const now = new Date().toISOString();
      applyPriceWatchState({
        ...state,
        alerts: state.alerts.map((alert) =>
          unread.includes(alert.id) ? { ...alert, readAt: now } : alert,
        ),
      });
      const initData = telegram.webApp?.initData;
      if (initData) {
        void markPriceAlertsRead(initData, unread)
          .then(applyPriceWatchState)
          .catch(() => {
            // A later opening safely retries; reading is not a destructive action.
          });
      }
    }
  } catch {
    if (!priceWatchListDialog.open || openToken !== priceWatchListOpenToken) return;
    priceWatchListContent.innerHTML = `<div class="shopping-list-empty"><span class="shopping-list-empty-icon" aria-hidden="true">!</span><h3>${escapeHtml(t("watchLoadFailed"))}</h3></div>`;
  }
}

function closePriceWatchList(): void {
  if (priceWatchListDialog.open) {
    priceWatchListDialog.close();
  }
}

function findExistingQueryWatch(query: string): PriceWatchRule | undefined {
  const normalized = query.trim().toLocaleLowerCase();
  return priceWatchState.rules.find(
    (rule) =>
      rule.targetKind === "query" &&
      rule.query?.trim().toLocaleLowerCase() === normalized,
  );
}

function copyPriceWatchDraft(rule: PriceWatchRuleDraft): PriceWatchRuleDraft {
  return {
    ...rule,
    stores: [...rule.stores],
    baselineCatalogIds: [...rule.baselineCatalogIds],
  };
}

function setRadioValue(name: string, value: string): void {
  priceWatchForm
    .querySelectorAll<HTMLInputElement>(`input[name="${name}"]`)
    .forEach((input) => {
      input.checked = input.value === value;
    });
}

function radioValue(name: string): string {
  return (
    priceWatchForm.querySelector<HTMLInputElement>(
      `input[name="${name}"]:checked`,
    )?.value ?? ""
  );
}

function numberInputValue(input: HTMLInputElement): number | null {
  if (!input.value.trim()) {
    return null;
  }
  const value = Number(input.value);
  return Number.isFinite(value) && value >= 0 ? value : null;
}

function updatePriceWatchEditorFields(): void {
  if (!editingPriceWatch) {
    return;
  }
  const priceMode = radioValue("price-watch-price-mode");
  const usesUnit = editingPriceWatch.unitBasis !== null;
  priceWatchMaxUnitPriceRow.hidden = priceMode !== "below" || !usesUnit;
  priceWatchMaxPriceRow.hidden = priceMode !== "below" || usesUnit;
  // Invisible price inputs must not block submitting the "any price" mode.
  priceWatchMaxUnitPrice.disabled = priceWatchMaxUnitPriceRow.hidden;
  priceWatchMaxPrice.disabled = priceWatchMaxPriceRow.hidden;
  priceWatchMaxUnitPrice.required = !priceWatchMaxUnitPriceRow.hidden;
  priceWatchMaxPrice.required = !priceWatchMaxPriceRow.hidden;
  priceWatchForm.classList.toggle("is-any-price", priceMode !== "below");
}

function syncPriceWatchViewport(): void {
  if (!priceWatchEditorDialog.open) return;
  const viewport = window.visualViewport;
  const height = viewport?.height ?? window.innerHeight;
  const top = viewport?.offsetTop ?? 0;
  priceWatchEditorDialog.style.setProperty("--watch-viewport-height", `${height}px`);
  priceWatchEditorDialog.style.setProperty("--watch-viewport-top", `${top}px`);
  priceWatchEditorDialog.style.setProperty(
    "--watch-viewport-bottom",
    `${Math.max(0, window.innerHeight - top - height)}px`,
  );
}

function renderPriceWatchEditor(): void {
  const rule = editingPriceWatch;
  if (!rule) {
    return;
  }
  priceWatchTargetTitle.textContent = localizedWatchTitle(rule);
  priceWatchTargetSummary.textContent = priceWatchRuleSummary(rule);
  priceWatchMatchFieldset.hidden = rule.targetKind === "query";
  const analogueInput = priceWatchForm.querySelector<HTMLInputElement>(
    'input[name="price-watch-match"][value="analogues"]',
  );
  if (analogueInput) {
    analogueInput.disabled = !rule.comparisonGroupId;
  }
  setRadioValue("price-watch-match", rule.matchMode);
  setRadioValue("price-watch-price-mode", rule.priceMode);
  priceWatchBelowOption.hidden = false;
  priceWatchBelowHelp.textContent = t("watchSetPriceHelp");
  setRadioValue(
    "price-watch-stores",
    rule.stores.length ? "personal" : "all",
  );
  const availablePersonalStores =
    personalPreferences.stores.length > 0
      ? personalPreferences.stores
      : rule.stores;
  priceWatchPersonalStores.disabled = availablePersonalStores.length === 0;
  priceWatchPersonalStoresHelp.textContent =
    availablePersonalStores.length > 0
      ? t("watchMyStoresHelp", { count: availablePersonalStores.length })
      : t("watchMyStoresEmptyHelp");
  priceWatchDefaultStoresInput.checked = priceWatchDefaultStores === "personal";
  priceWatchDefaultDirty = false;
  updatePriceWatchDefaultOption();
  priceWatchMaxPrice.value = rule.maxPriceMdl?.toString() ?? "";
  priceWatchMaxUnitPrice.value = rule.maxUnitPriceMdl?.toString() ?? "";
  priceWatchMinDiscount.value = rule.minimumDiscountPercent?.toString() ?? "";
  priceWatchMaxUnitLabel.textContent = watchMaxUnitPriceLabel(rule.unitBasis);
  priceWatchDelete.hidden = !rule.id;
  priceWatchSave.textContent = t(rule.id ? "saveWatch" : "enableWatch");
  priceWatchNotifyNote.textContent = priceWatchState.canNotify
    ? t("watchNotificationsReady")
    : t("watchNeedsBotAccess");
  priceWatchNotifyNote.classList.toggle(
    "is-warning",
    !priceWatchState.canNotify,
  );
  priceWatchFormStatus.textContent = "";
  priceWatchFormStatus.classList.remove("is-error");
  updatePriceWatchEditorFields();
}

function showPriceWatchEditor(rule: PriceWatchRuleDraft): void {
  editingPriceWatch = copyPriceWatchDraft(rule);
  renderPriceWatchEditor();
  if (!priceWatchEditorDialog.open) {
    priceWatchEditorDialog.showModal();
  }
  priceWatchForm.scrollTop = 0;
  syncPriceWatchViewport();
}

async function openProductPriceWatchEditor(product: ProductView, fromFeed = false): Promise<void> {
  if (openingProductPriceWatch || priceWatchEditorDialog.open) return;
  if (!telegram.webApp?.initData) {
    window.alert(t("watchTelegramOnly"));
    return;
  }
  openingProductPriceWatch = true;
  try {
    try {
      await ensurePriceWatchesLoaded();
      if (userStateSyncRequest) await userStateSyncRequest;
    } catch {
      window.alert(t("watchLoadFailed"));
      return;
    }
    // A slow fetch must not replace a different dialog opened in the meantime.
    if (fromFeed && document.querySelector("dialog[open]")) return;
    const existing = watchRuleForProduct(product);
    const rule = existing
      ? refreshProductPriceWatchDraft(copyPriceWatchDraft(existing), product)
      : productPriceWatchDraft(
          product,
          appLocale,
          defaultPriceWatchStores(),
          currentPriceWatchBaselineCatalogs(),
        );
    if (dialog.open) {
      priceWatchReturnProduct = {
        product,
        history: [...productDialogHistory],
      };
      dialog.close();
    } else if (fromFeed) {
      priceWatchReturnToFeed = { key: productKey(product), scrollY: window.scrollY };
    }
    showPriceWatchEditor(rule);
  } finally {
    openingProductPriceWatch = false;
  }
}

async function openQueryPriceWatchEditor(query: string): Promise<void> {
  if (!telegram.webApp?.initData) {
    window.alert(t("watchTelegramOnly"));
    return;
  }
  try {
    await ensurePriceWatchesLoaded();
    if (userStateSyncRequest) await userStateSyncRequest;
  } catch {
    window.alert(t("watchLoadFailed"));
    return;
  }
  const existing = findExistingQueryWatch(query);
  showPriceWatchEditor(
    existing
      ? copyPriceWatchDraft(existing)
      : queryPriceWatchDraft(
          query,
          appLocale,
          defaultPriceWatchStores(),
          currentPriceWatchBaselineCatalogs(),
        ),
  );
}

function closePriceWatchEditor(): void {
  if (priceWatchEditorDialog.open) {
    priceWatchEditorDialog.close();
  }
}

function priceWatchErrorMessage(error: unknown): string {
  if (
    error instanceof PriceWatchApiError &&
    error.code === "price_watch_limit_reached"
  ) {
    return t("watchLimitReached");
  }
  return t("watchSaveFailed");
}

function draftFromPriceWatchEditor(): PriceWatchRuleDraft | null {
  if (!editingPriceWatch) {
    return null;
  }
  const matchMode =
    editingPriceWatch.targetKind === "query"
      ? "query"
      : radioValue("price-watch-match") === "analogues"
        ? "analogues"
        : "exact";
  const priceMode =
    radioValue("price-watch-price-mode") === "below" ? "below" : "any";
  const maxPriceMdl =
    priceMode === "below" && editingPriceWatch.unitBasis === null
      ? numberInputValue(priceWatchMaxPrice)
      : null;
  const maxUnitPriceMdl =
    priceMode === "below" && editingPriceWatch.unitBasis !== null
      ? numberInputValue(priceWatchMaxUnitPrice)
      : null;
  if (priceMode === "below" && maxPriceMdl === null && maxUnitPriceMdl === null) {
    return null;
  }
  return {
    ...editingPriceWatch,
    matchMode,
    priceMode,
    maxPriceMdl,
    maxUnitPriceMdl,
    minimumDiscountPercent: numberInputValue(priceWatchMinDiscount),
    stores:
      radioValue("price-watch-stores") === "personal"
        ? [
            ...(personalPreferences.stores.length > 0
              ? personalPreferences.stores
              : editingPriceWatch.stores),
          ]
        : [],
    baselineCatalogIds: currentPriceWatchBaselineCatalogs(),
    locale: appLocale,
    enabled: editingPriceWatch.enabled,
  };
}

async function submitPriceWatch(): Promise<void> {
  const initData = telegram.webApp?.initData;
  const draft = draftFromPriceWatchEditor();
  if (!initData || !draft || priceWatchMutationInProgress) {
    if (!draft) {
      const missingPrice =
        radioValue("price-watch-price-mode") === "below" &&
        numberInputValue(priceWatchMaxPrice) === null &&
        numberInputValue(priceWatchMaxUnitPrice) === null;
      priceWatchFormStatus.textContent = t(
        missingPrice ? "watchPriceRequired" : "watchSaveFailed",
      );
      priceWatchFormStatus.classList.add("is-error");
      if (missingPrice) {
        window.requestAnimationFrame(() => {
          (editingPriceWatch?.unitBasis
            ? priceWatchMaxUnitPrice
            : priceWatchMaxPrice
          ).focus();
        });
      }
    }
    return;
  }
  priceWatchMutationInProgress = true;
  priceWatchSave.disabled = true;
  priceWatchDelete.disabled = true;
  priceWatchSave.textContent = t("watchSaving");
  priceWatchFormStatus.textContent = "";
  // Capture the submitted preference; a later click or failed save must not commit it.
  const submittedDefault = priceWatchDefaultDirty
    ? priceWatchDefaultStoresInput.checked ? "personal" : "all"
    : null;
  try {
    const created = !draft.id;
    const state = await savePriceWatch(initData, draft);
    if (submittedDefault) persistPriceWatchDefaultStores(submittedDefault);
    applyPriceWatchState(state);
    priceWatchFormStatus.textContent = t("watchSaved");
    if (!state.canNotify) {
      botAccessResolved = false;
      clearBotAccessPromptCooldown();
      scheduleBotAccessPrompt(600);
    }
    closePriceWatchEditor();
    if (created) {
      signalHomeScreenUsefulAction();
    }
  } catch (error) {
    priceWatchFormStatus.textContent = priceWatchErrorMessage(error);
    priceWatchFormStatus.classList.add("is-error");
  } finally {
    priceWatchMutationInProgress = false;
    priceWatchSave.disabled = false;
    priceWatchDelete.disabled = false;
    if (editingPriceWatch) {
      priceWatchSave.textContent = t(editingPriceWatch.id ? "saveWatch" : "enableWatch");
    }
  }
}

async function removeEditingPriceWatch(): Promise<void> {
  const initData = telegram.webApp?.initData;
  const ruleId = editingPriceWatch?.id;
  if (!initData || !ruleId || priceWatchMutationInProgress) {
    return;
  }
  priceWatchMutationInProgress = true;
  priceWatchSave.disabled = true;
  priceWatchDelete.disabled = true;
  try {
    applyPriceWatchState(await deletePriceWatch(initData, ruleId));
    closePriceWatchEditor();
  } catch (error) {
    priceWatchFormStatus.textContent = priceWatchErrorMessage(error);
    priceWatchFormStatus.classList.add("is-error");
  } finally {
    priceWatchMutationInProgress = false;
    priceWatchSave.disabled = false;
    priceWatchDelete.disabled = false;
  }
}

function updateShoppingCardButtons(): void {
  productGrid
    .querySelectorAll<HTMLElement>("[data-quantity-key]")
    .forEach((control) => {
      const key = control.dataset.quantityKey;
      const product = key ? renderedProducts.get(key) : undefined;
      if (product) updateProductQuantityControl(control, productQuantityView(product, "card"));
    });
}

function updateDialogShoppingButton(): void {
  if (!activeDialogProduct) {
    return;
  }
  const view = productQuantityView(activeDialogProduct, "dialog");
  const control = dialogShoppingControl.querySelector<HTMLElement>("[data-quantity-key]");
  if (control) updateProductQuantityControl(control, view);
  else dialogShoppingControl.innerHTML = productQuantityControlMarkup(view);
}

function formatShoppingDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : value;
}

function shoppingUnitPriceMarkup(item: ShoppingListItem): string {
  const unit = item.unitPrice;
  if (!unit) {
    return "";
  }
  const label = unitPriceLabel(unit.basis);
  const value = unit.isRange
    ? `${formatPrice(unit.minimumMdl, appLocale)}–${formatPrice(unit.maximumMdl, appLocale)}`
    : formatPrice(unit.minimumMdl, appLocale);
  return `<div class="shopping-list-unit-price"><span>${label}</span><strong>${escapeHtml(value)}</strong></div>`;
}

function bindShoppingImageFallbacks(container: HTMLElement): void {
  const markMissing = (image: HTMLImageElement): void => {
    image.hidden = true;
    image.closest<HTMLElement>(".shopping-list-image")?.classList.add("is-missing");
  };
  container.querySelectorAll<HTMLImageElement>(".shopping-list-image img").forEach((image) => {
    image.addEventListener("error", () => markMissing(image), { once: true });
    if (image.complete && image.naturalWidth === 0) {
      markMissing(image);
    }
  });
}

function expiredShoppingHeadingMarkup(): string {
  return `
    <div class="shopping-list-expired-heading">
      <strong>${escapeHtml(t("expiredOffers"))}</strong>
      <span>${escapeHtml(t("expiredOffersHelp", { days: EXPIRED_OFFER_RETENTION_DAYS }))}</span>
    </div>
  `;
}

function shoppingCatalogHeadingMarkup(
  group: ShoppingStoreGroup,
  catalog: ShoppingCatalogGroup,
): string {
  const validity = catalog.expired
    ? t("offerEnded", { date: formatShoppingDate(catalog.validUntil) })
    : t("offerUntil", { date: formatShoppingDate(catalog.validUntil) });
  const title = shoppingCatalogDisplayTitle(catalog.title, group.store);
  return `
    <div class="shopping-catalog-heading">
      <strong>${escapeHtml(validity)}</strong>
      ${title ? `<span>${escapeHtml(title)}</span>` : ""}
    </div>
  `;
}

function shoppingUnknownPriceHint(count: number): string {
  return t("shoppingUnknownPrices", { count });
}

function shoppingTotalAmountMarkup(bani: number): string {
  // Reuse locale/rounding from every existing price; separate only the currency.
  const amount = formatPrice(bani / 100, appLocale);
  const separator = amount.lastIndexOf(" ");
  return `<span class="shopping-total-number">${escapeHtml(amount.slice(0, separator))}</span> <span class="shopping-total-unit">${escapeHtml(amount.slice(separator + 1))}</span>`;
}

function shoppingMoneyTotalMarkup(
  total: ShoppingMoneyTotal | ShoppingStoreMoneyTotal,
  scope: "store" | "overall",
  savings?: ShoppingSavingsSummary,
): string {
  const store = "store" in total ? total.store : "";
  let label: string;
  if (total.knownPriceRows === 0) {
    label = scope === "store"
      ? t("shoppingStoreTotalUnavailable", { store })
      : t("shoppingOverallTotalUnavailable");
  } else {
    const amount = formatPrice(total.totalBani / 100, appLocale);
    label = scope === "store"
      ? t(
          total.complete
            ? "shoppingStoreTotal"
            : "shoppingStoreKnownTotal",
          { store, amount },
        )
      : t(
          total.complete
            ? "shoppingOverallTotal"
            : "shoppingOverallKnownTotal",
          { amount },
        );
  }
  const unknown = total.unknownPriceRows > 0
    ? shoppingUnknownPriceHint(total.unknownPriceRows)
    : "";
  const accessible = unknown ? `${label}. ${unknown}` : label;
  if (savings?.totalRows) {
    const totalLabel = !total.complete && total.knownPriceRows > 0
      ? t("shoppingKnownTotalLabel")
      : t(scope === "overall" ? "shoppingOverallTotalLabel" : "shoppingTotalLabel");
    const notes = [
      savings.unknownRows ? t("shoppingSavingsKnownOnly") : "",
      scope === "overall" ? t("shoppingSavingsHelp") : "",
    ].filter(Boolean).join(" ");
    return `
      <div class="shopping-money-total shopping-${scope}-total shopping-money-tiles${total.complete ? "" : " is-partial"}"
        role="group" aria-label="${escapeHtml(accessible)}" data-total-complete="${String(total.complete)}">
        ${scope === "overall" ? `<h3 class="shopping-overall-heading">${escapeHtml(t("shoppingAllStoresTotalHeading"))}</h3>` : ""}
        <div class="shopping-money-pair">
          <div class="shopping-money-stat shopping-payment">
            <span class="shopping-total-label">${escapeHtml(totalLabel)}</span>
            ${total.knownPriceRows
              ? `<strong class="shopping-tile-amount">${shoppingTotalAmountMarkup(total.totalBani)}</strong>`
              : `<strong class="shopping-total-unavailable">${escapeHtml(t("shoppingTotalUnavailableLabel"))}</strong>`}
          </div>
          <div class="shopping-money-stat shopping-savings" data-savings-bani="${savings.savingsBani}">
            <span class="shopping-total-label">${escapeHtml(t("shoppingSavings"))}</span>
            <b class="shopping-tile-amount">${shoppingTotalAmountMarkup(savings.savingsBani)}</b>
          </div>
        </div>
        ${unknown ? `<small class="shopping-payment-note">${escapeHtml(unknown)}</small>` : ""}
        ${notes ? `<small class="shopping-savings-help">${escapeHtml(notes)}</small>` : ""}
      </div>`;
  }
  return `
    <div
      class="shopping-money-total shopping-${scope}-total${total.complete ? "" : " is-partial"}"
      role="group"
      aria-label="${escapeHtml(accessible)}"
      data-total-complete="${String(total.complete)}"
    >
      <span aria-hidden="true">${scope === "overall" ? "💶" : "🧾"}</span>
      <strong>${escapeHtml(label)}</strong>
      ${unknown ? `<small>${escapeHtml(unknown)}</small>` : ""}
    </div>
  `;
}

function shoppingListCardMarkup(
  item: ShoppingListItem,
  today: string,
  shared = false,
): string {
  const expired = shoppingOfferExpired(item, today);
  const title = localizedShoppingTitle(item);
  const subtitle = secondaryShoppingTitle(item);
  const currentProduct =
    !shared && !expired
      ? products.find((product) => productKey(product) === item.key)
      : undefined;
  const price =
    item.newPriceMdl === null
      ? `<span class="shopping-list-no-price">${escapeHtml(t("priceInStore"))}</span>`
      : `<strong>${escapeHtml(formatPrice(item.newPriceMdl, appLocale))}</strong>`;
  const oldPrice =
    item.oldPriceMdl === null
      ? ""
      : `<s>${escapeHtml(formatPrice(item.oldPriceMdl, appLocale))}</s>`;
  const weight = item.weightOrVolume
    ? formatWeightOrVolume(item.weightOrVolume, appLocale)
    : "";
  const image = expired
    ? '<div class="shopping-list-image is-missing" aria-hidden="true"></div>'
    : currentProduct
      ? `<button
          class="shopping-list-image is-openable"
          type="button"
          data-shopping-open-product="${escapeHtml(item.key)}"
          aria-label="${escapeHtml(t("openProductCard", { title }))}"
          title="${escapeHtml(t("openProductCard", { title }))}"
        ><img src="${escapeHtml(appAssetUrl(currentProduct.image))}" alt="" loading="lazy" decoding="async" /></button>`
      : `<div class="shopping-list-image"><img src="${escapeHtml(appAssetUrl(item.image))}" alt="" loading="lazy" decoding="async" /></div>`;
  const sharedQuantity = shared
    ? `<span class="shared-list-quantity">× ${escapeHtml(item.quantity)}</span>`
    : "";
  const actions = shared
    ? ""
    : `
      <div class="shopping-list-actions">
        <div class="shopping-quantity" aria-label="${escapeHtml(t("quantityAria"))}">
          <button
            type="button"
            data-shopping-action="decrease"
            data-shopping-key="${escapeHtml(item.key)}"
            aria-label="${escapeHtml(t("decreaseQuantity", { title }))}"
            ${item.quantity <= 1 ? "disabled" : ""}
          >−</button>
          <span aria-label="${escapeHtml(t("quantityValue", { quantity: item.quantity }))}">${escapeHtml(item.quantity)}</span>
          <button
            type="button"
            data-shopping-action="increase"
            data-shopping-key="${escapeHtml(item.key)}"
            aria-label="${escapeHtml(t("increaseQuantity", { title }))}"
            ${item.quantity >= 99 ? "disabled" : ""}
          >+</button>
        </div>
        <button
          class="shopping-remove"
          type="button"
          data-shopping-action="remove"
          data-shopping-key="${escapeHtml(item.key)}"
          aria-label="${escapeHtml(t("removeProductFromList", { title }))}"
          title="${escapeHtml(t("remove"))}"
        >×</button>
      </div>
    `;
  return `
    <article
      class="shopping-list-item${shared ? " is-shared" : ""}${expired ? " is-expired" : ""}"
      ${shared ? "" : `data-shopping-row="${escapeHtml(item.key)}"`}
    >
      ${image}
      <div class="shopping-list-item-copy">
        <h3>${escapeHtml(title)}</h3>
        ${subtitle ? `<p class="shopping-list-original">${escapeHtml(subtitle)}</p>` : ""}
        <div class="shopping-list-price">
          ${price}
          ${oldPrice}
          ${weight ? `<span>${escapeHtml(weight)}</span>` : ""}
          ${sharedQuantity}
        </div>
        ${shoppingUnitPriceMarkup(item)}
        ${requiredCardMarkup(currentProduct?.required_card ?? item.requiredCard)}
        ${actions}
      </div>
    </article>
  `;
}

function shoppingStoreGroupMarkup(
  group: ShoppingStoreGroup,
  today: string,
  shared: boolean,
): string {
  const expired = group.catalogs.every((catalog) => catalog.expired);
  const catalogs = group.catalogs
    .map(
      (catalog) => `
        <div class="shopping-catalog-group">
          ${shoppingCatalogHeadingMarkup(group, catalog)}
          <div class="shopping-catalog-items">
            ${catalog.items
              .map((item) => shoppingListCardMarkup(item, today, shared))
              .join("")}
          </div>
        </div>
      `,
    )
    .join("");
  const storeItems = group.catalogs.flatMap((catalog) => catalog.items);
  const storeTotal = expired
    ? null
    : calculateShoppingMoneySummary(storeItems, today).stores[0] ?? null;
  return `
    <section
      class="shopping-store-group${expired ? " is-expired" : ""}"
      aria-label="${escapeHtml(group.store)}"
    >
      <div class="shopping-store-heading">
        <span aria-hidden="true">🏪</span>
        <strong>${escapeHtml(group.store)}</strong>
        ${shoppingSharingEnabled && !shared && !expired ? `
          <button type="button" class="shopping-store-share" data-share-store="${escapeHtml(group.store)}"
            aria-label="${escapeHtml(t("shareStoreListAria", { store: group.store }))}"
            title="${escapeHtml(t("shareStoreListAria", { store: group.store }))}"
            ${shareInProgress ? "disabled" : ""}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 16V3m-5 5 5-5 5 5M5 14v6h14v-6"/></svg>
            <span><b>${escapeHtml(t("shareStoreList"))}</b><small>${escapeHtml(t("shareStoreListOnly"))}</small></span>
          </button>` : ""}
      </div>
      <div class="shopping-store-catalogs">${catalogs}</div>
      ${storeTotal ? shoppingMoneyTotalMarkup(storeTotal, "store",
        SHOPPING_SAVINGS_ENABLED && !shared ? calculateShoppingSavings(storeItems, today) : undefined) : ""}
    </section>
  `;
}

function shoppingGroupedCardsMarkup(
  items: ShoppingListItem[],
  today: string,
  shared = false,
): string {
  return shoppingItemsByStoreAndCatalog(items, today)
    .map((group) => shoppingStoreGroupMarkup(group, today, shared))
    .join("");
}

function shoppingListCardsMarkup(
  items: ShoppingListItem[],
  shared = false,
  expiredControls = "",
): string {
  const today = currentShoppingDateKey();
  const sections = shoppingItemsByLifecycle(items, today);
  const overall = calculateShoppingMoneySummary(items, today).overall;
  return [
    shoppingGroupedCardsMarkup(sections.active, today, shared),
    shoppingMoneyTotalMarkup(overall, "overall",
      SHOPPING_SAVINGS_ENABLED && !shared ? calculateShoppingSavings(items, today) : undefined),
    ...(sections.expired.length
      ? [
          shared ? "" : expiredControls,
          expiredShoppingHeadingMarkup(),
          shoppingGroupedCardsMarkup(sections.expired, today, shared),
        ]
      : []),
  ].join("");
}

function shoppingListMarkup(): string {
  if (shoppingItems.length === 0) {
    const help = escapeHtml(t("shoppingEmptyHelp")).replace(
      "+",
      '<span class="shopping-guide-icon" aria-hidden="true">+</span>',
    );
    return `
      <div class="shopping-list-empty">
        <span class="shopping-list-empty-icon" aria-hidden="true">+</span>
        <h3>${escapeHtml(t("shoppingEmptyTitle"))}</h3>
        <p>${help}</p>
      </div>
    `;
  }
  const expiredCount = shoppingItemsByLifecycle(shoppingItems, currentShoppingDateKey()).expired.length;
  if (!expiredCount) confirmExpiredShoppingRemoval = false;
  const cleanup = !expiredCount ? "" : `
    <div class="shopping-expired-cleanup">
      ${confirmExpiredShoppingRemoval ? `
        <p id="shopping-expired-confirm">${escapeHtml(t("clearExpiredShoppingConfirm", { count: expiredCount }))}</p>
        <div class="shopping-expired-confirm-actions" role="group" aria-labelledby="shopping-expired-confirm">
          <button type="button" data-expired-shopping="cancel">${escapeHtml(t("clearExpiredShoppingCancel"))}</button>
          <button type="button" class="shopping-expired-remove" data-expired-shopping="confirm">${escapeHtml(t("remove"))}</button>
        </div>` : `
        <button type="button" class="shopping-expired-trigger" data-expired-shopping="request">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 10v7m4-7v7"/></svg>
          <span>${escapeHtml(t("clearExpiredShopping", { count: expiredCount }))}</span>
        </button>`}
    </div>`;
  return shoppingListCardsMarkup(shoppingItems, false, cleanup);
}

function renderShoppingList(): void {
  shoppingListContent.innerHTML = shoppingListMarkup();
  bindShoppingImageFallbacks(shoppingListContent);
  select<HTMLElement>("#shopping-cleanup-status").textContent = expiredShoppingRemovedCount
    ? t("clearExpiredShoppingDone", { count: expiredShoppingRemovedCount }) : "";
  updateShoppingShareControls();
}

function initializeShoppingSharing(): void {
  const enabled = shoppingSharingAvailable(__KILOPRICE_WEBSITE__);
  shoppingSharingEnabled = enabled;
  shoppingSharePhotosOption.hidden = !enabled;
  // Initialize once, not on sync/render/dialog reopen: preserve an explicit opt-out.
  shoppingSharePhotos.checked = enabled;
  shoppingListDialog.classList.toggle("has-shopping-sharing", enabled);
  // Match DOM/tab order to the visual footer, not just CSS flex order.
  shoppingListDialog.insertBefore(shoppingSharePanel, enabled
    ? select<HTMLElement>("#shopping-cleanup-status") : shoppingListContent);
  shoppingShareStatus.textContent = "";
  syncShoppingViewport();
}

function syncShoppingViewport(): void {
  if (!shoppingSharingEnabled) return;
  const viewport = window.visualViewport;
  const height = Math.min(viewport?.height ?? window.innerHeight, window.innerHeight);
  const top = Math.min(Math.max(0, viewport?.offsetTop ?? 0), Math.max(0, window.innerHeight - height));
  shoppingListDialog.style.setProperty("--shopping-viewport-height", `${height}px`);
  shoppingListDialog.style.setProperty("--shopping-viewport-top", `${top}px`);
  shoppingListDialog.style.setProperty("--shopping-viewport-bottom", `${Math.max(0, window.innerHeight - top - height)}px`);
}

function updateShoppingShareControls(): void {
  const active = shoppingSharingEnabled
    ? scopedShoppingShareItems(shoppingItems, dateKeyInMoldova(currentReferenceTime()), { kind: "active" })
    : shoppingItems;
  const label = shoppingSharingEnabled ? "shareEntireList" : "shareList";
  shoppingShareLabel.dataset.i18n = label;
  shoppingShareLabel.textContent = shareInProgress && sharingStore === undefined ? t("preparingMessage") : t(label);
  shoppingShareButton.disabled = shareInProgress || active.length === 0;
  shoppingSharePhotos.disabled = shareInProgress || active.length === 0;
  shoppingShareButton.setAttribute("aria-busy", String(shareInProgress && sharingStore === undefined));
  for (const button of shoppingListContent.querySelectorAll<HTMLButtonElement>("[data-share-store]")) {
    button.disabled = shareInProgress || !active.some((item) => item.store === button.dataset.shareStore);
    button.setAttribute("aria-busy", String(shareInProgress && sharingStore === button.dataset.shareStore));
  }
  if (shoppingSharingEnabled && (!shoppingShareStatus.textContent || shoppingShareStatus.textContent === shoppingShareStatus.dataset.hint)) {
    shoppingShareStatus.textContent = active.length ? "" : t("shareNoActiveItems");
    shoppingShareStatus.dataset.hint = shoppingShareStatus.textContent;
  }
}

function updateShoppingUi(): void {
  updateCatalogShoppingButton();
  refreshMealPilot();
  const empty = shoppingItems.length === 0;
  const quantity = shoppingQuantityTotal(shoppingItems);
  shoppingListButton.classList.toggle("is-empty", empty);
  shoppingListButton.setAttribute(
    "aria-label",
    empty
      ? t("shoppingListEmptyAria")
      : t("shoppingListCountAria", { count: quantity }),
  );
  shoppingListCount.hidden = empty;
  shoppingListCount.textContent = quantity > 99 ? "99+" : String(quantity);
  shoppingSharePanel.hidden = empty;
  if (empty) {
    shoppingShareStatus.textContent = "";
  }
  updateShoppingShareControls();
  updateShoppingCardButtons();
  updateDialogShoppingButton();
  if (catalogViewerDialog.open && activeCatalogViewerCatalog) {
    updateCatalogShoppingControls();
  }
  if (catalogProductChoiceDialog.open && activeCatalogProductGroup.length) {
    updateCatalogProductChoices();
  }
  if (shoppingListDialog.open) {
    renderShoppingList();
  }
}

function openShoppingList(): void {
  syncShoppingViewport();
  if (!shareInProgress) shoppingShareStatus.textContent = "";
  if (catalogInteractionsEnabled && catalogViewerDialog.open) {
    shoppingListFromCatalog = true;
    catalogShoppingButton.setAttribute("aria-expanded", "true");
    // An existing list may be underneath a product/catalogue. Promote it,
    // preserving its scroll; the queued close handler ignores reopened dialogs.
    if (shoppingListDialog.open) {
      const top = shoppingListDialog.scrollTop;
      shoppingListDialog.close();
      shoppingListDialog.showModal();
      shoppingListDialog.scrollTop = top;
      return;
    }
  }
  if (shoppingListDialog.open) {
    return;
  }
  renderShoppingList();
  shoppingListButton.setAttribute("aria-expanded", "true");
  shoppingListDialog.showModal();
}

function closeShoppingList(): void {
  if (shoppingListDialog.open) {
    shoppingListDialog.close();
  }
}

function sharedShoppingListMarkup(items: ShoppingListItem[]): string {
  return shoppingListCardsMarkup(items, true);
}

function sharedListErrorMessage(error: unknown): string {
  if (error instanceof SharedListApiError) {
    if (error.code === "shared_list_expired") {
      return t("sharedListExpired");
    }
    if (error.code === "shared_list_not_found") {
      return t("sharedListNotFound");
    }
  }
  return t("sharedListOpenFailed");
}

function renderLoadedSharedShoppingList(shared: SharedShoppingList): void {
  const expires = new Intl.DateTimeFormat(localeTag(appLocale), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(shared.expiresAt));
  sharedListMeta.textContent = t("sharedListMeta", {
    count: formatUiNumber(shared.items.length),
    word: productWord(shared.items.length),
    date: expires,
  });
  sharedListContent.innerHTML = sharedShoppingListMarkup(shared.items);
  bindShoppingImageFallbacks(sharedListContent);
  sharedListFooter.hidden = false;
}

async function openSharedShoppingList(token: string): Promise<void> {
  activeSharedList = null;
  activeSharedListItem = null;
  sharedListMeta.textContent = t("loadingSharedList");
  sharedListContent.innerHTML = '<div class="shared-list-loading"><span class="loader"></span></div>';
  sharedListFooter.hidden = true;
  sharedListStatus.textContent = "";
  sharedListImport.disabled = false;
  sharedListImport.textContent = t("addToMyList");
  if (!sharedListDialog.open) {
    sharedListDialog.showModal();
  }
  try {
    const shared = await loadSharedShoppingList(token);
    activeSharedList = shared;
    renderLoadedSharedShoppingList(shared);
  } catch (error) {
    sharedListMeta.textContent = t("sharedListUnavailable");
    sharedListContent.innerHTML = `<div class="shopping-list-empty"><span class="shopping-list-empty-icon" aria-hidden="true">!</span><h3>${escapeHtml(sharedListErrorMessage(error))}</h3><p>${escapeHtml(t("catalogsContinueWorking"))}</p></div>`;
  }
}

function renderSharedShoppingListItem(item: ShoppingListItem): void {
  const expired = shoppingOfferExpired(item, currentShoppingDateKey());
  const status = expired
    ? t("sharedListItemExpired", { date: formatShoppingDate(item.validUntil) })
    : t("sharedListItemSnapshotHelp");
  sharedListMeta.textContent = t("sharedListItemSnapshotTitle");
  sharedListContent.innerHTML = `
    <section class="shared-list-item-notice" aria-label="${escapeHtml(status)}">
      <strong>${escapeHtml(t("sharedListItemSnapshotTitle"))}</strong>
      <p>${escapeHtml(status)}</p>
    </section>
    <div class="shared-list-item-snapshot${expired ? " is-expired" : " is-unverified"}">
      ${shoppingListCardMarkup(item, currentShoppingDateKey(), true)}
    </div>
    <button class="shared-list-item-search" type="button" data-shared-item-search>
      🔎 ${escapeHtml(t("findCurrentOffers"))}
    </button>
  `;
  bindShoppingImageFallbacks(sharedListContent);
  sharedListFooter.hidden = true;
}

function renderSharedListItemError(message: string): void {
  sharedListMeta.textContent = t("sharedListUnavailable");
  sharedListContent.innerHTML = `<div class="shopping-list-empty"><span class="shopping-list-empty-icon" aria-hidden="true">!</span><h3>${escapeHtml(message)}</h3><p>${escapeHtml(t("catalogsContinueWorking"))}</p></div>`;
  sharedListFooter.hidden = true;
}

async function openSharedShoppingListItem(
  route: SharedListItemStartParameter,
): Promise<void> {
  activeSharedList = null;
  activeSharedListItem = null;
  sharedListMeta.textContent = t("loadingSharedList");
  sharedListContent.innerHTML = '<div class="shared-list-loading"><span class="loader"></span></div>';
  sharedListFooter.hidden = true;
  if (!sharedListDialog.open) {
    sharedListDialog.showModal();
  }
  try {
    const shared = await loadSharedShoppingList(route.token);
    const snapshotItem = shared.items[route.index];
    const liveProduct = snapshotItem
      ? products.find((candidate) => productKey(candidate) === snapshotItem.key)
      : undefined;
    const landing = resolveSharedListItemLanding(
      shared.items,
      route.index,
      currentShoppingDateKey(),
      liveProduct
        ? {
            key: productKey(liveProduct),
            store: liveProduct.catalog.chain,
            validUntil: liveProduct.catalog.valid_until,
          }
        : null,
    );
    if (landing.kind === "invalid") {
      renderSharedListItemError(t("sharedListItemNotFound"));
      return;
    }
    if (landing.kind === "live" && liveProduct) {
      sharedListDialog.close();
      openProduct(liveProduct);
      return;
    }
    activeSharedListItem = landing.item;
    renderSharedShoppingListItem(landing.item);
  } catch (error) {
    renderSharedListItemError(sharedListErrorMessage(error));
  }
}

function findCurrentOffersForSharedItem(): void {
  const item = activeSharedListItem;
  if (!item) {
    return;
  }
  const query = localizedShoppingTitle(item);
  activeSharedListItem = null;
  sharedListDialog.close();
  filters.store = "all";
  filters.catalogId = "all";
  filters.categoryId = "all";
  filters.query = query;
  searchInput.value = query;
  updateSearchClearButton();
  visibleCount = PAGE_SIZE;
  resetAndRender();
  queueActivity("searches");
  if (filterProducts(products, filters).length === 0) {
    queueActivity("zeroResultSearches");
  }
  window.requestAnimationFrame(() => {
    searchInput.scrollIntoView({ block: "center", behavior: "smooth" });
    searchInput.focus({ preventScroll: true });
  });
}

function shoppingShareErrorMessage(error: unknown): string {
  if (error instanceof SharedListApiError) {
    if (error.code === "no_active_items") return t("shareNoActiveItems");
    if (error.code === "pilot_unavailable") return t("telegramSessionExpired");
    if (error.code === "unauthorized") {
      return t("telegramSessionExpired");
    }
    if (error.code === "rate_limited") {
      return t("tooManyShares");
    }
    if (error.code === "telegram_api_failed") {
      return t("telegramMessageFailed");
    }
  }
  return t("listShareFailed");
}

async function shareLinkFallback(
  shareUrl: string,
  title: string,
  text: string,
  copyText = false,
): Promise<"shared" | "copied" | "cancelled" | "opened"> {
  if (openTelegramShareLink(telegram, shareUrl, text)) return "opened";
  if (navigator.share) {
    try {
      await navigator.share({
        title,
        text,
        url: shareUrl,
      });
      return "shared";
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return "cancelled";
      }
    }
  }
  await navigator.clipboard.writeText(copyText ? `${text}\n\n${shareUrl}` : shareUrl);
  return "copied";
}

function appShareErrorMessage(error: unknown): string {
  if (error instanceof AppShareApiError) {
    if (error.code === "unauthorized") {
      return t("telegramSessionExpired");
    }
    if (error.code === "telegram_api_failed") {
      return t("telegramMessageFailed");
    }
  }
  return t("appShareFailed");
}

async function shareApplication(): Promise<void> {
  if (appShareInProgress) {
    return;
  }
  appShareInProgress = true;
  appShareButton.disabled = true;
  appShareButton.classList.add("is-loading");
  appShareButton.setAttribute("aria-busy", "true");
  try {
    const initData = telegram.webApp?.initData;
    if (!initData) {
      const fallback = await shareLinkFallback(
        __KILOPRICE_WEBSITE__
          ? websiteHomeUrl(appLocale)
          : `https://t.me/KiloPrice_bot?startapp=${APP_INVITE_START_PARAMETER}`,
        t("shareApp"),
        t("appInviteText"),
      );
      if (fallback === "shared") {
        queueActivity("appShares");
      }
      return;
    }
    const prepared = await prepareAppShare(initData, appLocale);
    const sent = await sharePreparedTelegramMessage(
      telegram,
      prepared.preparedMessageId,
    );
    if (sent === true) {
      queueActivity("appShares");
    } else if (sent === null) {
      const fallback = await shareLinkFallback(
        prepared.shareUrl,
        t("shareApp"),
        t("appInviteText"),
      );
      if (fallback === "shared") {
        queueActivity("appShares");
      }
    }
  } catch (error) {
    window.alert(appShareErrorMessage(error));
  } finally {
    appShareInProgress = false;
    appShareButton.disabled = false;
    appShareButton.classList.remove("is-loading");
    appShareButton.removeAttribute("aria-busy");
  }
}

async function shareShoppingList(store?: string): Promise<void> {
  if (shareInProgress || shoppingItems.length === 0) {
    return;
  }
  if (store !== undefined && !shoppingSharingEnabled) return;
  const scope = shoppingSharingEnabled ? { kind: "active" as const, ...(store !== undefined ? { store } : {}) } : undefined;
  const withPhotos = shoppingSharingEnabled && shoppingSharePhotos.checked;
  // Fresh Moldova date, not a possibly stale rendered list at midnight.
  const items = scope ? scopedShoppingShareItems(shoppingItems, dateKeyInMoldova(currentReferenceTime()), scope) : [...shoppingItems];
  if (!items.length) {
    shoppingShareStatus.textContent = t("shareNoActiveItems");
    updateShoppingShareControls();
    return;
  }
  const initData = telegram.webApp?.initData;
  if (!initData && !__KILOPRICE_WEBSITE__) {
    shoppingShareStatus.textContent = t("sharingTelegramOnly");
    return;
  }
  shareInProgress = true;
  sharingStore = store;
  shoppingShareStatus.textContent = withPhotos ? t("preparingShoppingPhotos") : store === undefined ? "" : t("preparingStoreList", { store });
  updateShoppingShareControls();
  try {
    if (!initData) {
      if (__KILOPRICE_WEBSITE__) {
        const { prepareWebsiteShoppingListShare } = await import(
          "./website-sharing"
        );
        const prepared = await prepareWebsiteShoppingListShare(
          items,
          appLocale,
        );
        const fallback = await shareLinkFallback(
          prepared.shareUrl,
          t("shareListTitle"),
          t("openSharedList"),
        );
        shoppingShareStatus.textContent =
          fallback === "shared"
            ? t("linkSent")
            : fallback === "copied"
              ? t("linkCopied")
              : t("shareCancelled");
        return;
      }
      throw new SharedListApiError("unauthorized", 401);
    }
    const prepared = await prepareShoppingListShare(
      initData,
      items,
      appLocale,
      scope,
      withPhotos,
    );
    if (prepared.photoPreviewLeft) window.alert(t("sharePhotoPreviewLeft"));
    if (withPhotos && !prepared.photosAttached && !window.confirm(t("sharePhotosUnavailable"))) {
      shoppingShareStatus.textContent = t("shareCancelled");
      return;
    }
    const sent = await sharePreparedTelegramMessage(
      telegram,
      prepared.preparedMessageId,
    );
    if (sent === true) {
      queueActivity("shoppingListShares");
      shoppingShareStatus.textContent = prepared.photosAttached ? t("sharePhotosSent", { count: prepared.photosAttached }) : store === undefined ? t("listSent") : t("storeListSent", { store });
    } else if (sent === false) {
      shoppingShareStatus.textContent = t("shareCancelled");
    } else if (sent === "unknown") {
      shoppingShareStatus.textContent = t("shareUnconfirmed");
    } else {
      if (prepared.photosAttached && !window.confirm(t("sharePhotosLinkOnly"))) {
        shoppingShareStatus.textContent = t("shareCancelled");
        return;
      }
      const fallback = await shareLinkFallback(
        prepared.shareUrl,
        t("shareListTitle"),
        prepared.fallbackText ?? t("openSharedList"),
        Boolean(prepared.fallbackText),
      );
      shoppingShareStatus.textContent =
        fallback === "shared"
          ? t("linkSent")
          : fallback === "copied"
            ? t("linkCopied")
            : fallback === "opened" ? t("shareChooseChat") : t("shareCancelled");
      if (fallback === "shared") {
        queueActivity("shoppingListShares");
      }
    }
  } catch (error) {
    shoppingShareStatus.textContent = shoppingShareErrorMessage(error);
  } finally {
    shareInProgress = false;
    sharingStore = undefined;
    updateShoppingShareControls();
  }
}

function productShareErrorMessage(error: unknown): string {
  if (error instanceof SharedProductApiError) {
    if (error.code === "unauthorized") {
      return t("telegramSessionExpired");
    }
    if (error.code === "rate_limited") {
      return t("tooManyShares");
    }
    if (error.code === "telegram_api_failed") {
      return t("telegramProductFailed");
    }
  }
  return t("productShareFailed");
}

async function shareProduct(): Promise<void> {
  const product = activeDialogProduct;
  if (productShareInProgress || !product) {
    return;
  }
  const initData = telegram.webApp?.initData;
  if (!initData && !__KILOPRICE_WEBSITE__) {
    dialogShareStatus.textContent = t("sharingTelegramOnly");
    return;
  }
  productShareInProgress = true;
  dialogShareButton.disabled = true;
  dialogShareLabel.textContent = t("preparing");
  dialogShareStatus.textContent = "";
  try {
    if (!initData) {
      if (__KILOPRICE_WEBSITE__) {
        const { prepareWebsiteProductShare } = await import(
          "./website-sharing"
        );
        const title = localizedProductTitle(product);
        const prepared = await prepareWebsiteProductShare(
          createSharedProductSnapshot(product),
          appLocale,
        );
        const fallback = await shareLinkFallback(
          prepared.shareUrl,
          `${title} · KiloPrice`,
          t("openProduct"),
        );
        dialogShareStatus.textContent =
          fallback === "shared"
            ? t("linkSent")
            : fallback === "copied"
              ? t("linkCopied")
              : t("shareCancelled");
        return;
      }
      throw new SharedProductApiError("unauthorized", 401);
    }
    const prepared = await prepareProductShare(
      initData,
      createSharedProductSnapshot(product),
      appLocale,
    );
    const sent = await sharePreparedTelegramMessage(
      telegram,
      prepared.preparedMessageId,
    );
    if (sent === true) {
      queueActivity("productShares");
      dialogShareStatus.textContent = t("productSent");
    } else if (sent === false) {
      dialogShareStatus.textContent = t("shareCancelled");
    } else if (sent === "unknown") {
      dialogShareStatus.textContent = t("shareUnconfirmed");
    } else {
      const title = localizedProductTitle(product);
      const fallback = await shareLinkFallback(
        prepared.shareUrl,
        `${title} · KiloPrice`,
        t("openProduct"),
      );
      dialogShareStatus.textContent =
        fallback === "shared"
          ? t("linkSent")
          : fallback === "copied"
            ? t("linkCopied")
            : fallback === "opened" ? t("shareChooseChat") : t("shareCancelled");
      if (fallback === "shared") {
        queueActivity("productShares");
      }
    }
  } catch (error) {
    dialogShareStatus.textContent = productShareErrorMessage(error);
  } finally {
    productShareInProgress = false;
    dialogShareButton.disabled = false;
    dialogShareLabel.textContent = t("share");
  }
}

function catalogShareErrorMessage(error: unknown): string {
  if (error instanceof SharedCatalogApiError) {
    if (error.code === "unauthorized") {
      return t("telegramSessionExpired");
    }
    if (error.code === "telegram_api_failed") {
      return t("telegramMessageFailed");
    }
  }
  return t("catalogShareFailed");
}

async function shareCatalog(): Promise<void> {
  const catalog = activeCatalogViewerCatalog;
  const page = activeCatalogViewerPage;
  if (catalogShareInProgress || !catalog) {
    return;
  }
  catalogShareInProgress = true;
  catalogViewerShare.disabled = true;
  catalogViewerShare.setAttribute("aria-busy", "true");
  catalogViewerShareLabel.textContent = t("preparing");
  const fallbackUrl = sharedCatalogTelegramUrl(catalog.id, page);
  try {
    const initData = telegram.webApp?.initData;
    if (!initData) {
      const fallback = await shareLinkFallback(
        fallbackUrl,
        t("catalogShareTitle", { store: catalog.chain, title: catalog.title }),
        t("catalogShareFallback", {
          store: catalog.chain,
          title: catalog.title,
          page: formatUiNumber(page),
        }),
      );
      if (fallback === "shared") {
        queueActivity("catalogShares");
      }
      return;
    }
    const prepared = await prepareCatalogShare(
      initData,
      createSharedCatalogSnapshot(catalog, page),
      appLocale,
    );
    const sent = await sharePreparedTelegramMessage(
      telegram,
      prepared.preparedMessageId,
    );
    if (sent === true) {
      queueActivity("catalogShares");
    } else if (sent === null) {
      const fallback = await shareLinkFallback(
        prepared.shareUrl,
        t("catalogShareTitle", { store: catalog.chain, title: catalog.title }),
        t("catalogShareFallback", {
          store: catalog.chain,
          title: catalog.title,
          page: formatUiNumber(page),
        }),
      );
      if (fallback === "shared") {
        queueActivity("catalogShares");
      }
    }
  } catch (error) {
    window.alert(catalogShareErrorMessage(error));
  } finally {
    catalogShareInProgress = false;
    catalogViewerShare.disabled = false;
    catalogViewerShare.removeAttribute("aria-busy");
    catalogViewerShareLabel.textContent = t("catalogShare");
  }
}

function sharedProductErrorMessage(error: unknown): string {
  if (error instanceof SharedProductApiError) {
    if (error.code === "shared_product_expired") {
      return t("sharedProductExpired");
    }
    if (error.code === "shared_product_not_found") {
      return t("sharedProductNotFound");
    }
  }
  return t("sharedProductOpenFailed");
}

async function openSharedProduct(token: string): Promise<void> {
  activeDialogProduct = null;
  dialogImage.hidden = true;
  dialogActions.hidden = true;
  dialogStore.textContent = t("sharedWithYou");
  dialogTitle.textContent = t("loadingProduct");
  dialogMeta.innerHTML = '<div class="shared-list-loading"><span class="loader"></span></div>';
  dialogShareStatus.textContent = "";
  showProductDialog();
  try {
    const shared = await loadSharedProduct(token);
    openProduct(productViewFromSharedSnapshot(shared.product));
  } catch (error) {
    dialogStore.textContent = t("sharedProduct");
    dialogTitle.textContent = t("productUnavailable");
    dialogMeta.innerHTML = `<div class="shopping-list-empty"><span class="shopping-list-empty-icon" aria-hidden="true">!</span><h3>${escapeHtml(sharedProductErrorMessage(error))}</h3><p>${escapeHtml(t("catalogsContinueWorking"))}</p></div>`;
  }
}

function importSharedShoppingList(): void {
  if (!activeSharedList || sharedListImport.disabled) {
    return;
  }
  const existingKeys = new Set(shoppingItems.map((item) => item.key));
  const added = activeSharedList.items.filter(
    (item) => !existingKeys.has(item.key),
  ).length;
  if (added > 0) {
    queueActivity("shoppingAdds", added);
  }
  persistShoppingList(mergeShoppingLists(shoppingItems, activeSharedList.items));
  sharedListImport.disabled = true;
  sharedListImport.textContent = t("addedToMyList");
  sharedListStatus.textContent = t("productsAdded");
}

function openSharedContentFromStartParameter(): boolean {
  const startParameter = telegramStartParameter(telegram);
  const listItem = parseSharedListItemStartParameter(startParameter);
  if (listItem) {
    void openSharedShoppingListItem(listItem);
    return true;
  }
  const listToken =
    websiteRouteContext?.sharedListToken ??
    parseSharedListStartParameter(startParameter);
  if (listToken) {
    void openSharedShoppingList(listToken);
    return true;
  }
  const productToken =
    websiteRouteContext?.sharedProductToken ??
    parseSharedProductStartParameter(startParameter);
  if (productToken) {
    void openSharedProduct(productToken);
    return true;
  }
  const sharedCatalog = parseSharedCatalogStartParameter(startParameter);
  if (sharedCatalog) {
    const catalog = dataset?.catalogs.find(
      (candidate) => candidate.id === sharedCatalog.catalogId,
    );
    if (catalog && catalogPageImage(catalog, sharedCatalog.page)) {
      openCatalogViewer(catalog, sharedCatalog.page);
    } else {
      openCatalogLibrary();
      window.setTimeout(() => window.alert(t("sharedCatalogUnavailable")), 0);
    }
    return true;
  }
  const directProduct = parseDirectProductStartParameter(startParameter);
  if (directProduct) {
    const product = products.find(
      (candidate) =>
        candidate.catalog_id === directProduct.catalogId &&
        candidate.id === directProduct.productId,
    );
    if (product) {
      openProduct(product);
    } else {
      window.setTimeout(() => window.alert(t("productUnavailable")), 0);
    }
    return true;
  }
  return false;
}

function openWebsiteProductRoute(): void {
  const catalogId = websiteRouteContext?.catalogId;
  const productId = websiteRouteContext?.productId;
  if (!__KILOPRICE_WEBSITE__ || !catalogId || !productId) {
    return;
  }
  const product = products.find(
    (candidate) =>
      candidate.catalog_id === catalogId && candidate.id === productId
      && websiteOfferActive(candidate, candidate.catalog, currentShoppingDateKey()),
  );
  if (product) {
    openProduct(product);
  }
}

function productCard(product: ProductView): string {
  const title = localizedProductTitle(product);
  const subtitle = secondaryProductTitle(product);
  const note = productDisplayNote(product, appLocale);
  const weight = product.weight_or_volume
    ? `<span class="weight">${escapeHtml(formatWeightOrVolume(product.weight_or_volume, appLocale))}</span>`
    : "";
  const compactExpiryDate = formatCompactCatalogDate(
    product.catalog.valid_until,
  );
  const expiryIsSoon = catalogExpiresTodayOrTomorrow(
    product.catalog.valid_until,
    currentShoppingDateKey(),
  );
  return `
    <article class="product-card" data-card-key="${escapeHtml(productKey(product))}">
      <div class="product-image-wrap">
        <button class="product-image-button" type="button" aria-label="${escapeHtml(t("openProductCard", { title }))}">
          <img src="${escapeHtml(appAssetUrl(product.image))}" alt="${escapeHtml(title)}" loading="lazy" decoding="async" />
        </button>
        ${productQuantityControlMarkup(productQuantityView(product, "card"))}
        ${telegram.webApp?.initData ? productWatchControlMarkup(productWatchView(product)) : ""}
      </div>
      <div class="product-body">
        <div class="card-topline">
          <span class="store-badge">${escapeHtml(product.catalog.chain)}</span>
          <span class="card-validity">${escapeHtml(formatValidity(product.catalog.valid_from, product.catalog.valid_until, appLocale))} · ${escapeHtml(t("pageShort"))} ${escapeHtml(product.page)}</span>
          <span
            class="card-valid-until${expiryIsSoon ? " is-ending-soon" : ""}"
            aria-label="${escapeHtml(t("offerValidUntil", { date: compactExpiryDate }))}"
          >${escapeHtml(t("compactValidUntil", { date: compactExpiryDate }))}</span>
        </div>
        <h3>${__KILOPRICE_WEBSITE__ ? `<a href="${escapeHtml(websiteProductPath(product, product.catalog, appLocale))}">${escapeHtml(title)}</a>` : escapeHtml(title)}</h3>
        ${subtitle ? `<p class="original-name">${escapeHtml(subtitle)}</p>` : ""}
        <div class="product-facts">
          ${product.brand ? `<span>${escapeHtml(product.brand)}</span>` : ""}
          ${weight}
        </div>
        ${priceMarkup(product)}
        ${unitPriceMarkup(product)}
        ${note ? `<p class="product-note">${escapeHtml(note)}</p>` : ""}
      </div>
    </article>
  `;
}

function renderStoreChips(): void {
  if (!dataset) {
    return;
  }
  const countByStore = new Map<string, number>();
  const alcoholOnly = filters.categoryId === "alcohol";
  const displayProducts = products.filter(
    (product) =>
      alcoholOnly
        ? product.category_id === "alcohol"
        : product.category_id !== "alcohol",
  );
  for (const product of displayProducts) {
    const chain = product.catalog.chain;
    countByStore.set(chain, (countByStore.get(chain) ?? 0) + 1);
  }
  const chips = [
    `<button type="button" data-store="all" class="${filters.store === "all" ? "active" : ""}">${escapeHtml(t("all"))}</button>`,
    personalFilterControlMarkup(
      "stores",
      t("myStores"),
      personalPreferences.stores.length > 0,
      personalPreferences.stores.length,
      filters.store === PERSONAL_STORES_FILTER,
    ),
    ...storeFilterOptions(uniqueStores(dataset, appLocale)).map(
      ({ value: store, active }) =>
        active
          ? `<button type="button" data-store="${escapeHtml(store)}" class="${filters.store === store ? "active" : ""}">${escapeHtml(store)} <span>${countByStore.get(store) ?? 0}</span></button>`
          : `<button type="button" data-unavailable-store="${escapeHtml(store)}" class="store-unavailable" aria-label="${escapeHtml(t("storeCatalogUnavailableShort", { store }))}" title="${escapeHtml(t("storeCatalogUnavailableShort", { store }))}">${escapeHtml(store)}</button>`,
    ),
  ];
  storeChips.innerHTML = chips.join("");
  updateStoreButtonGlow();
}

function updateStoreButtonGlow(): void {
  const control = storeChips.querySelector<HTMLElement>(
    ".personal-filter-setup, .personal-filter-control",
  );
  if (!control) return;
  const view = storeButtonGlow.view(personalPreferences.stores);
  control.classList.toggle("stores-glow-cue", !!view);
  control.classList.toggle("stores-glow-moving", !!view?.animate);
  if (view) {
    control.style.setProperty("--stores-glow-delay", `-${view.elapsedMs}ms`);
  } else {
    control.style.removeProperty("--stores-glow-delay");
  }
}

function personalFilterControlMarkup(
  kind: PreferenceKind,
  label: string,
  configured: boolean,
  selectedCount: number,
  active: boolean,
): string {
  if (!configured) {
    return `<button type="button" class="personal-filter-setup" data-personal-config="${kind}" aria-haspopup="dialog" aria-controls="preference-dialog">+ ${label}</button>`;
  }
  return `
    <span class="personal-filter-control ${active ? "active" : ""}">
      <button type="button" class="personal-filter-apply" data-personal-apply="${kind}" aria-pressed="${String(active)}">${label}</button>
      <button type="button" class="personal-filter-edit" data-personal-config="${kind}" aria-haspopup="dialog" aria-controls="preference-dialog" aria-label="${escapeHtml(t("configuredSelection", { label: label.toLocaleLowerCase(localeTag(appLocale)), count: selectedCount }))}" title="${escapeHtml(t("configure"))}">
        <span class="personal-filter-selected-count" aria-hidden="true">${selectedCount}</span>
        <span class="personal-filter-edit-icon" aria-hidden="true">✎</span>
      </button>
    </span>
  `;
}

function renderCategoryControls(): void {
  if (!dataset?.categories?.length) {
    categoryControls.hidden = true;
    filters.categoryId = "all";
    return;
  }
  categoryControls.hidden = false;
  const counts = new Map<string, number>();
  for (const product of products) {
    if (product.category_id) {
      counts.set(product.category_id, (counts.get(product.category_id) ?? 0) + 1);
    }
  }
  const categories = [...dataset.categories]
    .filter((category) => (counts.get(category.id) ?? 0) > 0)
    .sort((left, right) => {
      if (left.id === "alcohol") {
        return 1;
      }
      if (right.id === "alcohol") {
        return -1;
      }
      return left.order - right.order;
    });
  if (
    filters.categoryId !== "all" &&
    filters.categoryId !== PERSONAL_CATEGORIES_FILTER &&
    !categories.some((category) => category.id === filters.categoryId)
  ) {
    filters.categoryId = "all";
  }
  categoryChips.innerHTML = [
    `<button type="button" data-category="all" class="${filters.categoryId === "all" ? "active" : ""}">${escapeHtml(t("allCategories"))}</button>`,
    personalFilterControlMarkup(
      "categories",
      t("myCategories"),
      personalPreferences.categoryIds.length > 0,
      personalPreferences.categoryIds.length,
      filters.categoryId === PERSONAL_CATEGORIES_FILTER,
    ),
    ...categories.map(
      (category) =>
        `<button type="button" data-category="${escapeHtml(category.id)}" class="${filters.categoryId === category.id ? "active" : ""}">${escapeHtml(localizedCategoryLabel(category))} <span>${counts.get(category.id) ?? 0}</span></button>`,
    ),
  ].join("");
}

function renderActiveFilterDock(): void {
  if (!dataset) {
    activeFilterDockTrack.replaceChildren();
    return;
  }
  const storeLabel =
    filters.store === "all"
      ? t("allStores")
      : filters.store === PERSONAL_STORES_FILTER
        ? t("myStores")
        : filters.store;
  const selectedCategory = dataset.categories?.find(
    (category) => category.id === filters.categoryId,
  );
  const categoryLabel =
    filters.categoryId === "all"
      ? t("allCategories")
      : filters.categoryId === PERSONAL_CATEGORIES_FILTER
        ? t("myCategories")
        : selectedCategory
          ? localizedCategoryLabel(selectedCategory)
          : t("allCategories");
  const chips = [
    activeFilterDockChipMarkup(
      storeLabel,
      filters.store === PERSONAL_STORES_FILTER ? "stores" : null,
      personalPreferences.stores.length,
    ),
    ...(categoryControls.hidden
      ? []
      : [
          activeFilterDockChipMarkup(
            categoryLabel,
            filters.categoryId === PERSONAL_CATEGORIES_FILTER
              ? "categories"
              : null,
            personalPreferences.categoryIds.length,
          ),
        ]),
  ];
  activeFilterDockTrack.innerHTML = chips.join("");
}

function activeFilterDockChipMarkup(
  label: string,
  preferenceKind: PreferenceKind | null,
  selectedCount: number,
): string {
  if (!preferenceKind) {
    return `<span class="active-filter-chip">${escapeHtml(label)}</span>`;
  }
  return `
    <span class="active-filter-chip active-filter-personal-chip">
      <span class="active-filter-personal-label">${escapeHtml(label)}</span>
      <button
        type="button"
        class="active-filter-personal-edit"
        data-personal-config="${preferenceKind}"
        aria-haspopup="dialog"
        aria-controls="preference-dialog"
        aria-label="${escapeHtml(t("configuredSelection", { label: label.toLocaleLowerCase(localeTag(appLocale)), count: selectedCount }))}"
        title="${escapeHtml(t("configure"))}"
      >
        <span class="personal-filter-selected-count" aria-hidden="true">${selectedCount}</span>
        <span class="personal-filter-edit-icon" aria-hidden="true">✎</span>
      </button>
    </span>
  `;
}

function renderCatalogPicker(): void {
  if (!websiteRouteContext?.catalogId) {
    filters.catalogId = "all";
  }
  catalogPickerValue.textContent = t("browseCatalogs");
  const count = dataset?.catalogs.length ?? 0;
  catalogPickerCount.textContent = formatUiNumber(count);
  catalogPickerCount.hidden = count === 0;
  catalogPicker.setAttribute("aria-description", t("catalogsAvailable", { count: formatUiNumber(count) }));
  catalogPicker.disabled = count === 0;
}

function catalogLibraryCatalogs(): Catalog[] {
  if (!dataset) {
    return [];
  }
  return orderCatalogLibraryCatalogs(dataset.catalogs, localeTag(appLocale));
}

function renderCatalogLibrary(): void {
  const grouped = new Map<string, Catalog[]>();
  for (const catalog of catalogLibraryCatalogs()) {
    const group = grouped.get(catalog.chain) ?? [];
    group.push(catalog);
    grouped.set(catalog.chain, group);
  }
  catalogLibraryList.innerHTML = [...grouped.entries()]
    .map(
      ([store, catalogs]) => `
        <section class="catalog-library-store">
          <div class="catalog-library-store-heading">
            <h3>${escapeHtml(store)}</h3>
            <span>${escapeHtml(formatUiNumber(catalogs.length))} ${escapeHtml(catalogWord(catalogs.length))}</span>
          </div>
          <div class="catalog-library-items">
            ${catalogs
              .map((catalog) => {
                const canOpen = Boolean(catalogPageImage(catalog, 1));
                return `
                  <article class="catalog-library-item">
                    <div class="catalog-library-item-copy">
                      <h4>${escapeHtml(catalog.title)}</h4>
                      <p>${escapeHtml(formatValidity(catalog.valid_from, catalog.valid_until, appLocale))}</p>
                      <p>${escapeHtml(t("catalogPagesAndOffers", {
                        pages: formatUiNumber(catalog.pages),
                        products: formatUiNumber(catalog.product_count),
                        word: offerWord(catalog.product_count),
                      }))}</p>
                    </div>
                    <button
                      class="catalog-library-open"
                      type="button"
                      data-open-catalog="${escapeHtml(catalog.id)}"
                      aria-label="${escapeHtml(t("openCatalogAria", {
                        store: catalog.chain,
                        title: catalog.title,
                      }))}"
                      ${canOpen ? "" : "disabled"}
                    >${escapeHtml(t("openCatalog"))}</button>
                  </article>
                `;
              })
              .join("")}
          </div>
        </section>
      `,
    )
    .join("");
}

function openCatalogLibrary(): void {
  if (catalogLibraryDialog.open || !dataset?.catalogs.length) {
    return;
  }
  renderCatalogLibrary();
  catalogPicker.setAttribute("aria-expanded", "true");
  catalogLibraryDialog.showModal();
}

function closeCatalogLibrary(): void {
  if (catalogLibraryDialog.open) {
    catalogLibraryDialog.close();
  }
}

function renderCatalogPageOverlays(): void {
  const catalog = activeCatalogViewerCatalog;
  if (!catalog) {
    catalogPageOverlays.replaceChildren();
    return;
  }
  const pageProducts = catalogProductsOnPage(catalog, activeCatalogViewerPage);
  const markerGroups = catalogProductMarkerGroups(
    catalog,
    activeCatalogViewerPage,
  );
  const canvasWidth = catalogCanvasBaseWidth() * catalogViewerScale;
  const canvasHeight =
    canvasWidth *
    (catalogPageImageElement.naturalWidth > 0 &&
    catalogPageImageElement.naturalHeight > 0
      ? catalogPageImageElement.naturalHeight /
        catalogPageImageElement.naturalWidth
      : 1.414);
  const markerPositions = new Map(
    separateCatalogMarkerAnchors(
      markerGroups.map((group) => ({
        id: group.id,
        left: group.geometry.actionLeft,
        top: group.geometry.actionTop,
      })),
      canvasWidth,
      canvasHeight,
      46,
    ).map((position) => [position.id, position] as const),
  );
  const highlights = pageProducts.flatMap((product, index) =>
    productKey(product) === activeCatalogHighlightKey
      ? [
          `<span
            class="catalog-product-highlight"
            data-catalog-highlight-index="${index}"
            aria-hidden="true"
          ></span>`,
        ]
      : [],
  );
  const actions = markerGroups.map((group, index) => {
    const product = group.products[0]!;
    if (group.products.length === 1) {
      return `<div class="catalog-quantity-anchor" data-catalog-overlay-index="${index}">
        ${productQuantityControlMarkup(productQuantityView(product, "catalog"))}
      </div>`;
    }
    const addedCount = group.products.filter((candidate) =>
      isInShoppingList(productKey(candidate)),
    ).length;
    const allAdded = addedCount === group.products.length;
    const partiallyAdded = addedCount > 0 && !allAdded;
    const label = t("chooseCatalogProductVariants", { count: group.products.length });
    return `
      <button
        class="catalog-product-add ${allAdded ? "is-added" : ""} ${partiallyAdded ? "is-partial" : ""}"
        type="button"
        data-catalog-product-group-index="${index}"
        data-catalog-overlay-index="${index}"
        aria-label="${escapeHtml(label)}"
        aria-pressed="${String(allAdded)}"
      >
        <span class="catalog-product-add-icon" aria-hidden="true">${allAdded ? "✓" : "+"}</span>
        <span class="catalog-product-add-count" aria-hidden="true">${group.products.length}</span>
      </button>
    `;
  });
  const accessibleTargets = catalogInteractionsEnabled ? markerGroups.map((group, index) => `
    <button type="button" class="catalog-product-hit" data-catalog-hit-group="${index}"
      aria-label="${escapeHtml(group.products.length === 1
        ? t("openProductCard", { title: localizedProductTitle(group.products[0]!) })
        : t("chooseCatalogProductVariants", { count: group.products.length }))}"
    ></button>`) : [];
  catalogPageOverlays.innerHTML = [...highlights, ...accessibleTargets, ...actions].join("");
  pageProducts.forEach((product, index) => {
    const geometry = catalogOverlayGeometry(product.bounding_box);
    const highlight = catalogPageOverlays.querySelector<HTMLElement>(
      `[data-catalog-highlight-index="${index}"]`,
    );
    if (highlight) {
      highlight.style.left = `${geometry.left}%`;
      highlight.style.top = `${geometry.top}%`;
      highlight.style.width = `${geometry.width}%`;
      highlight.style.height = `${geometry.height}%`;
    }
  });
  markerGroups.forEach((group, index) => {
    const hit = catalogPageOverlays.querySelector<HTMLElement>(`[data-catalog-hit-group="${index}"]`);
    if (hit) {
      Object.assign(hit.style, { left: `${group.geometry.left}%`, top: `${group.geometry.top}%`,
        width: `${group.geometry.width}%`, height: `${group.geometry.height}%` });
    }
    const position = markerPositions.get(group.id);
    const action = catalogPageOverlays.querySelector<HTMLElement>(
      `[data-catalog-overlay-index="${index}"]`,
    );
    if (action && position) {
      action.dataset.anchorLeft = String(position.left);
      action.dataset.anchorTop = String(position.top);
      action.style.left = `${position.left}%`;
      action.style.top = `${position.top}%`;
    }
  });
  positionCatalogQuantityControls();
}

function positionCatalogQuantityControls(): void {
  const width = catalogPageCanvas.clientWidth;
  const height = catalogPageCanvas.clientHeight;
  catalogPageOverlays.querySelectorAll<HTMLElement>(".catalog-quantity-anchor").forEach((anchor) => {
    anchor.style.left = `${clampQuantityControlAnchor(Number(anchor.dataset.anchorLeft), width, anchor.offsetWidth)}%`;
    anchor.style.top = `${clampQuantityControlAnchor(Number(anchor.dataset.anchorTop), height, anchor.offsetHeight)}%`;
    anchor.classList.toggle("is-expanded", Boolean(anchor.querySelector(".quantity-catalog.is-added:not(.is-compact)")));
  });
}

function updateCatalogShoppingControls(): void {
  const catalog = activeCatalogViewerCatalog;
  if (!catalog) return;
  const pageProducts = new Map(catalogProductsOnPage(catalog, activeCatalogViewerPage)
    .map((product) => [productKey(product), product]));
  catalogPageOverlays.querySelectorAll<HTMLElement>("[data-quantity-key]").forEach((control) => {
    const product = pageProducts.get(control.dataset.quantityKey ?? "");
    if (product) updateProductQuantityControl(control, productQuantityView(product, "catalog"));
  });
  const groups = catalogProductMarkerGroups(catalog, activeCatalogViewerPage);
  catalogPageOverlays.querySelectorAll<HTMLButtonElement>("[data-catalog-product-group-index]").forEach((button) => {
    const group = groups[Number(button.dataset.catalogProductGroupIndex)];
    if (!group) return;
    const added = group.products.filter((product) => isInShoppingList(productKey(product))).length;
    const allAdded = added === group.products.length;
    button.classList.toggle("is-added", allAdded);
    button.classList.toggle("is-partial", added > 0 && !allAdded);
    button.setAttribute("aria-pressed", String(allAdded));
    button.setAttribute("aria-label", t("chooseCatalogProductVariants", { count: group.products.length }));
    button.querySelector<HTMLElement>(".catalog-product-add-icon")!.textContent = allAdded ? "✓" : "+";
  });
  positionCatalogQuantityControls();
}

function renderCatalogProductChoices(): void {
  catalogProductChoiceList.dataset.locale = appLocale;
  catalogProductChoiceDescription.textContent = t(
    "catalogProductVariantsDescription",
  );
  catalogProductChoiceList.innerHTML = activeCatalogProductGroup
    .map((product) => {
      const title = localizedProductTitle(product);
      const original = secondaryProductTitle(product);
      const added = isInShoppingList(productKey(product));
      const price = productDisplayPrice(product);
      const currentPrice =
        price === null
          ? `<span>${escapeHtml(t("checkPriceInStore"))}</span>`
          : `<strong>${escapeHtml(formatPrice(price.current, appLocale))}</strong>`;
      const oldPrice =
        price?.old == null
          ? ""
          : `<s>${escapeHtml(formatPrice(price.old, appLocale))}</s>`;
      const packageText = product.weight_or_volume
        ? formatWeightOrVolume(product.weight_or_volume, appLocale)
        : "";
      return `
        <div
          class="catalog-product-choice-item ${added ? "is-added" : ""}"
        >
          <${catalogProductChoiceMode === "open" ? `button type="button" data-catalog-open-product="${escapeHtml(productKey(product))}"` : "span"} class="catalog-product-choice-copy">
            <strong>${escapeHtml(title)}</strong>
            ${original ? `<span class="catalog-product-choice-original">${escapeHtml(original)}</span>` : ""}
            ${packageText ? `<span class="catalog-product-choice-package">${escapeHtml(packageText)}</span>` : ""}
            <span class="catalog-product-choice-price">${currentPrice}${oldPrice}</span>
            ${requiredCardMarkup(product.required_card)}
            ${price?.conditional ? `<span class="price-requirement">${escapeHtml(conditionalPriceText(product, appLocale))}</span>` : ""}
          </${catalogProductChoiceMode === "open" ? "button" : "span"}>
          ${productQuantityControlMarkup(productQuantityView(product, "variant"))}
        </div>
      `;
    })
    .join("");
}

function updateCatalogProductChoices(): void {
  if (catalogProductChoiceList.dataset.locale !== appLocale) {
    renderCatalogProductChoices();
    return;
  }
  catalogProductChoiceList.querySelectorAll<HTMLElement>("[data-quantity-key]").forEach((control) => {
    const product = activeCatalogProductGroup.find((candidate) => productKey(candidate) === control.dataset.quantityKey);
    if (!product) return;
    const view = productQuantityView(product, "variant");
    updateProductQuantityControl(control, view);
    control.closest(".catalog-product-choice-item")?.classList.toggle("is-added", view.quantity > 0);
  });
}

function openCatalogProductChoices(products: ProductView[], mode: "quantity" | "open" = "quantity"): void {
  catalogProductChoiceMode = mode;
  activeCatalogProductGroup = products;
  renderCatalogProductChoices();
  if (!catalogProductChoiceDialog.open) {
    catalogProductChoiceDialog.showModal();
  }
}

function closeCatalogProductChoices(): void {
  if (catalogProductChoiceDialog.open) {
    catalogProductChoiceDialog.close();
  }
}

function readStoreSavePromptDismissals(): Record<string, number> {
  try {
    return parseStorePromptDismissals(
      readUserStorageValue(STORE_SAVE_PROMPT_DISMISSALS_KEY),
    );
  } catch {
    return {};
  }
}

function readStoreSavePromptNextEligibleAt(): number {
  try {
    return parseStoreSavePromptNextEligibleAt(
      readUserStorageValue(STORE_SAVE_PROMPT_NEXT_ELIGIBLE_AT_KEY),
    );
  } catch {
    return 0;
  }
}

function writeStoreSavePromptNextEligibleAt(nextEligibleAt: number): void {
  try {
    writeUserStorageValue(
      STORE_SAVE_PROMPT_NEXT_ELIGIBLE_AT_KEY,
      JSON.stringify(nextEligibleAt),
    );
  } catch {
    // The synchronized state still protects other devices when storage fails.
  }
}

function extendCatalogStoreSavePromptCooldown(durationMs: number): void {
  const nextEligibleAt = extendStoreSavePromptCooldown(
    storeSavePromptNextEligibleAt,
    durationMs,
    currentReferenceTime().getTime(),
  );
  if (nextEligibleAt === storeSavePromptNextEligibleAt) {
    return;
  }
  storeSavePromptNextEligibleAt = nextEligibleAt;
  writeStoreSavePromptNextEligibleAt(nextEligibleAt);
  queueUserStateMutation({ storeSavePromptNextEligibleAt: nextEligibleAt });
}

function writeStoreSavePromptDismissals(
  dismissals: Readonly<Record<string, number>>,
): void {
  try {
    writeUserStorageValue(
      STORE_SAVE_PROMPT_DISMISSALS_KEY,
      JSON.stringify(dismissals),
    );
  } catch {
    // The session-level claim still prevents a repeated prompt this launch.
  }
}

function renderCatalogStoreSavePromptCopy(saved = false): void {
  const store = catalogStorePromptStore;
  if (!store) {
    return;
  }
  catalogStoreSaveCopy.textContent = t(
    saved ? "storeSavedConfirmation" : "storeSavePrompt",
    { store },
  );
  catalogStoreSaveAction.textContent = t("storeSaveAction");
  catalogStoreSaveClose.setAttribute(
    "aria-label",
    t("storeSaveDismissAria"),
  );
}

function hideCatalogStoreSavePrompt(immediate = false): void {
  window.clearTimeout(catalogStorePromptHideTimer);
  catalogStorePromptHideTimer = undefined;
  catalogStoreSavePrompt.classList.remove("is-visible", "is-saved");
  catalogStoreSaveAction.disabled = false;
  const finish = () => {
    if (!catalogStoreSavePrompt.classList.contains("is-visible")) {
      catalogStoreSavePrompt.hidden = true;
      catalogStorePromptStore = null;
    }
  };
  if (immediate) {
    finish();
    return;
  }
  catalogStorePromptHideTimer = window.setTimeout(finish, 210);
}

function showCatalogStoreSavePrompt(store: string): void {
  window.clearTimeout(catalogStorePromptHideTimer);
  catalogStorePromptHideTimer = undefined;
  catalogStorePromptStore = store;
  catalogStoreSavePrompt.classList.remove("is-saved");
  catalogStoreSaveAction.disabled = false;
  renderCatalogStoreSavePromptCopy();
  catalogStoreSavePrompt.hidden = false;
  window.requestAnimationFrame(() => {
    catalogStoreSavePrompt.classList.add("is-visible");
  });
}

function maybeOfferCatalogStoreSave(): void {
  const catalog = activeCatalogViewerCatalog;
  if (
    !catalog ||
    !catalogViewerDialog.open ||
    catalog.id !== catalogStorePromptCatalogId ||
    retentionPromptClaimedThisSession ||
    storePromptEvaluatedThisSession || homeScreenHasPriority() ||
    storeSavePromptExperimentVariant === "off" ||
    (!storeSavePromptLocalPreview &&
      (!telegram.webApp?.initData || !priceWatchState.canNotify)) ||
    !savedStoreCountAllowsStorePrompt(personalPreferences.stores) ||
    personalPreferences.stores.includes(catalog.chain) ||
    (!storeSavePromptLocalPreview &&
      storeSavePromptGlobalCooldownIsActive(
        storeSavePromptNextEligibleAt,
        currentReferenceTime().getTime(),
      )) ||
    storePromptIsCoolingDown(
      readStoreSavePromptDismissals(),
      catalog.chain,
    ) ||
    !catalogBrowsingQualifiesForStorePrompt(
      catalog.pages,
      catalogStorePromptVisitedPages,
    )
  ) {
    return;
  }

  storePromptEvaluatedThisSession = true;
  queueActivity("storeSavePromptEligible");
  if (storeSavePromptExperimentVariant === "control") {
    return;
  }
  retentionPromptClaimedThisSession = true;
  queueActivity("storeSavePromptTreatment");
  extendCatalogStoreSavePromptCooldown(STORE_SAVE_PROMPT_SHOW_COOLDOWN_MS);
  queueActivity("storeSavePromptShown");
  showCatalogStoreSavePrompt(catalog.chain);
}

function dismissCatalogStoreSavePrompt(): void {
  const store = catalogStorePromptStore;
  if (!store) {
    return;
  }
  writeStoreSavePromptDismissals(
    dismissStorePrompt(readStoreSavePromptDismissals(), store),
  );
  extendCatalogStoreSavePromptCooldown(STORE_SAVE_PROMPT_DISMISS_COOLDOWN_MS);
  queueActivity("storeSavePromptDismissed");
  hapticTap(telegram);
  hideCatalogStoreSavePrompt();
}

function saveCatalogStoreFromPrompt(): void {
  const store = catalogStorePromptStore;
  if (!store || catalogStoreSaveAction.disabled) {
    return;
  }
  const stores = personalPreferences.stores.includes(store)
    ? personalPreferences.stores
    : [...personalPreferences.stores, store];
  const catalogNotificationStores =
    personalPreferences.catalogNotificationStores.includes(store)
      ? personalPreferences.catalogNotificationStores
      : [...personalPreferences.catalogNotificationStores, store];
  personalPreferences = {
    ...personalPreferences,
    stores,
    catalogNotificationStores,
  };
  savePersonalPreferences();
  renderStoreChips();
  renderActiveFilterDock();
  queueActivity("storeSavePromptAccepted");
  hapticSuccess(telegram);
  catalogStoreSaveAction.disabled = true;
  catalogStoreSavePrompt.classList.add("is-saved");
  renderCatalogStoreSavePromptCopy(true);
  catalogStorePromptHideTimer = window.setTimeout(
    () => hideCatalogStoreSavePrompt(),
    1_650,
  );
}

function preloadCatalogPage(catalog: Catalog, page: number): void {
  const path = catalogPageImage(catalog, page);
  if (!path) {
    return;
  }
  const image = new Image();
  image.decoding = "async";
  image.src = appAssetUrl(path);
}

function renderCatalogViewer(): void {
  catalogViewerDialog.classList.toggle("has-catalog-interactions", catalogInteractionsEnabled);
  catalogShoppingButton.hidden = !catalogInteractionsEnabled;
  updateCatalogShoppingButton();
  const catalog = activeCatalogViewerCatalog;
  if (!catalog) {
    return;
  }
  activeCatalogViewerPage = clampCatalogPage(
    activeCatalogViewerPage,
    catalog.pages,
  );
  const rawPagePath = catalogPageImage(catalog, activeCatalogViewerPage);
  if (!rawPagePath) {
    return;
  }
  const pagePath = appAssetUrl(rawPagePath);
  catalogViewerStore.textContent = catalog.chain;
  catalogViewerTitle.textContent = catalog.title;
  catalogViewerValidity.textContent = formatValidity(
    catalog.valid_from,
    catalog.valid_until,
    appLocale,
  );
  catalogViewerShare.setAttribute(
    "aria-label",
    t("catalogShareAria", {
      store: catalog.chain,
      title: catalog.title,
      page: formatUiNumber(activeCatalogViewerPage),
    }),
  );
  catalogPageCounter.textContent = t("catalogPageCounter", {
    page: formatUiNumber(activeCatalogViewerPage),
    pages: formatUiNumber(catalog.pages),
  });
  catalogPagePrevious.disabled = activeCatalogViewerPage <= 1;
  catalogPageNext.disabled = activeCatalogViewerPage >= catalog.pages;
  catalogPageImageElement.alt = t("catalogPageAlt", {
    store: catalog.chain,
    title: catalog.title,
    page: formatUiNumber(activeCatalogViewerPage),
  });
  if (catalogPageImageElement.getAttribute("src") !== pagePath) {
    catalogPageImageElement.classList.add("is-loading");
    catalogPageImageElement.src = pagePath;
    catalogPageScroll.scrollTo({ top: 0, left: 0 });
  }
  renderCatalogPageOverlays();
  if (activeCatalogViewerPage > 1) {
    preloadCatalogPage(catalog, activeCatalogViewerPage - 1);
  }
  if (activeCatalogViewerPage < catalog.pages) {
    preloadCatalogPage(catalog, activeCatalogViewerPage + 1);
  }
  if (!catalogStoreSavePrompt.hidden) {
    renderCatalogStoreSavePromptCopy(
      catalogStoreSavePrompt.classList.contains("is-saved"),
    );
  }
}

function catalogCanvasBaseWidth(): number {
  const styles = getComputedStyle(catalogPageScroll);
  const horizontalPadding =
    (Number.parseFloat(styles.paddingLeft) || 0) +
    (Number.parseFloat(styles.paddingRight) || 0);
  return Math.min(
    860,
    Math.max(1, catalogPageScroll.clientWidth - horizontalPadding),
  );
}

function updateCatalogZoomUi(): void {
  const zoomed = catalogViewerScale > CATALOG_ZOOM_MIN + 0.01;
  catalogPageScroll.classList.toggle("is-zoomed", zoomed);
  catalogViewerZoom.setAttribute("aria-pressed", String(zoomed));
  catalogViewerZoom.setAttribute(
    "aria-label",
    t(zoomed ? "zoomOut" : "zoomIn"),
  );
  catalogViewerZoomPlus.toggleAttribute("hidden", zoomed);
  catalogViewerZoomMinus.toggleAttribute("hidden", !zoomed);
}

function setCatalogViewerScale(
  requestedScale: number,
  anchor?: { x: number; y: number },
  refreshMarkers = true,
): void {
  const nextScale = clampCatalogZoomScale(requestedScale);
  const oldCanvasRect = catalogPageCanvas.getBoundingClientRect();
  const scrollRect = catalogPageScroll.getBoundingClientRect();
  const anchorPoint = anchor ?? {
    x: scrollRect.left + scrollRect.width / 2,
    y: scrollRect.top + scrollRect.height / 2,
  };
  const relativeX =
    oldCanvasRect.width > 0
      ? Math.min(
          1,
          Math.max(0, (anchorPoint.x - oldCanvasRect.left) / oldCanvasRect.width),
        )
      : 0.5;
  const relativeY =
    oldCanvasRect.height > 0
      ? Math.min(
          1,
          Math.max(0, (anchorPoint.y - oldCanvasRect.top) / oldCanvasRect.height),
        )
      : 0.5;
  catalogViewerScale = nextScale;
  catalogPageCanvas.style.width = `${catalogCanvasBaseWidth() * nextScale}px`;
  updateCatalogZoomUi();
  const newCanvasRect = catalogPageCanvas.getBoundingClientRect();
  catalogPageScroll.scrollLeft +=
    newCanvasRect.left + relativeX * newCanvasRect.width - anchorPoint.x;
  catalogPageScroll.scrollTop +=
    newCanvasRect.top + relativeY * newCanvasRect.height - anchorPoint.y;
  if (nextScale === CATALOG_ZOOM_MIN) {
    catalogPageScroll.scrollLeft = 0;
  }
  if (
    refreshMarkers &&
    catalogViewerDialog.open &&
    activeCatalogViewerCatalog
  ) {
    renderCatalogPageOverlays();
  }
}

function catalogTouchDistance(touches: TouchList): number {
  const first = touches.item(0);
  const second = touches.item(1);
  if (!first || !second) {
    return 0;
  }
  return Math.hypot(
    second.clientX - first.clientX,
    second.clientY - first.clientY,
  );
}

function catalogTouchMidpoint(
  touches: TouchList,
): { x: number; y: number } | null {
  const first = touches.item(0);
  const second = touches.item(1);
  return first && second
    ? {
        x: (first.clientX + second.clientX) / 2,
        y: (first.clientY + second.clientY) / 2,
      }
    : null;
}

function finishCatalogPinch(): void {
  if (!catalogPinchState) {
    return;
  }
  catalogPinchState = null;
  catalogPageScroll.classList.remove("is-pinching");
  if (catalogViewerScale < 1.04) {
    setCatalogViewerScale(CATALOG_ZOOM_MIN);
  } else {
    updateCatalogZoomUi();
    renderCatalogPageOverlays();
  }
}

function changeCatalogViewerPage(delta: number): void {
  catalogTap.cancel();
  const catalog = activeCatalogViewerCatalog;
  if (!catalog) {
    return;
  }
  const nextPage = clampCatalogPage(
    activeCatalogViewerPage + delta,
    catalog.pages,
  );
  if (nextPage === activeCatalogViewerPage) {
    return;
  }
  activeCatalogViewerPage = nextPage;
  activeCatalogQuantityKey = null;
  setCatalogViewerScale(CATALOG_ZOOM_MIN, undefined, false);
  catalogPageScroll.scrollTo({ top: 0, left: 0 });
  hapticTap(telegram);
  renderCatalogViewer();
  catalogStorePromptVisitedPages.add(nextPage);
  if (
    catalogBrowsingQualifiesForStorePrompt(
      catalog.pages,
      catalogStorePromptVisitedPages,
    )
  ) {
    signalHomeScreenUsefulAction();
  }
  maybeOfferCatalogStoreSave();
}

function openCatalogViewer(
  catalog: Catalog,
  page = 1,
  highlightKey: string | null = null,
  returnTarget: CatalogViewerReturn = { kind: "library" },
): void {
  if (!catalogPageImage(catalog, page)) {
    return;
  }
  if (catalogInteractionsEnabled && catalogViewerDialog.open && activeCatalogViewerCatalog) {
    // A card/analogue can lead into another catalogue without losing its parent.
    catalogReturnFrames.push({ catalogId: activeCatalogViewerCatalog.id,
      page: activeCatalogViewerPage, scale: catalogViewerScale,
      top: catalogPageScroll.scrollTop, left: catalogPageScroll.scrollLeft,
      aspectRatio: catalogPageCanvas.style.aspectRatio,
      highlight: activeCatalogHighlightKey, returnTarget: catalogViewerReturn,
      shopping: shoppingListDialog.open, shoppingTop: shoppingListDialog.scrollTop });
    if (shoppingListDialog.open) shoppingListDialog.close();
    closeCatalogProductChoices();
  }
  catalogTap.cancel();
  queueActivity("catalogOpens");
  catalogViewerTransitioning = true;
  if (catalogLibraryDialog.open) {
    catalogLibraryDialog.close();
  }
  if (dialog.open) {
    dialog.close();
  }
  catalogViewerTransitioning = false;
  activeCatalogViewerCatalog = catalog;
  activeCatalogQuantityKey = null;
  activeCatalogViewerPage = clampCatalogPage(page, catalog.pages);
  hideCatalogStoreSavePrompt(true);
  catalogStorePromptCatalogId = catalog.id;
  catalogStorePromptVisitedPages = new Set([activeCatalogViewerPage]);
  activeCatalogHighlightKey = highlightKey;
  catalogViewerReturn = returnTarget;
  pendingCatalogViewerReturn = null;
  if (!catalogViewerDialog.open) {
    catalogViewerDialog.showModal();
  }
  setCatalogViewerScale(CATALOG_ZOOM_MIN);
  renderCatalogViewer();
}

function closeCatalogViewer(returnToSource: boolean): void {
  if (!catalogViewerDialog.open) {
    return;
  }
  pendingCatalogViewerReturn = returnToSource ? catalogViewerReturn : null;
  catalogViewerDialog.close();
}

function renderSortPicker(): void {
  sortPickerValue.textContent =
    sortChoices().find((choice) => choice.value === filters.sort)?.label ??
    t("sortDefault");
}

function openChoiceDialog(kind: ChoiceKind): void {
  if (choiceDialog.open) {
    return;
  }
  const choices = [...sortChoices()];
  const selectedValue = filters.sort;
  const title = t("productDisplay");
  activeChoiceKind = kind;
  activeChoiceTrigger = sortPicker;
  choiceDialogEyebrow.textContent = t("sorting");
  choiceDialogTitle.textContent = title;
  choiceOptions.innerHTML = choices
    .map((choice) => {
      const active = choice.value === selectedValue;
      return `
        <button
          class="choice-option ${active ? "active" : ""}"
          type="button"
          data-choice-value="${escapeHtml(choice.value)}"
          aria-pressed="${String(active)}"
        >
          <span>${escapeHtml(choice.label)}</span>
          <span class="choice-check" aria-hidden="true">✓</span>
        </button>
      `;
    })
    .join("");
  activeChoiceTrigger.setAttribute("aria-expanded", "true");
  choiceDialog.showModal();
  window.requestAnimationFrame(() => {
    choiceOptions
      .querySelector<HTMLElement>(".choice-option.active")
      ?.scrollIntoView({ block: "nearest" });
  });
}

function closeChoiceDialog(): void {
  if (choiceDialog.open) {
    choiceDialog.close();
  }
}

interface PreferenceOption {
  value: string;
  label: string;
  count: number;
}

function preferenceOptionList(kind: PreferenceKind): PreferenceOption[] {
  if (!dataset) {
    return [];
  }
  if (kind === "stores") {
    const counts = new Map<string, number>();
    for (const product of products) {
      if (product.category_id === "alcohol") {
        continue;
      }
      counts.set(
        product.catalog.chain,
        (counts.get(product.catalog.chain) ?? 0) + 1,
      );
    }
    const knownFromData = (sourceDataset ?? dataset).catalogs.map(
      (catalog) => catalog.chain,
    );
    return storeDirectory(knownFromData, personalPreferences.stores).map(
      (store) => ({
        value: store,
        label: store,
        count: counts.get(store) ?? 0,
      }),
    );
  }

  const counts = new Map<string, number>();
  for (const product of products) {
    if (product.category_id) {
      counts.set(product.category_id, (counts.get(product.category_id) ?? 0) + 1);
    }
  }
  return [...(dataset.categories ?? [])]
    .sort((left, right) => {
      if (left.id === "alcohol") {
        return 1;
      }
      if (right.id === "alcohol") {
        return -1;
      }
      return left.order - right.order;
    })
    .map((category) => ({
      value: category.id,
      label: localizedCategoryLabel(category),
      count: counts.get(category.id) ?? 0,
    }));
}

function orderedPreferenceValues(kind: PreferenceKind): string[] {
  const ordered = preferenceOptionList(kind)
    .map((option) => option.value)
    .filter((value) => draftPreferenceValues.has(value));
  const visible = new Set(ordered);
  return [
    ...ordered,
    ...[...draftPreferenceValues].filter((value) => !visible.has(value)),
  ];
}

function currentOfferCountLabel(count: number): string {
  return t("currentOffers", {
    count: formatUiNumber(count),
    word: offerWord(count),
  });
}

function savedPreferenceValues(): readonly string[] {
  if (activePreferenceKind === "stores") {
    return personalPreferences.stores;
  }
  if (activePreferenceKind === "categories") {
    return personalPreferences.categoryIds;
  }
  return [];
}

function preferenceSelectionChanged(): boolean {
  return hasPreferenceDraftChanges(
    savedPreferenceValues(),
    draftPreferenceValues,
  );
}

function preferenceDraftHasChanges(): boolean {
  return hasPreferenceDraftChanges(
    savedPreferenceValues(),
    draftPreferenceValues,
    activePreferenceKind === "stores"
      ? {
          saved: personalPreferences.catalogNotificationStores,
          draft: draftCatalogNotificationStores,
        }
      : undefined,
  );
}

function updatePreferenceActions(): void {
  preferenceClear.disabled = draftPreferenceValues.size === 0;
  preferenceApply.disabled = !preferenceDraftHasChanges();
  preferenceApply.textContent = draftPreferenceValues.size
    ? t("applyCount", { count: draftPreferenceValues.size })
    : t("apply");
}

function renderPreferenceOptions(): void {
  if (!activePreferenceKind) {
    preferenceOptions.replaceChildren();
    return;
  }
  preferenceOptions.innerHTML = preferenceDialogOptions
    .map((option) => {
      const active = draftPreferenceValues.has(option.value);
      const notificationEnabled =
        active && draftCatalogNotificationStores.has(option.value);
      const availability =
        option.count > 0
          ? currentOfferCountLabel(option.count)
          : t("noCurrentOffers");
      if (activePreferenceKind === "stores") {
        return `
          <div class="preference-store-option ${active ? "active" : ""}">
            <button
              class="preference-store-selection"
              type="button"
              data-preference-value="${escapeHtml(option.value)}"
              role="checkbox"
              aria-checked="${String(active)}"
            >
              <span class="preference-option-copy">
                <strong>${escapeHtml(option.label)}</strong>
                <small>${escapeHtml(availability)}</small>
              </span>
              <span class="choice-check" aria-hidden="true">✓</span>
            </button>
            <label class="preference-store-notification ${active ? "" : "is-disabled"}">
              <input
                type="checkbox"
                role="switch"
                data-catalog-notification-store="${escapeHtml(option.value)}"
                ${notificationEnabled ? "checked" : ""}
                ${active ? "" : "disabled"}
                aria-label="${escapeHtml(
                  t("catalogNotificationsForStore", { store: option.label }),
                )}"
              />
              <svg
                class="preference-store-notification-icon"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
                <path d="M10 21h4" />
              </svg>
              <span class="preference-store-notification-label">${escapeHtml(
                t("catalogNotificationsShort"),
              )}</span>
              <span class="preference-toggle" aria-hidden="true"></span>
            </label>
          </div>
        `;
      }
      return `
        <button
          class="choice-option ${active ? "active" : ""}"
          type="button"
          data-preference-value="${escapeHtml(option.value)}"
          role="checkbox"
          aria-checked="${String(active)}"
        >
          <span class="preference-option-copy">
            <strong>${escapeHtml(option.label)}</strong>
            <small>${availability}</small>
          </span>
          <span class="choice-check" aria-hidden="true">✓</span>
        </button>
      `;
    })
    .join("");
  updatePreferenceActions();
}

function openPreferenceDialog(
  kind: PreferenceKind,
  trigger: HTMLButtonElement,
): void {
  if (preferenceDialog.open || !dataset) {
    return;
  }
  if (kind === "stores") {
    storeButtonGlow.acknowledge();
    updateStoreButtonGlow();
  }
  activePreferenceKind = kind;
  activePreferenceTrigger = trigger;
  draftPreferenceValues = new Set(
    kind === "stores"
      ? personalPreferences.stores
      : personalPreferences.categoryIds,
  );
  draftCatalogNotificationStores = new Set(
    kind === "stores"
      ? personalPreferences.catalogNotificationStores.filter((store) =>
          draftPreferenceValues.has(store),
        )
      : [],
  );
  preferenceDialogOptions = selectedOptionsFirst(
    preferenceOptionList(kind),
    draftPreferenceValues,
  );
  preferenceDialogEyebrow.textContent =
    kind === "stores" ? t("personalFilter") : t("interests");
  preferenceDialogTitle.textContent =
    kind === "stores" ? t("myStores") : t("myCategories");
  preferenceDialogDescription.textContent =
    kind === "stores"
      ? t("storesPreferenceDescription")
      : t("categoriesPreferenceDescription");
  hidePreferenceUnsavedPrompt();
  renderPreferenceOptions();
  trigger.setAttribute("aria-expanded", "true");
  preferenceDialog.showModal();
}

function setPreferenceDialogContentInert(inert: boolean): void {
  const content = preferenceDialog.querySelectorAll<HTMLElement>(
    ":scope > .preference-dialog-header, :scope > .preference-dialog-description, :scope > .preference-options, :scope > .preference-actions",
  );
  for (const element of content) {
    element.inert = inert;
  }
}

function preferenceUnsavedSummary(): string {
  if (!activePreferenceKind) {
    return "";
  }
  const lines: string[] = [];
  if (preferenceSelectionChanged()) {
    if (draftPreferenceValues.size === 0) {
      lines.push(
        activePreferenceKind === "stores"
          ? t("clearStoresSummary")
          : t("clearCategoriesSummary"),
      );
    } else {
      lines.push(
        activePreferenceKind === "stores"
          ? t("selectedStoresSummary", {
              count: draftPreferenceValues.size,
            })
          : t("selectedCategoriesSummary", {
              count: draftPreferenceValues.size,
            }),
      );
    }
  }
  if (
    activePreferenceKind === "stores" &&
    hasPreferenceDraftChanges(
      personalPreferences.catalogNotificationStores,
      draftCatalogNotificationStores,
    )
  ) {
    lines.push(t("notificationSettingsChangedSummary"));
  }
  return lines.join(" ");
}

function showPreferenceUnsavedPrompt(): void {
  if (!preferenceUnsavedOverlay.hidden) {
    return;
  }
  preferenceUnsavedDescription.textContent = preferenceUnsavedSummary();
  setPreferenceDialogContentInert(true);
  preferenceUnsavedOverlay.hidden = false;
  preferenceUnsavedDiscard.focus({ preventScroll: true });
}

function hidePreferenceUnsavedPrompt(): void {
  preferenceUnsavedOverlay.hidden = true;
  setPreferenceDialogContentInert(false);
}

function finishClosePreferenceDialog(): void {
  hidePreferenceUnsavedPrompt();
  if (preferenceDialog.open) {
    preferenceDialog.close();
  }
}

function requestClosePreferenceDialog(): void {
  if (!preferenceDialog.open) {
    return;
  }
  if (preferenceDraftHasChanges()) {
    showPreferenceUnsavedPrompt();
    return;
  }
  finishClosePreferenceDialog();
}

function applyPreferenceSelection(): void {
  if (!activePreferenceKind) {
    return;
  }
  const values = orderedPreferenceValues(activePreferenceKind);
  if (activePreferenceKind === "stores") {
    personalPreferences = {
      ...personalPreferences,
      stores: values,
      catalogNotificationStores: values.filter((store) =>
        draftCatalogNotificationStores.has(store),
      ),
    };
    if (values.length > 0) {
      filters.store = PERSONAL_STORES_FILTER;
      filters.catalogId = "all";
      setPersonalFilterMode("stores", "personal");
    } else {
      setPersonalFilterMode("stores", "all");
      if (filters.store === PERSONAL_STORES_FILTER) {
        filters.store = "all";
        filters.catalogId = "all";
      }
    }
  } else {
    personalPreferences = { ...personalPreferences, categoryIds: values };
    if (values.length > 0) {
      filters.categoryId = PERSONAL_CATEGORIES_FILTER;
      setPersonalFilterMode("categories", "personal");
    } else {
      setPersonalFilterMode("categories", "all");
      if (filters.categoryId === PERSONAL_CATEGORIES_FILTER) {
        filters.categoryId = "all";
      }
    }
  }
  savePersonalPreferences();
  saveFilterSelection();
  hapticTap(telegram);
  finishClosePreferenceDialog();
  resetAndRender();
}

function clearPreferenceSelection(): void {
  if (!activePreferenceKind) {
    return;
  }
  draftPreferenceValues.clear();
  if (activePreferenceKind === "stores") {
    draftCatalogNotificationStores.clear();
  }
  hapticTap(telegram);
  renderPreferenceOptions();
}

function comparisonStarterDefaultContext(): boolean {
  return comparisonStarterShelfEnabled && discoveryFeedContext(filters);
}

function comparisonShelfView(): ComparisonShelfView {
  return {
    families: comparisonStarterFamilies, totalFamilies: comparisonCatalog.length,
    activeId: activeComparisonStarterFamily,
    all: comparisonShelfAll, query: comparisonShelfQuery, limit: comparisonShelfLimit,
    mode: comparisonShelfMode ?? "home",
    locale: appLocale, assetUrl: appAssetUrl,
  };
}

function renderComparisonStarterShelf(): void {
  const minimumFamilies = comparisonShelfMode === "search" ? 1 : 2;
  if (!comparisonShelfMode || comparisonStarterFamilies.length < minimumFamilies) {
    comparisonStarterShelf.hidden = true;
    comparisonStarterShelf.classList.remove("is-search");
    comparisonStarterShelf.classList.remove("is-expanded");
    comparisonStarterShelf.replaceChildren();
    return;
  }
  comparisonStarterShelf.hidden = false;
  comparisonStarterShelf.classList.toggle("is-search", comparisonShelfMode === "search");
  comparisonStarterShelf.classList.toggle(
    "is-expanded",
    comparisonShelfMode === "home" && comparisonShelfAll,
  );
  if (!comparisonStarterShelfExposureRecorded) {
    comparisonStarterShelfExposureRecorded = true;
    queueActivity("starterShelfEligible");
    queueActivity("starterShelfTreatment");
    queueActivity("starterShelfShown");
  }
  comparisonStarterShelf.innerHTML = comparisonShelfMarkup(comparisonShelfView());
  scheduleComparisonAutoLoadIfNeeded();
  resetComparisonCollapsePosition();
  scheduleComparisonCollapsePosition();
}

function positionComparisonStarterShelf(): void {
  if (
    comparisonShelfMode === "home" &&
    comparisonStarterFamilies.length >= 2
  ) {
    const productCards = productGrid.querySelectorAll(":scope > .product-card");
    productGrid.insertBefore(
      comparisonStarterShelf,
      productCards.item(4) || null,
    );
    return;
  }
  productGrid.before(comparisonStarterShelf);
}

function updateFeedSentinel(): void {
  feedSentinel.hidden = visibleCount >= feedProducts.length;
}

function scheduleAutoLoadIfNeeded(): void {
  if (autoLoadScheduled || feedSentinel.hidden) {
    return;
  }
  autoLoadScheduled = true;
  window.requestAnimationFrame(() => {
    autoLoadScheduled = false;
    if (feedSentinel.hidden) {
      return;
    }
    const position = feedSentinel.getBoundingClientRect();
    if (position.top <= window.innerHeight + 700) {
      appendNextPage();
    }
  });
}

function scheduleComparisonAutoLoadIfNeeded(): void {
  if (
    comparisonAutoLoadScheduled ||
    comparisonShelfMode !== "home" ||
    !comparisonShelfAll ||
    !comparisonStarterShelf.querySelector("[data-comparison-starter-sentinel]")
  ) {
    return;
  }
  comparisonAutoLoadScheduled = true;
  window.requestAnimationFrame(() => {
    comparisonAutoLoadScheduled = false;
    const sentinel = comparisonStarterShelf.querySelector<HTMLElement>(
      "[data-comparison-starter-sentinel]",
    );
    if (
      comparisonShelfMode === "home" &&
      comparisonShelfAll &&
      sentinel &&
      sentinel.getBoundingClientRect().top <= window.innerHeight + 700
    ) {
      appendNextComparisonPage();
    }
  });
}

function appendNextComparisonPage(): void {
  const grid = comparisonStarterShelf.querySelector<HTMLElement>(
    "[data-comparison-starter-grid]",
  );
  if (
    comparisonShelfMode !== "home" ||
    !comparisonShelfAll ||
    !grid ||
    !comparisonStarterShelf.querySelector("[data-comparison-starter-sentinel]")
  ) {
    return;
  }
  comparisonShelfLimit += COMPARISON_PAGE_SIZE;
  grid.innerHTML = comparisonShelfGridMarkup(comparisonShelfView());
  scheduleComparisonAutoLoadIfNeeded();
}

function resetComparisonCollapsePosition(): void {
  comparisonCollapseFixedTop = 0;
  comparisonCollapseFixedBottom = 0;
}

function scheduleComparisonCollapsePosition(): void {
  if (comparisonCollapsePositionScheduled) return;
  comparisonCollapsePositionScheduled = true;
  window.requestAnimationFrame(() => {
    comparisonCollapsePositionScheduled = false;
    const button = comparisonStarterShelf.querySelector<HTMLElement>(
      "[data-comparison-starter-collapse]",
    );
    if (!button) {
      resetComparisonCollapsePosition();
      return;
    }
    if (!comparisonCollapseFixedBottom) {
      button.classList.remove("is-docked", "is-before-shelf");
      const fixed = button.getBoundingClientRect();
      comparisonCollapseFixedTop = fixed.top;
      comparisonCollapseFixedBottom = fixed.bottom;
    }
    const shelf = comparisonStarterShelf.getBoundingClientRect();
    const docked = shelf.bottom - 12 <= comparisonCollapseFixedBottom;
    button.classList.toggle("is-docked", docked);
    button.classList.toggle(
      "is-before-shelf",
      !docked && shelf.top + 12 >= comparisonCollapseFixedTop,
    );
  });
}

function appendNextPage(): void {
  const nextCount = nextVisibleCount(
    visibleCount,
    feedProducts.length,
    PAGE_SIZE,
  );
  if (nextCount <= visibleCount) {
    updateFeedSentinel();
    return;
  }
  const nextProducts = feedProducts.slice(visibleCount, nextCount);
  visibleCount = nextCount;
  productGrid.insertAdjacentHTML(
    "beforeend",
    nextProducts.map(productCard).join(""),
  );
  for (const product of nextProducts) {
    renderedProducts.set(productKey(product), product);
  }
  updateFeedSentinel();
  scheduleAutoLoadIfNeeded();
}

function searchRecoveryLabel(label: SearchRecoveryLabel): string {
  const keys = {
    personalStores: "searchRecoveryMyStores",
    personalCategories: "searchRecoveryMyCategories",
    personalBoth: "searchRecoveryMyBoth",
    otherStores: "searchRecoveryOtherStores",
    otherCategories: "searchRecoveryOtherCategories",
    otherCatalogs: "searchRecoveryOtherCatalogs",
    otherFilters: "searchRecoveryOtherFilters",
  } as const;
  return t(keys[label]);
}

function searchRecoveryMarkup(): string {
  if (!searchRecovery.groups.length) return "";
  const intro = matchingProducts.length > 0
    ? `<p class="search-recovery-intro">${escapeHtml(t("searchRecoveryMore"))}</p>` : "";
  return intro + searchRecovery.groups.map((group) => {
    const preview = group.products.slice(0, SEARCH_RECOVERY_PREVIEW_SIZE);
    for (const product of preview) renderedProducts.set(productKey(product), product);
    const titleId = `search-recovery-${group.scope}-title`;
    return `<section class="search-recovery-section" aria-labelledby="${titleId}">
      <div class="search-recovery-heading">
        <h3 id="${titleId}">${escapeHtml(searchRecoveryLabel(group.label))}</h3>
        <span class="search-recovery-count">${formatUiNumber(group.products.length)}</span>
      </div>
      <div class="search-recovery-products">${preview.map(productCard).join("")}</div>
      <button type="button" class="search-recovery-action" data-search-recovery-scope="${group.scope}">
        ${escapeHtml(t(group.scope === "all" ? "searchRecoveryEverywhere" : "searchRecoveryShowAll", {
          count: formatUiNumber(group.destinationCount),
        }))}
      </button>
    </section>`;
  }).join("");
}

function renderProducts(): void {
  if (!dataset) {
    return;
  }
  const websiteGroup = __KILOPRICE_WEBSITE__ ? websiteRouteContext?.productGroupId : undefined;
  const scopedProducts = __KILOPRICE_WEBSITE__
    ? products.filter(product => websiteOfferActive(product, product.catalog, activeDateKey)
      && (!websiteGroup || (websiteGroupKeys
        ? websiteGroupKeys.has(`${product.catalog.id}/${product.id}`)
        : matchesWebsiteProductGroup(product, websiteGroup)))) : products;
  const defaultContext = comparisonStarterDefaultContext();
  const comparisonSearchQuery = filters.query.trim();
  const parsedQuery = parseProductSearchQuery(comparisonSearchQuery, products);
  const brandedQuery = !parsedQuery.multiple && !parsedQuery.error && parsedQuery.items[0]
    ? qualifiedBrandQuery(products, parsedQuery.items[0]) : undefined;
  const nextComparisonMode: ComparisonShelfMode | null = defaultContext
    ? "home"
    : comparisonStarterShelfEnabled && comparisonSearchQuery && !parsedQuery.multiple && !parsedQuery.error && !brandedQuery ? "search" : null;
  // Localized catalog assets share generated_at. Include the active locale so
  // the comparison shelf cannot retain ProductView objects from the old asset.
  const comparisonRevision = `${appLocale}:${dataset.generated_at}:${activeDateKey}`;
  if (comparisonStarterShelfEnabled && comparisonCatalogRevision !== comparisonRevision) {
    comparisonCatalog = orderComparisonFamilies(buildComparisonCatalog(products, activeDateKey));
    comparisonCatalogRevision = comparisonRevision;
  }
  if (nextComparisonMode !== comparisonShelfMode) {
    activeComparisonStarterFamily = null;
    comparisonShelfAll = false;
    comparisonShelfQuery = "";
    comparisonShelfLimit = COMPARISON_PAGE_SIZE;
    comparisonSearchReturnFamilies = [];
  }
  comparisonShelfMode = nextComparisonMode;
  comparisonStarterFamilies = nextComparisonMode === "home"
    ? comparisonCatalog
    : nextComparisonMode === "search"
      ? comparisonFamiliesForSearch(comparisonCatalog, comparisonSearchQuery)
      : [];
  if (
    activeComparisonStarterFamily &&
    !comparisonStarterFamilies.some(
      (family) => family.id === activeComparisonStarterFamily,
    )
  ) {
    activeComparisonStarterFamily = null;
  }
  // Disclosure state never owns the feed/filter. These products remain visible
  // in their usual positions even while the comparison is open.
  const signature = parsedQuery.items.join("\u0000");
  if (signature !== multiSearchSignature) {
    multiSearchSignature = signature;
    multiSearchLimits.clear();
  }
  multiSearchGroups = [];
  if (parsedQuery.multiple || parsedQuery.error || brandedQuery) {
    multiSearchGroups = buildMultiSearchGroups(scopedProducts, filters, !__KILOPRICE_WEBSITE__);
    matchingProducts = multiSearchDistinctProducts(multiSearchGroups);
    feedProducts = []; // Each group owns bounded expansion, not global pagination.
    searchRecovery = { groups: [], totalMatches: matchingProducts.length };
    renderedProducts = new Map();
    productGrid.innerHTML = multiSearchMarkup({
      groups: multiSearchGroups, limits: multiSearchLimits, count: formatUiNumber,
      recoveryLabel: searchRecoveryLabel, canWatch: Boolean(telegram.webApp?.initData),
      renderProduct: (product) => { renderedProducts.set(productKey(product), product); return productCard(product); },
    });
    resultCount.textContent = formatUiNumber(matchingProducts.length);
    resultTitle.textContent = parsedQuery.multiple ? t("multiSearchResults", { count: formatUiNumber(parsedQuery.items.length) })
      : t("queryResult", { query: filters.query.trim() });
    renderComparisonStarterShelf();
    positionComparisonStarterShelf();
    if (parsedQuery.error) setStatus(t(parsedQuery.error === "items" ? "multiSearchLimit" : "searchTextLimit", {
      count: parsedQuery.error === "items" ? MAX_SEARCH_ITEMS : MAX_SEARCH_TEXT,
    }));
    else hideStatus();
    updateFeedSentinel();
    return;
  }
  if (__KILOPRICE_WEBSITE__) matchingProducts = filterProducts(scopedProducts, filters);
  else matchingProducts = filterProducts(products, filters);
  searchRecovery = !__KILOPRICE_WEBSITE__
    ? buildSearchRecovery(products, filters, matchingProducts)
    : { groups: [], totalMatches: matchingProducts.length };
  feedProducts =
    discoveryFeedVisit &&
    discoveryFeedContext(filters)
      ? discoveryFeedVisit.rank(
          matchingProducts,
          `${dataset.generated_at}:${activeDateKey}`,
          {
            today: activeDateKey,
            personalStores: personalPreferences.stores,
            personalCategoryIds: personalPreferences.categoryIds,
          },
        )
      : matchingProducts;
  const visible = feedProducts.slice(0, visibleCount);
  renderedProducts = new Map(visible.map((product) => [productKey(product), product]));
  const primaryHeading = searchRecovery.groups.length && matchingProducts.length
    ? `<div class="search-results-scope"><h3>${escapeHtml(t("searchRecoverySelected"))}</h3><span>${formatUiNumber(matchingProducts.length)}</span></div>`
    : "";
  productGrid.innerHTML = primaryHeading + visible.map(productCard).join("") + searchRecoveryMarkup();
  renderComparisonStarterShelf();
  positionComparisonStarterShelf();

  resultCount.textContent = formatUiNumber(searchRecovery.totalMatches);
  const selectedCategory = dataset.categories?.find(
    (category) => category.id === filters.categoryId,
  );
  const selectedCatalog = dataset.catalogs.find(
    (catalog) => catalog.id === filters.catalogId,
  );
  const personalStoresActive = filters.store === PERSONAL_STORES_FILTER;
  const personalCategoriesActive =
    filters.categoryId === PERSONAL_CATEGORIES_FILTER;
  resultTitle.textContent = filters.query.trim()
      ? t("queryResult", { query: filters.query.trim() })
    : websiteGroup ? websiteProductGroup(websiteGroup)?.labels[appLocale] ?? t("allOffers")
    : selectedCatalog
      ? `${selectedCatalog.chain} · ${selectedCatalog.title}`
      : personalStoresActive && personalCategoriesActive
        ? t("myOffers")
        : personalCategoriesActive
          ? t("myCategories")
          : selectedCategory
            ? localizedCategoryLabel(selectedCategory)
            : personalStoresActive
              ? t("myStores")
              : filters.store === "all"
                ? t("allOffers")
                : t("storeOffers", { store: filters.store });

  const comparisonAnswersSearch = comparisonShelfMode === "search" &&
    comparisonStarterFamilies.length > 0;
  if (matchingProducts.length === 0) {
    if (searchRecovery.groups.length) {
      setStatus(t("searchRecoveryEmpty"));
      statusPanel.classList.add("search-recovery-notice");
    } else if (comparisonAnswersSearch) {
      // A strict all-store comparison is already a useful answer. Do not put a
      // contradictory dead-end notice directly above it when narrow filters
      // happen to hide the ordinary product cards.
      hideStatus();
    } else if (!filters.query.trim() && personalStoresActive) {
      setStatus(t("noPersonalStoreOffers"));
    } else if (!filters.query.trim() && personalCategoriesActive) {
      setStatus(t("noPersonalCategoryOffers"));
    } else {
      setStatus(t("nothingFound"));
      const query = filters.query.trim();
      if (query.length >= 2 && telegram.webApp?.initData) {
        const watchButton = document.createElement("button");
        watchButton.type = "button";
        watchButton.className = "status-watch-button";
        watchButton.dataset.watchQuery = query;
        watchButton.textContent = t("notifyWhenAppears", { query });
        statusPanel.append(watchButton);
      }
    }
  } else {
    hideStatus();
  }
  updateFeedSentinel();
  scheduleAutoLoadIfNeeded();
}

function resetAndRender(): void {
  visibleCount = PAGE_SIZE;
  renderStoreChips();
  renderCategoryControls();
  renderActiveFilterDock();
  renderCatalogPicker();
  renderSortPicker();
  renderProducts();
  scheduleFilterMeasurement();
}

function currentReferenceTime(): Date {
  return new Date(Date.now() + serverClockOffsetMs);
}

function currentShoppingDateKey(): string {
  return activeDateKey || dateKeyInMoldova(currentReferenceTime());
}

function removeShoppingItemsPastRetention(): void {
  const retainedItems = pruneExpiredShoppingItems(
    shoppingItems,
    currentShoppingDateKey(),
  );
  if (retainedItems.length !== shoppingItems.length) {
    persistShoppingList(retainedItems);
  }
}

function refreshActiveCatalogs(force = false): void {
  if (!sourceDataset) {
    return;
  }
  const nextDateKey = dateKeyInMoldova(currentReferenceTime());
  if (!force && nextDateKey === activeDateKey) {
    return;
  }
  activeDateKey = nextDateKey;
  // Hide expired matches immediately on rollover/foreground, even if the
  // subsequent read-only refresh fails. Saved rules and delivery history stay intact.
  if (priceWatchListDialog.open) renderPriceWatchList();
  if (priceWatchesLoaded && priceWatchesLoadedDate !== nextDateKey) {
    void ensurePriceWatchesLoaded().catch(() => { /* Retry on next opening. */ });
  }
  if (__KILOPRICE_WEBSITE__) expireWebsiteOffers(nextDateKey, t("expiredOffers"));
  removeShoppingItemsPastRetention();
  dataset = activeCatalogDataset(sourceDataset, nextDateKey);
  products = flattenProducts(dataset);
  productRelationshipIndex = buildProductRelationshipIndex(products);
  const currentProductsByKey = new Map(
    products.map((product) => [productKey(product), product]),
  );
  const currentProduct = (product: ProductView): ProductView | null => {
    const current = currentProductsByKey.get(productKey(product));
    return current && (!__KILOPRICE_WEBSITE__ || websiteOfferActive(current, current.catalog, nextDateKey))
      ? current : null;
  };
  if (activeDialogProduct) {
    activeDialogProduct = currentProduct(activeDialogProduct);
  }
  productDialogHistory = productDialogHistory.flatMap((product) => {
    const refreshed = currentProduct(product);
    return refreshed ? [refreshed] : [];
  });
  activeCatalogProductGroup = activeCatalogProductGroup.flatMap((product) => {
    const refreshed = currentProduct(product);
    return refreshed ? [refreshed] : [];
  });
  if (priceWatchReturnProduct) {
    const refreshedProduct = currentProduct(priceWatchReturnProduct.product);
    priceWatchReturnProduct = refreshedProduct
      ? {
          product: refreshedProduct,
          history: priceWatchReturnProduct.history.flatMap((product) => {
            const refreshed = currentProduct(product);
            return refreshed ? [refreshed] : [];
          }),
        }
      : null;
  }
  if (activeCatalogViewerCatalog) {
    const refreshedCatalog = dataset.catalogs.find(
      (catalog) => catalog.id === activeCatalogViewerCatalog?.id,
    );
    if (refreshedCatalog) {
      activeCatalogViewerCatalog = refreshedCatalog;
    } else if (catalogViewerDialog.open) {
      closeCatalogViewer(false);
    }
  }
  enrichShoppingUnitPrices();
  const stores = new Set(dataset.catalogs.map((catalog) => catalog.chain));
  if (
    filters.store !== "all" &&
    filters.store !== PERSONAL_STORES_FILTER &&
    !stores.has(filters.store) && !(chatSearchEntry && filters.store === chatSearchEntry.store)
  ) {
    filters.store = "all";
    filters.catalogId = "all";
    saveFilterSelection();
  }
  renderDatasetMeta(dataset);
  resetAndRender();
  refreshMealPilot();
  if (catalogLibraryDialog.open) {
    renderCatalogLibrary();
  }
  const productsToIndex = products;
  window.setTimeout(() => {
    if (products === productsToIndex) {
      void prepareProductSearch(productsToIndex).catch(() => {
        // Exact search remains available through the guarded fallback.
      });
    }
  }, 250);
}

function dismissSearchKeyboard(): void {
  searchInput.blur();
  const focused = document.activeElement;
  if (focused instanceof HTMLElement && focused !== document.body) {
    focused.blur();
  }
  const virtualKeyboard = (
    navigator as Navigator & { virtualKeyboard?: { hide(): void } }
  ).virtualKeyboard;
  virtualKeyboard?.hide();
}

function updateSearchClearButton(): void {
  searchClear.hidden = searchInput.value.length === 0;
}

function recordSettledSearch(): void {
  window.clearTimeout(searchActivityTimer);
  searchActivityTimer = undefined;
  const query = searchInput.value.trim().toLocaleLowerCase();
  if (!query) {
    lastTrackedSearch = "";
    return;
  }
  if (query === lastTrackedSearch) {
    return;
  }
  lastTrackedSearch = query;
  queueActivity("searches");
  if (
    filterProducts(products, {
      ...filters,
      query: searchInput.value,
    }).length === 0
  ) {
    queueActivity("zeroResultSearches");
  }
}

function submitSearch(): void {
  visitFilterHydration.keepCurrent();
  window.clearTimeout(searchTimer);
  recordSettledSearch();
  filters.query = searchInput.value;
  visibleCount = PAGE_SIZE;
  renderProducts();
  dismissSearchKeyboard();
}

function relatedUnitPriceText(product: ProductView): string {
  const unit = product.unit_price;
  if (!unit || unit.is_range) {
    return "";
  }
  return `${formatPrice(unit.minimum_mdl, appLocale)} ${unitPriceLabel(unit.basis)}`;
}

function analogueRelationLabel(analogue: ProductAnalogue): string {
  if (analogue.relation === "same") {
    return t("sameComparablePrice");
  }
  return t(analogue.relation === "cheaper" ? "cheaperBy" : "dearerBy", {
    percent: formatUiNumber(analogue.differencePercent),
  });
}

function relatedProductCardMarkup(
  product: ProductView,
  analogue: ProductAnalogue | null,
): string {
  const title = localizedProductTitle(product);
  const packageLabel = product.weight_or_volume
    ? formatWeightOrVolume(product.weight_or_volume, appLocale)
    : "";
  const price = productDisplayPrice(product);
  const currentPrice =
    price === null
      ? t("priceInStore")
      : formatPrice(price.current, appLocale);
  const unitPrice = relatedUnitPriceText(product);
  const relation = analogue ? analogueRelationLabel(analogue) : "";
  return `
    <button
      class="related-product-card"
      type="button"
      data-related-product-key="${escapeHtml(relatedProductKey(product))}"
      aria-label="${escapeHtml(t("openRelatedProduct", { title }))}"
    >
      <span class="related-product-image">
        <img src="${escapeHtml(appAssetUrl(product.image))}" alt="" loading="lazy" decoding="async" />
      </span>
      <span class="related-product-copy">
        <strong>${escapeHtml(title)}</strong>
        <span class="related-product-meta">
          <b>${escapeHtml(product.catalog.chain)}</b>
          ${packageLabel ? `<span>${escapeHtml(packageLabel)}</span>` : ""}
        </span>
        <span class="related-product-prices">
          <b>${escapeHtml(currentPrice)}</b>
          ${unitPrice ? `<span>${escapeHtml(unitPrice)}</span>` : ""}
        </span>
        ${requiredCardMarkup(product.required_card)}
        ${price?.conditional ? `<span class="price-requirement">${escapeHtml(conditionalPriceText(product, appLocale))}</span>` : ""}
      </span>
      ${
        analogue
          ? `<span class="related-product-badges">
              ${analogue.exactProduct ? `<small>${escapeHtml(t("sameProduct"))}</small>` : ""}
              <em class="is-${analogue.relation}">${escapeHtml(relation)}</em>
            </span>`
          : ""
      }
    </button>
  `;
}

function relatedSectionMarkup(
  section: "analogues" | "similar",
  title: string,
  help: string,
  cards: readonly string[],
): string {
  if (cards.length === 0) {
    return "";
  }
  return `
    <section class="related-products-section" data-related-section="${section}">
      <div class="related-products-heading">
        <h3>${escapeHtml(title)}</h3>
        <p>${escapeHtml(help)}</p>
      </div>
      <div class="related-products-list">${cards.join("")}</div>
    </section>
  `;
}

function productRelationshipsMarkup(product: ProductView): string {
  const analogues = productAnalogues(product, productRelationshipIndex);
  const excluded = new Set(
    analogues.map((analogue) => relatedProductKey(analogue.product)),
  );
  const similar = similarProducts(
    product,
    productRelationshipIndex,
    excluded,
    SIMILAR_PRODUCTS_MAX_COUNT,
  );
  const analogueCards = analogues.map((analogue) =>
    relatedProductCardMarkup(analogue.product, analogue),
  );
  const similarCards = similar.map((candidate) =>
    relatedProductCardMarkup(candidate, null),
  );
  return [
    relatedSectionMarkup(
      "analogues",
      t("comparableProducts"),
      t("comparableProductsHelp"),
      analogueCards,
    ),
    relatedSectionMarkup(
      "similar",
      t("similarProducts"),
      t("similarProductsHelp"),
      similarCards,
    ),
  ].join("");
}

function productPriceWatchButtonMarkup(product: ProductView): string {
  if (!telegram.webApp?.initData) {
    return "";
  }
  const tracked = Boolean(watchRuleForProduct(product)?.enabled);
  return `
    <button
      class="dialog-price-watch${tracked ? " is-active" : ""}"
      type="button"
      data-price-watch-product="${escapeHtml(productKey(product))}"
      aria-pressed="${String(tracked)}"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M18 9.5a6 6 0 0 0-12 0c0 6-2.5 6-2.5 7.5h17C20.5 15.5 18 15.5 18 9.5Z" />
        <path d="M9.5 20h5" />
      </svg>
      <span>${escapeHtml(t(tracked ? "priceTracked" : "watchPrice"))}</span>
    </button>
  `;
}

function fullProductMarkup(product: ProductView): string {
  const originalName = secondaryProductTitle(product);
  const category = dataset?.categories?.find(
    (candidate) => candidate.id === product.category_id,
  );
  const facts = [
    product.brand ? `<span>${escapeHtml(t("brand"))}: ${escapeHtml(product.brand)}</span>` : "",
    product.weight_or_volume
      ? `<span>${escapeHtml(t("package"))}: ${escapeHtml(formatWeightOrVolume(product.weight_or_volume, appLocale))}</span>`
      : "",
    category ? `<span>${escapeHtml(t("category"))}: ${escapeHtml(localizedCategoryLabel(category))}</span>` : "",
  ].filter(Boolean);
  const translatedNote = productDisplayNote(product, appLocale);
  const conditionalPrice = productDisplayPrice(product)?.conditional === true;
  const originalNote = secondaryProductNote(product);
  const canOpenCatalogPage = Boolean(productCatalogPage(product, dataset?.catalogs));
  const catalogLabel = `${product.catalog.chain} · ${product.catalog.title}`;
  const catalogPageLabel = `${formatValidity(
    product.catalog.valid_from,
    product.catalog.valid_until,
    appLocale,
  )} · ${t("pageShort")} ${product.page}`;
  const catalogReferenceMainContent = `
    <span class="dialog-catalog-reference-main">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H11v16H6.5A2.5 2.5 0 0 0 4 21.5v-16ZM20 5.5A2.5 2.5 0 0 0 17.5 3H13v16h4.5a2.5 2.5 0 0 1 2.5 2.5v-16Z" />
      </svg>
      <span class="dialog-catalog-reference-title">${escapeHtml(catalogLabel)}</span>
    </span>
  `;
  const catalogReferenceContextContent = `<span class="dialog-catalog-reference-context">${escapeHtml(catalogPageLabel)}</span>`;

  return `
    ${originalName ? `<section><span class="dialog-label">${escapeHtml(t("originalName"))}</span><p>${escapeHtml(originalName)}</p></section>` : ""}
    <section class="dialog-price-block">
      ${priceMarkup(product)}
      ${unitPriceMarkup(product)}
      ${conditionalPrice && translatedNote ? `<p class="offer-price-context">${escapeHtml(translatedNote)}</p>` : ""}
      ${productPriceWatchButtonMarkup(product)}
    </section>
    ${facts.length ? `<div class="dialog-facts">${facts.join("")}</div>` : ""}
    ${translatedNote && !conditionalPrice ? `<section><span class="dialog-label">${escapeHtml(t("note"))}</span><p>${escapeHtml(translatedNote)}</p>${originalNote ? `<p class="dialog-original-note">${escapeHtml(originalNote)}</p>` : ""}</section>` : ""}
    <section class="dialog-catalog-meta">
      ${canOpenCatalogPage ? `
        <button
          class="dialog-open-catalog"
          type="button"
          data-open-catalog-page="${escapeHtml(product.page)}"
          aria-label="${escapeHtml(t("openCatalogPageAria", {
            page: product.page,
            title: product.catalog.title,
          }))}"
        >
          ${catalogReferenceMainContent}
          <span class="dialog-catalog-reference-side">
            ${catalogReferenceContextContent}
            <span class="dialog-catalog-reference-action" aria-hidden="true">
              ${escapeHtml(t("openCatalog"))}
              <svg viewBox="0 0 12 12">
                <path d="m4.25 2.25 3.5 3.75-3.5 3.75" />
              </svg>
            </span>
          </span>
        </button>
      ` : `<div class="dialog-catalog-reference is-static">${catalogReferenceMainContent}${catalogReferenceContextContent}</div>`}
    </section>
    ${productRelationshipsMarkup(product)}
  `;
}

interface OpenProductOptions {
  preserveHistory?: boolean;
  scrollToTop?: boolean;
  trackActivity?: boolean;
  source?: "feed" | "search" | "starter-shelf" | "catalog" | "related" | "meal";
}

function updateProductDialogCloseButton(): void {
  const returnsToProduct = productDialogHistory.length > 0 || productFromCatalog;
  dialogCloseButton.dataset.mode = returnsToProduct ? "back" : "close";
  dialogCloseButton.dataset.i18nAriaLabel = returnsToProduct ? "back" : "close";
  dialogCloseButton.setAttribute(
    "aria-label",
    t(returnsToProduct ? "back" : "close"),
  );
}

function openProduct(
  product: ProductView,
  options: OpenProductOptions = {},
): void {
  if (!options.preserveHistory) {
    productDialogHistory = [];
  }
  productFromCatalog = catalogInteractionsEnabled && catalogViewerDialog.open;
  if (options.trackActivity !== false) {
    queueActivity("productOpens");
    if (options.source === "starter-shelf") {
      queueActivity("starterShelfProductOpens");
    } else if (options.source === "meal") {
      queueActivity("mealProductOpens");
    } else if (options.source === "search") {
      queueActivity("searchProductOpens");
    } else if (options.source === "catalog") {
      queueActivity("catalogProductOpens");
    } else if (options.source === "related") {
      queueActivity("relatedProductOpens");
    } else if (options.source === "feed") {
      queueActivity("feedProductOpens");
    }
  }
  activeDialogProduct = product;
  dialogImage.hidden = false;
  dialogActions.hidden = false;
  dialogImage.src = appAssetUrl(product.image);
  dialogImage.alt = localizedProductTitle(product);
  dialogStore.textContent = `${product.catalog.chain} · ${product.catalog.title}`;
  dialogTitle.textContent = localizedProductTitle(product);
  dialogMeta.innerHTML = fullProductMarkup(product);
  dialogShareButton.disabled = false;
  dialogShareLabel.textContent = t("share");
  dialogShareStatus.textContent = "";
  updateDialogShoppingButton();
  updateProductDialogCloseButton();
  showProductDialog();
  if (options.trackActivity !== false) signalHomeScreenUsefulAction();
  if (options.scrollToTop !== false) {
    dialog.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }
}

function openRelatedProduct(product: ProductView): void {
  if (activeDialogProduct) {
    productDialogHistory.push(activeDialogProduct);
  }
  openProduct(product, { preserveHistory: true, source: "related" });
}

function closeOrReturnProductDialog(): void {
  const previousProduct = productDialogHistory.pop();
  if (previousProduct) {
    openProduct(previousProduct, {
      preserveHistory: true,
      trackActivity: false,
    });
    return;
  }
  if (dialog.open) {
    dialog.close();
  }
}

function showProductDialog(): void {
  if (dialog.open) {
    return;
  }
  productDialogScrollLock.lock();
  try {
    dialog.showModal();
  } catch (error) {
    productDialogScrollLock.unlock();
    throw error;
  }
}

function firstVisibleCard(): HTMLElement | null {
  const panelBottom = filterSurface.getBoundingClientRect().bottom;
  const threshold = Math.max(0, panelBottom);
  const cards = productGrid.querySelectorAll<HTMLElement>(".product-card");
  return (
    Array.from(cards).find((card) => card.getBoundingClientRect().bottom > threshold) ??
    cards.item(0)
  );
}

function applyGridMode(value: GridMode, preservePosition = true): void {
  const anchor = preservePosition ? firstVisibleCard() : null;
  const anchorTop = anchor?.getBoundingClientRect().top ?? 0;
  gridMode = value;
  productGrid.dataset.layout = value;
  updateGridToggleCopy();
  saveGridMode(value);
  if (anchor) {
    window.requestAnimationFrame(() => {
      const offset = anchor.getBoundingClientRect().top - anchorTop;
      if (Math.abs(offset) > 1) {
        window.scrollBy(0, offset);
      }
      scheduleAutoLoadIfNeeded();
    });
  }
}

function updateGridToggleCopy(): void {
  const compact = gridMode === "compact";
  gridToggle.dataset.gridMode = gridMode;
  gridToggle.setAttribute("aria-pressed", String(compact));
  gridToggle.setAttribute(
    "aria-label",
    t(compact ? "gridCompactAria" : "gridSingleAria"),
  );
  gridToggle.title = t(compact ? "gridCompactTitle" : "gridSingleTitle");
}

function updateFilterCollapse(): void {
  if (filterExpandedHeight <= 0 || filterCollapsedHeight <= 0) {
    return;
  }
  const collapseRange = Math.max(
    1,
    filterExpandedHeight - filterCollapsedHeight,
  );
  const naturalOffset = Math.min(
    collapseRange,
    Math.max(0, window.scrollY - filterDocumentTop),
  );
  const revealOffset =
    filterRevealScrollY === null
      ? null
      : document.activeElement === searchInput
        ? 0
        : Math.min(
            collapseRange,
            Math.max(0, window.scrollY - filterRevealScrollY),
          );
  const collapseOffset = revealOffset ?? naturalOffset;
  if (revealOffset === collapseRange) {
    filterRevealScrollY = null;
  }
  const progress = collapseOffset / collapseRange;
  const dockLinearProgress = Math.max(0, Math.min(1, (progress - 0.45) / 0.55));
  const dockProgress =
    dockLinearProgress * dockLinearProgress * (3 - 2 * dockLinearProgress);
  filterSurface.style.height = `${filterExpandedHeight - collapseOffset}px`;
  filterSurface.style.setProperty(
    "--filter-collapse-progress",
    progress.toFixed(4),
  );
  filterDetails.style.opacity = String(Math.max(0, 1 - progress * 1.15));
  filterDetails.style.transform = `translateY(${-8 * progress}px)`;
  activeFilterDock.style.opacity = dockProgress.toFixed(4);
  activeFilterDock.style.transform = `translateY(calc(-50% + ${(
    10 *
    (1 - dockProgress)
  ).toFixed(2)}px)) scale(${(0.97 + dockProgress * 0.03).toFixed(4)})`;
  const dockAccessible = progress > 0.88;
  if (activeFilterDock.inert === dockAccessible) {
    activeFilterDock.inert = !dockAccessible;
    activeFilterDock.setAttribute("aria-hidden", String(!dockAccessible));
    activeFilterDock.style.pointerEvents = dockAccessible ? "auto" : "none";
  }
  const detailsUnavailable = progress > 0.995;
  if (filterDetails.inert !== detailsUnavailable) {
    filterDetails.inert = detailsUnavailable;
    filterDetails.setAttribute("aria-hidden", String(detailsUnavailable));
  }
  filterSurface.classList.toggle("is-fully-collapsed", progress > 0.995);
}

function measureFilterPanel(): boolean {
  const surfaceStyle = window.getComputedStyle(filterSurface);
  const borderTop = Number.parseFloat(surfaceStyle.borderTopWidth);
  const borderBottom = Number.parseFloat(surfaceStyle.borderBottomWidth);
  const paddingTop = Number.parseFloat(surfaceStyle.paddingTop);
  const paddingBottom = Number.parseFloat(surfaceStyle.paddingBottom);
  const borderHeight = borderTop + borderBottom;
  const paddingHeight = paddingTop + paddingBottom;
  const panelRectangle = filtersPanel.getBoundingClientRect();
  const surfaceRectangle = filterSurface.getBoundingClientRect();
  const searchRectangle = searchForm.getBoundingClientRect();
  const heights = validatedFilterPanelHeights({
    scrollHeight: filterSurface.scrollHeight,
    toolbarHeight: filterToolbar.getBoundingClientRect().height,
    searchWidth: searchRectangle.width,
    searchHeight: searchRectangle.height,
    paddingHeight,
    borderHeight,
  });
  if (!heights) {
    return false;
  }
  filterExpandedHeight = heights.expandedHeight;
  filterCollapsedHeight = heights.collapsedHeight;
  activeFilterDock.style.left = `${searchRectangle.left - panelRectangle.left}px`;
  activeFilterDock.style.width = `${searchRectangle.width}px`;
  activeFilterDock.style.top = `${
    searchRectangle.bottom -
    surfaceRectangle.top +
    (paddingBottom + borderBottom) / 2
  }px`;
  filterDocumentTop =
    filtersAnchor.getBoundingClientRect().top + window.scrollY;
  filtersPanel.style.height = `${filterExpandedHeight}px`;
  updateFilterCollapse();
  return true;
}

function scheduleFilterMeasurement(): void {
  if (filterMeasureScheduled) {
    return;
  }
  filterMeasureScheduled = true;
  window.requestAnimationFrame(() => {
    filterMeasureScheduled = false;
    measureFilterPanel();
  });
}

const scheduleFilterStabilityCheck = createFilterPanelStabilityCheck({
  measure: measureFilterPanel,
  hasOpenOverlay: () => Boolean(document.querySelector("dialog[open]")),
  requestFrame: (callback) => {
    window.requestAnimationFrame(callback);
  },
});

function revealFilters(): void {
  filterRevealScrollY = window.scrollY;
  updateFilterCollapse();
}

function scheduleFilterCollapse(): void {
  if (filterScrollScheduled) {
    return;
  }
  filterScrollScheduled = true;
  window.requestAnimationFrame(() => {
    filterScrollScheduled = false;
    const nextScrollY = Math.max(0, window.scrollY);
    if (
      filterRevealScrollY !== null &&
      document.activeElement === searchInput
    ) {
      filterRevealScrollY = nextScrollY;
    } else if (nextScrollY <= filterDocumentTop) {
      filterRevealScrollY = null;
    }
    updateFilterCollapse();
  });
}

function validateDataset(value: unknown): CatalogDataset {
  const candidate = value as Partial<CatalogDataset>;
  if (
    !value ||
    typeof value !== "object" ||
    ![1, 2].includes(candidate.schema_version ?? -1) ||
    !Array.isArray(candidate.catalogs) ||
    (candidate.schema_version === 2 && !Array.isArray(candidate.categories))
  ) {
    throw new Error(t("invalidCatalogData"));
  }
  return value as CatalogDataset;
}

const localeDatasetCache = new Map<AppLocale, Promise<CatalogDataset>>();

function loadLocaleDataset(locale: AppLocale): Promise<CatalogDataset> {
  const cached = localeDatasetCache.get(locale);
  if (cached) {
    return cached;
  }
  const request = fetch(`/generated/catalogs.${locale}.json`, {
    cache: "no-cache",
  }).then(async (response) => {
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const serverDate = Date.parse(response.headers.get("Date") ?? "");
    if (Number.isFinite(serverDate)) {
      serverClockOffsetMs = serverDate - Date.now();
    }
    return validateDataset(await response.json());
  });
  localeDatasetCache.set(locale, request);
  void request.catch(() => localeDatasetCache.delete(locale));
  return request;
}

function renderDatasetMeta(value: CatalogDataset): void {
  const generated = new Intl.DateTimeFormat(localeTag(appLocale), {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value.generated_at));
  datasetMeta.textContent = t("datasetMeta", {
    catalogs: formatUiNumber(value.catalog_count),
    catalogWord: catalogWord(value.catalog_count),
    products: formatUiNumber(value.product_count),
    productWord: productWord(value.product_count),
    date: generated,
  });
}

async function start(): Promise<void> {
  const businessAvailability=refreshPublicBusinessAccess();
  updateMealPilotAccess();
  syncExistingBotWriteAccess();
  const startParameter = initialStartParameter;
  if (homeScreenDirectLaunch) {
    botAccessResolved = true;
    homeScreenExperimentEvaluationStarted = true;
    void showHomeScreenPrompt({
      knownStatus: homeScreenLocalPreview ? "missed" : undefined,
    });
  }
  const initData = telegram.webApp?.initData;
  priceWatchButton.hidden = !initData;
  if (initData) {
    void recordAdCampaignOpen(initData, startParameter).catch(() => {
      // Attribution must never delay or block the Mini App.
    });
  }
  try {
    sourceDataset = await loadLocaleDataset(appLocale);
    refreshActiveCatalogs(true);
    if (!openSharedContentFromStartParameter()) {
      if (isCatalogLibraryStartParameter(startParameter)) {
        openCatalogLibrary();
      } else {
        openWebsiteProductRoute();
      }
    }
    const initialSync = flushUserStateSync();
    const businessRoute = /^business_([a-z0-9-]{3,40})$/.exec(startParameter ?? "");
    if (initData && (businessRoute || startParameter === RESTAURANTS_START_PARAMETER)) void Promise.all([initialSync,businessAvailability]).then(() => openBusinessPanel(businessRoute?.[1]));
    const fuelRoute = parseFuelNavigation(startParameter);
    if (initData && fuelRoute) {
      void initialSync.then(() => {
        if (!document.querySelector("dialog[open]")) return openFuelPanel(fuelRoute.stationId, fuelRoute.mine, fuelRoute.settings);
      });
    }
    if (isShoppingListStartParameter(startParameter)) {
      void initialSync.then(() => openShoppingList());
    }
    const editWatchId = parsePriceWatchSettingsStartParameter(startParameter);
    if (editWatchId && initData) {
      void initialSync.then(() => {
        if (!document.querySelector("dialog[open]")) return openPriceWatchList(editWatchId);
      });
    } else if (isPriceAlertsStartParameter(startParameter) && initData) {
      void initialSync.then(() => openPriceWatchList());
    }
    if (isStorePreferencesStartParameter(startParameter)) {
      void initialSync.then(() => {
        const configButton = storeChips.querySelector<HTMLButtonElement>(
          'button[data-personal-config="stores"]',
        );
        if (configButton) {
          openPreferenceDialog("stores", configButton);
        }
      });
    }
    if (!homeScreenDirectLaunch) {
      scheduleBotAccessPrompt();
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    datasetMeta.textContent = t("currentCatalogsUnavailable");
    setStatus(t("dataLoadFailed", { message }));
  }
}

let searchTimer: number | undefined;
select<HTMLButtonElement>("#meal-ideas-button").addEventListener("click", () => { void openMealPilot(); });
select<HTMLButtonElement>("#fuel-button").addEventListener("click", () => { void openFuelPanel(); });
select<HTMLButtonElement>("#business-button").addEventListener("click", () => { void openBusinessPanel(); });
searchInput.addEventListener("paste", (event) => {
  const text = event.clipboardData?.getData("text/plain");
  if (!text || !/[\r\n]/.test(text)) return;
  event.preventDefault();
  searchInput.setRangeText(productSearchPasteText(text), searchInput.selectionStart ?? 0,
    searchInput.selectionEnd ?? searchInput.value.length, "end");
  searchInput.dispatchEvent(new Event("input", { bubbles: true }));
});
searchInput.addEventListener("input", () => {
  updateSearchClearButton();
  visitFilterHydration.keepCurrent();
  window.clearTimeout(searchTimer);
  window.clearTimeout(searchActivityTimer);
  searchTimer = window.setTimeout(() => {
    filters.query = searchInput.value;
    visibleCount = PAGE_SIZE;
    renderProducts();
  }, 90);
  searchActivityTimer = window.setTimeout(
    recordSettledSearch,
    SEARCH_ACTIVITY_DELAY_MS,
  );
});

searchForm.addEventListener("submit", (event) => {
  event.preventDefault();
  submitSearch();
});

searchForm.addEventListener("click", revealFilters);
searchInput.addEventListener("focus", revealFilters);

searchInput.addEventListener("keydown", (event) => {
  if (event.key === "Search" || event.key === "Enter") {
    event.preventDefault();
    submitSearch();
  }
});

enableHorizontalWheelScroll(storeChips);
enableHorizontalWheelScroll(categoryChips);

storeChips.addEventListener("animationend", (event) => {
  if (event.animationName !== "stores-glow-sweep") return;
  // Clear the finite animation so a genuine warm revisit can start it again.
  (event.target as Element).closest(".stores-glow-moving")?.classList.remove("stores-glow-moving");
});

storeChips.addEventListener("click", (event) => {
  const unavailableButton = (event.target as Element).closest<HTMLButtonElement>(
    "button[data-unavailable-store]",
  );
  if (unavailableButton?.dataset.unavailableStore) {
    hapticTap(telegram);
    setStatus(
      t("storeCatalogUnavailable", {
        store: unavailableButton.dataset.unavailableStore,
      }),
    );
    return;
  }
  const configButton = (event.target as Element).closest<HTMLButtonElement>(
    'button[data-personal-config="stores"]',
  );
  if (configButton) {
    hapticTap(telegram);
    openPreferenceDialog("stores", configButton);
    return;
  }
  const personalButton = (event.target as Element).closest<HTMLButtonElement>(
    'button[data-personal-apply="stores"]',
  );
  if (personalButton) {
    storeButtonGlow.acknowledge();
    filters.store = PERSONAL_STORES_FILTER;
    filters.catalogId = "all";
    setPersonalFilterMode("stores", "personal");
    saveFilterSelection();
    hapticTap(telegram);
    resetAndRender();
    return;
  }
  const button = (event.target as Element).closest<HTMLButtonElement>("button[data-store]");
  if (!button?.dataset.store) {
    return;
  }
  filters.store = button.dataset.store;
  filters.catalogId = "all";
  setPersonalFilterMode("stores", "all");
  saveFilterSelection();
  hapticTap(telegram);
  resetAndRender();
});

activeFilterDockTrack.addEventListener("click", (event) => {
  const configButton = (event.target as Element).closest<HTMLButtonElement>(
    "button[data-personal-config]",
  );
  const preferenceKind = configButton?.dataset.personalConfig;
  if (
    !configButton ||
    (preferenceKind !== "stores" && preferenceKind !== "categories")
  ) {
    return;
  }
  hapticTap(telegram);
  openPreferenceDialog(preferenceKind, configButton);
});

categoryChips.addEventListener("click", (event) => {
  const configButton = (event.target as Element).closest<HTMLButtonElement>(
    'button[data-personal-config="categories"]',
  );
  if (configButton) {
    hapticTap(telegram);
    openPreferenceDialog("categories", configButton);
    return;
  }
  const personalButton = (event.target as Element).closest<HTMLButtonElement>(
    'button[data-personal-apply="categories"]',
  );
  if (personalButton) {
    filters.categoryId = PERSONAL_CATEGORIES_FILTER;
    setPersonalFilterMode("categories", "personal");
    saveFilterSelection();
    hapticTap(telegram);
    resetAndRender();
    return;
  }
  const button = (event.target as Element).closest<HTMLButtonElement>(
    "button[data-category]",
  );
  if (!button?.dataset.category) {
    return;
  }
  filters.categoryId = button.dataset.category;
  setPersonalFilterMode("categories", "all");
  saveFilterSelection();
  hapticTap(telegram);
  resetAndRender();
});

catalogPicker.addEventListener("click", () => {
  hapticTap(telegram);
  openCatalogLibrary();
});

sortPicker.addEventListener("click", () => {
  hapticTap(telegram);
  openChoiceDialog("sort");
});

choiceOptions.addEventListener("click", (event) => {
  const button = (event.target as Element).closest<HTMLButtonElement>(
    "button[data-choice-value]",
  );
  const value = button?.dataset.choiceValue;
  if (!value || !activeChoiceKind) {
    return;
  }
  const choice = sortChoices().find((candidate) => candidate.value === value);
  if (!choice) {
    return;
  }
  filters.sort = choice.value;
  saveSortMode(choice.value);
  renderSortPicker();
  visibleCount = PAGE_SIZE;
  renderProducts();
  hapticTap(telegram);
  closeChoiceDialog();
});

preferenceOptions.addEventListener("click", (event) => {
  const button = (event.target as Element).closest<HTMLButtonElement>(
    "button[data-preference-value]",
  );
  const value = button?.dataset.preferenceValue;
  if (!value) {
    return;
  }
  if (draftPreferenceValues.has(value)) {
    draftPreferenceValues.delete(value);
    if (activePreferenceKind === "stores") {
      draftCatalogNotificationStores.delete(value);
    }
  } else {
    draftPreferenceValues.add(value);
    if (activePreferenceKind === "stores") {
      draftCatalogNotificationStores.add(value);
    }
  }
  hapticTap(telegram);
  renderPreferenceOptions();
});

preferenceApply.addEventListener("click", applyPreferenceSelection);
preferenceClear.addEventListener("click", clearPreferenceSelection);
preferenceOptions.addEventListener("change", (event) => {
  const toggle = (event.target as Element).closest<HTMLInputElement>(
    "input[data-catalog-notification-store]",
  );
  const store = toggle?.dataset.catalogNotificationStore;
  if (!toggle || !store || !draftPreferenceValues.has(store)) {
    return;
  }
  if (toggle.checked) {
    draftCatalogNotificationStores.add(store);
  } else {
    draftCatalogNotificationStores.delete(store);
  }
  hapticTap(telegram);
  updatePreferenceActions();
});

searchClear.addEventListener("click", () => {
  visitFilterHydration.keepCurrent();
  window.clearTimeout(searchTimer);
  window.clearTimeout(searchActivityTimer);
  searchActivityTimer = undefined;
  searchInput.value = "";
  filters.query = "";
  lastTrackedSearch = "";
  visibleCount = PAGE_SIZE;
  updateSearchClearButton();
  renderProducts();
  searchInput.focus({ preventScroll: true });
});

gridToggle.addEventListener("click", () => {
  hapticTap(telegram);
  applyGridMode(gridMode === "compact" ? "single" : "compact");
});

languageToggle.addEventListener("click", () => {
  hapticTap(telegram);
  toggleLanguageMenu();
});

languageMenu.addEventListener("click", (event) => {
  const option = (event.target as Element).closest<HTMLButtonElement>(
    "button[data-language-option]",
  );
  const nextLocale = normalizeAppLocale(option?.dataset.languageOption);
  if (!nextLocale || nextLocale === appLocale) {
    closeLanguageMenu(true);
    return;
  }
  hapticTap(telegram);
  closeLanguageMenu();
  if (__KILOPRICE_WEBSITE__) {
    window.location.assign(websiteLocalizedUrl(nextLocale));
    return;
  }
  languageToggle.disabled = true;
  void applyAppLocale(nextLocale, true)
    .catch((error) => {
      console.error("KiloPrice locale switch failed", error);
      window.alert(t("languageLoadFailed"));
    })
    .finally(() => {
      languageToggle.disabled = false;
      languageToggle.focus({ preventScroll: true });
    });
});

themeToggle.addEventListener("click", () => {
  hapticTap(telegram);
  toggleThemeMenu();
});

themeMenu.addEventListener("click", (event) => {
  const option = (event.target as Element).closest<HTMLButtonElement>(
    "button[data-theme-option]",
  );
  const preference = normalizeThemePreference(option?.dataset.themeOption);
  if (!preference) {
    return;
  }
  hapticTap(telegram);
  applyThemePreference(preference, true);
  closeThemeMenu(true);
});

document.addEventListener("pointerdown", (event) => {
  if (!themeMenu.hidden && !themeControl.contains(event.target as Node)) {
    closeThemeMenu();
  }
  if (
    !languageMenu.hidden &&
    !languageControl.contains(event.target as Node)
  ) {
    closeLanguageMenu();
  }
});

document.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && !themeMenu.hidden) {
    event.preventDefault();
    closeThemeMenu(true);
  }
  if (event.key === "Escape" && !languageMenu.hidden) {
    event.preventDefault();
    closeLanguageMenu(true);
  }
});

appShareButton.addEventListener("click", () => {
  hapticTap(telegram);
  void shareApplication();
});

botAccessSave.addEventListener("click", () => {
  hapticTap(telegram);
  void saveBotAccess();
});

botAccessLater.addEventListener("click", dismissBotAccessPrompt);
botAccessClose.addEventListener("click", dismissBotAccessPrompt);

homeScreenAdd.addEventListener("click", () => {
  hapticTap(telegram);
  addHomeScreenShortcut();
});
homeScreenLater.addEventListener("click", dismissHomeScreenExperimentPrompt);
homeScreenClose.addEventListener("click", dismissHomeScreenExperimentPrompt);
homeScreenBackdrop.addEventListener("click", dismissHomeScreenExperimentPrompt);

function updateComparisonDisclosure(anchorSelector?: string): void {
  const anchor = anchorSelector ? comparisonStarterShelf.querySelector<HTMLElement>(anchorSelector) : null;
  const top = anchor?.getBoundingClientRect().top;
  const focused = anchor?.matches(":focus-visible") ?? false;
  renderComparisonStarterShelf();
  const replacement = anchorSelector ? comparisonStarterShelf.querySelector<HTMLElement>(anchorSelector) : null;
  if (replacement && top !== undefined) {
    window.scrollBy({ top: replacement.getBoundingClientRect().top - top, left: 0, behavior: "instant" });
    if (focused) replacement.focus({ preventScroll: true });
  }
}

comparisonStarterShelf.addEventListener("click", (event) => {
  const button = (event.target as Element).closest<HTMLButtonElement>("button");
  if (!button) return;
  event.stopPropagation();
  const key = button.dataset.comparisonStarterProductKey;
  if (key) {
    const product = comparisonStarterFamilies.find((family) => family.id === activeComparisonStarterFamily)
      ?.products.find((candidate) => productKey(candidate) === key);
    if (product) { hapticTap(telegram); openProduct(product, { source: "starter-shelf" }); }
    return;
  }
  const familyId = button.dataset.comparisonStarterFamily;
  if (familyId && comparisonStarterFamilies.some((family) => family.id === familyId)) {
    hapticTap(telegram);
    activeComparisonStarterFamily = activeComparisonStarterFamily === familyId ? null : familyId;
    if (activeComparisonStarterFamily) queueActivity("starterShelfGroupOpens");
    updateComparisonDisclosure(`[data-comparison-starter-family="${CSS.escape(familyId)}"]`);
  } else if (button.hasAttribute("data-comparison-starter-close")) {
    const id = activeComparisonStarterFamily;
    activeComparisonStarterFamily = null;
    updateComparisonDisclosure(id ? `[data-comparison-starter-family="${CSS.escape(id)}"]` : undefined);
    // A footer disclosure close returns focus to its controlling tile.
    if (id) comparisonStarterShelf.querySelector<HTMLButtonElement>(`[data-comparison-starter-family="${CSS.escape(id)}"]`)?.focus({ preventScroll: true });
  } else if (button.hasAttribute("data-comparison-starter-all")) {
    if (!comparisonShelfAll) queueActivity("comparisonAllOpens");
    comparisonSearchReturnFamilies = [];
    comparisonShelfAll = true;
    comparisonShelfQuery = "";
    comparisonShelfLimit = COMPARISON_PAGE_SIZE;
    activeComparisonStarterFamily = null;
    updateComparisonDisclosure(".comparison-starter-heading");
    // Opening all comparisons must not summon the phone keyboard by itself.
    const heading = comparisonStarterShelf.querySelector<HTMLElement>(".comparison-starter-heading");
    if (heading) { heading.tabIndex = -1; heading.focus({ preventScroll: true }); }
  } else if (button.hasAttribute("data-comparison-search-all")) {
    if (comparisonShelfMode !== "home" || !comparisonShelfAll) queueActivity("comparisonAllOpens");
    hapticTap(telegram);
    comparisonSearchReturnFamilies = [...comparisonStarterFamilies];
    comparisonStarterFamilies = comparisonFamiliesWithMatchesFirst(
      comparisonCatalog,
      comparisonSearchReturnFamilies,
    );
    comparisonShelfMode = "home";
    comparisonShelfAll = true;
    comparisonShelfQuery = "";
    comparisonShelfLimit = COMPARISON_PAGE_SIZE;
    activeComparisonStarterFamily = null;
    updateComparisonDisclosure(".comparison-starter-heading");
    const heading = comparisonStarterShelf.querySelector<HTMLElement>(".comparison-starter-heading");
    if (heading) { heading.tabIndex = -1; heading.focus({ preventScroll: true }); }
  } else if (button.hasAttribute("data-comparison-starter-collapse")) {
    hapticTap(telegram);
    comparisonShelfAll = false;
    comparisonShelfLimit = COMPARISON_PAGE_SIZE;
    activeComparisonStarterFamily = null;
    if (comparisonSearchReturnFamilies.length) {
      comparisonShelfMode = "search";
      comparisonStarterFamilies = comparisonSearchReturnFamilies;
      comparisonSearchReturnFamilies = [];
    }
    renderComparisonStarterShelf();
    const shelfDocumentTop =
      comparisonStarterShelf.getBoundingClientRect().top + window.scrollY;
    const stickyClearance = Math.max(
      filterCollapsedHeight,
      filterSurface.getBoundingClientRect().height,
    );
    window.scrollTo({
      top: Math.max(0, shelfDocumentTop - stickyClearance - 12),
      left: 0,
      behavior: "instant",
    });
    comparisonStarterShelf.querySelector<HTMLButtonElement>(
      comparisonShelfMode === "search"
        ? "[data-comparison-search-all]"
        : "[data-comparison-starter-all]",
    )?.focus({ preventScroll: true });
    scheduleAutoLoadIfNeeded();
  } else if (button.hasAttribute("data-comparison-starter-clear")) {
    comparisonShelfQuery = "";
    activeComparisonStarterFamily = null;
    comparisonShelfLimit = COMPARISON_PAGE_SIZE;
    renderComparisonStarterShelf();
    comparisonStarterShelf.querySelector<HTMLInputElement>("#comparison-starter-search")?.focus({ preventScroll: true });
  }
});
comparisonStarterShelf.addEventListener("input", (event) => {
  const input = event.target as HTMLInputElement;
  if (input.id !== "comparison-starter-search") return;
  // Count one nonempty search episode, not keystrokes. Never send its text.
  if (!comparisonShelfQuery.trim() && input.value.trim()) queueActivity("comparisonSearches");
  comparisonShelfQuery = input.value.slice(0, 100);
  comparisonShelfLimit = COMPARISON_PAGE_SIZE;
  activeComparisonStarterFamily = null;
  // Keep the real input node, caret and keyboard alive while filtering groups.
  comparisonStarterShelf.querySelector<HTMLElement>("[data-comparison-starter-grid]")!.innerHTML = comparisonShelfGridMarkup(comparisonShelfView());
  comparisonStarterShelf.querySelector<HTMLButtonElement>("[data-comparison-starter-clear]")!.hidden = !comparisonShelfQuery;
  scheduleComparisonAutoLoadIfNeeded();
});

productGrid.addEventListener("click", (event) => {
  const productLink = (event.target as Element).closest<HTMLAnchorElement>(".product-card a[href]");
  if (productLink) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    // Preserve native open-in-new-tab, but enhance an ordinary click to the
    // existing fast product dialog once this card is owned by the renderer.
    if (!productLink.closest("[data-card-key]")) return;
    event.preventDefault();
  }
  const multiButton = (event.target as Element).closest<HTMLButtonElement>("button[data-multi-search-more], button[data-multi-search-collapse]");
  if (multiButton) {
    const key = multiButton.dataset.multiSearchMore ?? multiButton.dataset.multiSearchCollapse ?? "";
    const [indexText, scope] = key.split(":");
    const group = multiSearchGroups[Number(indexText)];
    const rows = scope === "primary" ? group?.primary : scope === "alternatives" ? group?.alternatives?.products
      : group?.recovery.groups.find(item => item.scope === scope)?.products;
    if (!rows || !group || !scope) return;
    const expanding = Boolean(multiButton.dataset.multiSearchMore);
    const preview = multiSearchPreview(group, scope);
    multiSearchLimits.set(key, expanding ? Math.min(rows.length, (multiSearchLimits.get(key) ?? preview) + MULTI_SEARCH_PAGE) : preview);
    const y = window.scrollY;
    renderProducts();
    if (expanding) window.scrollTo({ top: y, left: 0, behavior: "instant" });
    else {
      const section = productGrid.querySelector<HTMLElement>(`#multi-search-${Number(indexText)}`);
      if (section) window.scrollTo({ top: Math.max(0, section.getBoundingClientRect().top + window.scrollY - filterSurface.getBoundingClientRect().height - 12), left: 0, behavior: "instant" });
    }
    const next = productGrid.querySelector<HTMLElement>(`[data-multi-search-more="${CSS.escape(key)}"], [data-multi-search-collapse="${CSS.escape(key)}"]`);
    next?.focus({ preventScroll: true });
    return;
  }
  const watchButton = (event.target as Element).closest<HTMLButtonElement>("button[data-watch-query]");
  if (watchButton?.dataset.watchQuery) {
    hapticTap(telegram);
    void openQueryPriceWatchEditor(watchButton.dataset.watchQuery);
    return;
  }
  const recoveryButton = (event.target as Element).closest<HTMLButtonElement>(
    "button[data-search-recovery-scope]",
  );
  if (recoveryButton) {
    const group = searchRecovery.groups.find(
      (candidate) => candidate.scope === recoveryButton.dataset.searchRecoveryScope,
    );
    if (!group) return;
    Object.assign(filters, group.filters);
    setPersonalFilterMode("stores", filters.store === PERSONAL_STORES_FILTER ? "personal" : "all");
    setPersonalFilterMode("categories", filters.categoryId === PERSONAL_CATEGORIES_FILTER ? "personal" : "all");
    saveFilterSelection();
    hapticTap(telegram);
    resetAndRender();
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    return;
  }
  if (handleProductQuantityClick(event, (key) => renderedProducts.get(key))) return;
  const productWatchButton = (event.target as Element).closest<HTMLButtonElement>("button[data-price-watch-product]");
  if (productWatchButton) {
    const product = renderedProducts.get(productWatchButton.dataset.priceWatchProduct ?? "");
    if (product && !productWatchButton.disabled) {
      hapticTap(telegram);
      productWatchButton.disabled = true;
      productWatchButton.setAttribute("aria-busy", "true");
      void openProductPriceWatchEditor(product, true).finally(() => {
        productWatchButton.disabled = false;
        productWatchButton.removeAttribute("aria-busy");
      });
    }
    return;
  }
  const card = (event.target as Element).closest<HTMLElement>(
    ".product-card[data-card-key]",
  );
  const key = card?.dataset.cardKey;
  if (!key) {
    return;
  }
  const product = renderedProducts.get(key);
  if (product) {
    hapticTap(telegram);
    openProduct(product, {
      source: filters.query.trim()
          ? "search"
          : "feed",
    });
    if (filters.query.trim()) {
      signalHomeScreenUsefulAction();
    }
  }
});

dialogShoppingControl.addEventListener("click", (event) => {
  handleProductQuantityClick(event, (key) => activeDialogProduct && productKey(activeDialogProduct) === key
    ? activeDialogProduct : undefined);
});

dialogShareButton.addEventListener("click", () => {
  hapticTap(telegram);
  void shareProduct();
});

dialogMeta.addEventListener("click", (event) => {
  const target = event.target as Element;
  const watchButton = target.closest<HTMLButtonElement>(
    "button[data-price-watch-product]",
  );
  if (watchButton && activeDialogProduct) {
    hapticTap(telegram);
    void openProductPriceWatchEditor(activeDialogProduct);
    return;
  }
  const relatedButton = target.closest<HTMLButtonElement>(
    "button[data-related-product-key]",
  );
  const relatedKey = relatedButton?.dataset.relatedProductKey;
  if (relatedKey) {
    const relatedProduct = productRelationshipIndex.byKey.get(relatedKey);
    if (relatedProduct) {
      hapticTap(telegram);
      openRelatedProduct(relatedProduct);
    }
    return;
  }
  const button = (event.target as Element).closest<HTMLButtonElement>(
    "button[data-open-catalog-page]",
  );
  const product = activeDialogProduct;
  if (!button || !product) {
    return;
  }
  const catalog = productCatalogPage(product, dataset?.catalogs);
  if (!catalog) return;
  hapticTap(telegram);
  openCatalogViewer(
    catalog,
    product.page,
    productKey(product),
    {
      kind: "product",
      product,
      productHistory: [...productDialogHistory],
    },
  );
});

statusPanel.addEventListener("click", (event) => {
  const button = (event.target as Element).closest<HTMLButtonElement>(
    "button[data-watch-query]",
  );
  const query = button?.dataset.watchQuery;
  if (!query) {
    return;
  }
  hapticTap(telegram);
  void openQueryPriceWatchEditor(query);
});

catalogLibraryList.addEventListener("click", (event) => {
  const button = (event.target as Element).closest<HTMLButtonElement>(
    "button[data-open-catalog]",
  );
  const catalogId = button?.dataset.openCatalog;
  const catalog = catalogId
    ? dataset?.catalogs.find((candidate) => candidate.id === catalogId)
    : undefined;
  if (!button || button.disabled || !catalog) {
    return;
  }
  hapticTap(telegram);
  openCatalogViewer(catalog, 1, null, { kind: "library" });
});

catalogPageOverlays.addEventListener("click", (event) => {
  const keyboardTarget = (event.target as Element).closest<HTMLElement>("[data-catalog-hit-group]");
  if (keyboardTarget && catalogInteractionsEnabled && event.detail === 0 && activeCatalogViewerCatalog) {
    const group = catalogProductMarkerGroups(activeCatalogViewerCatalog, activeCatalogViewerPage)
      [Number(keyboardTarget.dataset.catalogHitGroup)];
    if (group) openCatalogHitProducts(group.products);
    return;
  }
  const groupButton = (event.target as Element).closest<HTMLButtonElement>(
    "button[data-catalog-product-group-index]",
  );
  const groupIndex = Number(groupButton?.dataset.catalogProductGroupIndex);
  const currentCatalog = activeCatalogViewerCatalog;
  const group =
    groupButton &&
    currentCatalog &&
    Number.isInteger(groupIndex) &&
    groupIndex >= 0
      ? catalogProductMarkerGroups(
          currentCatalog,
          activeCatalogViewerPage,
        )[groupIndex]
      : undefined;
  if (groupButton && group) {
    hapticTap(telegram);
    openCatalogProductChoices(group.products);
    return;
  }
  handleProductQuantityClick(event, (key) => currentCatalog
    ? catalogProductsOnPage(currentCatalog, activeCatalogViewerPage).find((candidate) => productKey(candidate) === key)
    : undefined);
});

catalogProductChoiceList.addEventListener("click", (event) => {
  const opener = (event.target as Element).closest<HTMLElement>("[data-catalog-open-product]");
  if (opener && catalogInteractionsEnabled) {
    const product = activeCatalogProductGroup.find((p) => productKey(p) === opener.dataset.catalogOpenProduct);
    if (product) { closeCatalogProductChoices(); openCatalogHitProducts([product]); }
    return;
  }
  handleProductQuantityClick(event, (key) => activeCatalogProductGroup.find((product) => productKey(product) === key));
});

catalogPagePrevious.addEventListener("click", () => {
  changeCatalogViewerPage(-1);
});

catalogPageNext.addEventListener("click", () => {
  changeCatalogViewerPage(1);
});

catalogShoppingButton.addEventListener("click", () => {
  if (!catalogInteractionsEnabled) return;
  hapticTap(telegram);
  openShoppingList();
});

function openCatalogHitProducts(matches: ProductView[]): void {
  if (!catalogInteractionsEnabled || !matches.length) return;
  hapticTap(telegram);
  if (matches.length === 1) openProduct(matches[0]!, { source: "catalog" });
  else openCatalogProductChoices(matches, "open");
}

catalogStoreSaveAction.addEventListener("click", () => {
  saveCatalogStoreFromPrompt();
});

catalogStoreSaveClose.addEventListener("click", () => {
  dismissCatalogStoreSavePrompt();
});

catalogViewerShare.addEventListener("click", () => {
  hapticTap(telegram);
  void shareCatalog();
});

catalogViewerZoom.addEventListener("click", () => {
  hapticTap(telegram);
  setCatalogViewerScale(
    catalogViewerScale > CATALOG_ZOOM_MIN + 0.01
      ? CATALOG_ZOOM_MIN
      : CATALOG_ZOOM_BUTTON_SCALE,
  );
});

catalogViewerBack.addEventListener("click", () => {
  hapticTap(telegram);
  closeCatalogViewer(true);
});

catalogViewerClose.addEventListener("click", () => {
  hapticTap(telegram);
  // The cross leaves the entire reader trail for all current catalogs.
  // Back still restores a product/parent when the reader was opened from it.
  catalogReturnFrames.length = 0;
  catalogViewerReturn = { kind: "library" };
  closeCatalogViewer(true);
});

catalogPageImageElement.addEventListener("load", () => {
  catalogPageImageElement.classList.remove("is-loading");
  if (
    catalogPageImageElement.naturalWidth > 0 &&
    catalogPageImageElement.naturalHeight > 0
  ) {
    catalogPageCanvas.style.aspectRatio = `${catalogPageImageElement.naturalWidth} / ${catalogPageImageElement.naturalHeight}`;
    renderCatalogPageOverlays();
  }
});

catalogPageImageElement.addEventListener("error", () => {
  catalogPageImageElement.classList.remove("is-loading");
});

catalogPageScroll.addEventListener("click", (event) => {
  if (activeCatalogQuantityKey && !(event.target as Element).closest("button, [data-quantity-key]")) {
    activeCatalogQuantityKey = null;
    updateCatalogShoppingControls();
  }
  const point = catalogTap.consume(performance.now());
  if (!point || !catalogInteractionsEnabled || !activeCatalogViewerCatalog ||
    (event.target as Element).closest("button, [data-quantity-key]") ||
    catalogPageImageElement.classList.contains("is-loading") || !catalogPageImageElement.naturalWidth) return;
  openCatalogHitProducts(catalogProductsAtPoint(
    catalogProductsOnPage(activeCatalogViewerCatalog, activeCatalogViewerPage),
    point, catalogPageImageElement.getBoundingClientRect(),
  ));
});

catalogPageScroll.addEventListener("pointerdown", (event) => {
  if (!catalogInteractionsEnabled || !event.isPrimary || event.button !== 0 || catalogPinchState ||
    (event.target as Element).closest("button, [data-quantity-key]")) { catalogTap.cancel(); return; }
  catalogTap.begin(event.pointerId, { x: event.clientX, y: event.clientY }, performance.now());
});
catalogPageScroll.addEventListener("pointermove", (event) =>
  catalogTap.move(event.pointerId, { x: event.clientX, y: event.clientY }));
catalogPageScroll.addEventListener("pointerup", (event) =>
  catalogTap.end(event.pointerId, { x: event.clientX, y: event.clientY }, performance.now()));
catalogPageScroll.addEventListener("pointercancel", () => catalogTap.cancel());
catalogPageScroll.addEventListener("scroll", () => catalogTap.cancel(), { passive: true });

catalogPageScroll.addEventListener("pointerdown", (event) => {
  if (
    catalogViewerScale > CATALOG_ZOOM_MIN + 0.01 ||
    catalogPinchState ||
    event.button !== 0 ||
    (event.target as Element).closest("button, [data-quantity-key]")
  ) {
    return;
  }
  catalogSwipeStart = {
    x: event.clientX,
    y: event.clientY,
    pointerId: event.pointerId,
  };
});

catalogPageScroll.addEventListener("pointerup", (event) => {
  const start = catalogSwipeStart;
  catalogSwipeStart = null;
  if (
    !start ||
    start.pointerId !== event.pointerId ||
    catalogViewerScale > CATALOG_ZOOM_MIN + 0.01 ||
    catalogPinchState
  ) {
    return;
  }
  const deltaX = event.clientX - start.x;
  const deltaY = event.clientY - start.y;
  if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) {
    return;
  }
  changeCatalogViewerPage(deltaX < 0 ? 1 : -1);
});

catalogPageScroll.addEventListener("pointercancel", () => {
  catalogSwipeStart = null;
});

catalogPageScroll.addEventListener(
  "touchstart",
  (event) => {
    if (event.touches.length !== 2) {
      return;
    }
    catalogTap.cancel();
    const distance = catalogTouchDistance(event.touches);
    if (distance <= 0) {
      return;
    }
    event.preventDefault();
    catalogSwipeStart = null;
    catalogPinchState = {
      distance,
      scale: catalogViewerScale,
    };
    catalogPageScroll.classList.add("is-pinching");
  },
  { passive: false },
);

catalogPageScroll.addEventListener(
  "touchmove",
  (event) => {
    if (!catalogPinchState || event.touches.length !== 2) {
      return;
    }
    const midpoint = catalogTouchMidpoint(event.touches);
    if (!midpoint) {
      return;
    }
    event.preventDefault();
    setCatalogViewerScale(
      catalogPinchZoomScale(
        catalogPinchState.scale,
        catalogPinchState.distance,
        catalogTouchDistance(event.touches),
      ),
      midpoint,
      false,
    );
  },
  { passive: false },
);

catalogPageScroll.addEventListener("touchend", (event) => {
  if (event.touches.length < 2) {
    finishCatalogPinch();
  }
});

catalogPageScroll.addEventListener("touchcancel", finishCatalogPinch);

shoppingListButton.addEventListener("click", () => {
  hapticTap(telegram);
  openShoppingList();
});

shoppingShareButton.addEventListener("click", () => {
  hapticTap(telegram);
  void shareShoppingList();
});

shoppingListContent.addEventListener("click", (event) => {
  const target = event.target as Element;
  const storeShare = target.closest<HTMLButtonElement>("button[data-share-store]");
  if (storeShare) {
    if (shoppingSharingEnabled && !storeShare.disabled) {
      hapticTap(telegram);
      void shareShoppingList(storeShare.dataset.shareStore);
    }
    return;
  }
  const cleanup = target.closest<HTMLButtonElement>("button[data-expired-shopping]");
  if (cleanup) {
    hapticTap(telegram);
    if (cleanup.dataset.expiredShopping === "confirm" && confirmExpiredShoppingRemoval) {
      const retained = removeExpiredShoppingItems(shoppingItems, currentShoppingDateKey());
      expiredShoppingRemovedCount = shoppingItems.length - retained.length;
      confirmExpiredShoppingRemoval = false;
      if (expiredShoppingRemovedCount) {
        queueActivity("shoppingRemoves", expiredShoppingRemovedCount);
        persistShoppingList(retained);
      } else renderShoppingList();
      select<HTMLButtonElement>("#shopping-list-close").focus({ preventScroll: true });
    } else {
      confirmExpiredShoppingRemoval = cleanup.dataset.expiredShopping === "request";
      expiredShoppingRemovedCount = 0;
      renderShoppingList();
      shoppingListContent.querySelector<HTMLElement>(".shopping-expired-cleanup")
        ?.scrollIntoView({ block: "nearest", behavior: "instant" });
      shoppingListContent.querySelector<HTMLButtonElement>(confirmExpiredShoppingRemoval
        ? '[data-expired-shopping="cancel"]' : '[data-expired-shopping="request"]')?.focus({ preventScroll: true });
    }
    return;
  }
  const button = target.closest<HTMLButtonElement>(
    "button[data-shopping-action][data-shopping-key]",
  );
  const action = button?.dataset.shoppingAction;
  const key = button?.dataset.shoppingKey;
  const item = key ? shoppingItems.find((candidate) => candidate.key === key) : undefined;
  if (button && !button.disabled && action && key && item) {
    hapticTap(telegram);
    if (action === "remove") {
      queueActivity("shoppingRemoves");
      persistShoppingList(shoppingItems.filter((candidate) => candidate.key !== key));
      return;
    }
    const delta = action === "increase" ? 1 : action === "decrease" ? -1 : 0;
    if (delta !== 0) {
      persistShoppingList(
        setShoppingQuantity(shoppingItems, key, item.quantity + delta),
      );
    }
    return;
  }

  const productButton = target.closest<HTMLButtonElement>(
    "button[data-shopping-open-product]",
  );
  const productKeyValue = productButton?.dataset.shoppingOpenProduct;
  const shoppingItem = productKeyValue
    ? shoppingItems.find((candidate) => candidate.key === productKeyValue)
    : undefined;
  const product = productKeyValue
    ? products.find((candidate) => productKey(candidate) === productKeyValue)
    : undefined;
  if (
    !shoppingItem ||
    shoppingOfferExpired(shoppingItem, currentShoppingDateKey()) ||
    !product
  ) {
    return;
  }
  hapticTap(telegram);
  openProduct(product);
});

if ("IntersectionObserver" in window) {
  const feedObserver = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        scheduleAutoLoadIfNeeded();
      }
    },
    { rootMargin: "700px 0px" },
  );
  feedObserver.observe(feedSentinel);
}
window.addEventListener("scroll", scheduleAutoLoadIfNeeded, { passive: true });
window.addEventListener("scroll", scheduleComparisonAutoLoadIfNeeded, { passive: true });
window.addEventListener("scroll", scheduleComparisonCollapsePosition, { passive: true });
window.addEventListener("scroll", scheduleFilterCollapse, { passive: true });
window.addEventListener("resize", () => {
  scheduleAutoLoadIfNeeded();
  scheduleComparisonAutoLoadIfNeeded();
  resetComparisonCollapsePosition();
  scheduleComparisonCollapsePosition();
  scheduleFilterMeasurement();
  if (catalogViewerDialog.open && activeCatalogViewerCatalog) {
    setCatalogViewerScale(catalogViewerScale, undefined, false);
    renderCatalogPageOverlays();
  }
});

dialogCloseButton.addEventListener("click", closeOrReturnProductDialog);
dialog.addEventListener("click", (event) => {
  if (event.target === dialog) {
    closeOrReturnProductDialog();
  }
});
dialog.addEventListener("cancel", (event) => {
  event.preventDefault();
  closeOrReturnProductDialog();
});
dialog.addEventListener("close", () => {
  if (dialog.open) return;
  const returnToCatalog = productFromCatalog;
  productFromCatalog = false;
  activeDialogProduct = null;
  productDialogHistory = [];
  updateProductDialogCloseButton();
  productDialogScrollLock.unlock();
  if (returnToCatalog && catalogViewerDialog.open) {
    (shoppingListDialog.open ? select<HTMLElement>("#shopping-list-close") : catalogPageCanvas)
      .focus({ preventScroll: true });
  }
});

select<HTMLButtonElement>("#choice-dialog-close").addEventListener(
  "click",
  closeChoiceDialog,
);
choiceDialog.addEventListener("click", (event) => {
  if (event.target === choiceDialog) {
    closeChoiceDialog();
  }
});
choiceDialog.addEventListener("close", () => {
  sortPicker.setAttribute("aria-expanded", "false");
  const trigger = activeChoiceTrigger;
  activeChoiceKind = null;
  activeChoiceTrigger = null;
  trigger?.focus({ preventScroll: true });
});

select<HTMLButtonElement>("#catalog-library-close").addEventListener(
  "click",
  closeCatalogLibrary,
);
catalogLibraryDialog.addEventListener("click", (event) => {
  if (event.target === catalogLibraryDialog) {
    closeCatalogLibrary();
  }
});
catalogLibraryDialog.addEventListener("close", () => {
  catalogPicker.setAttribute("aria-expanded", "false");
  if (!catalogViewerTransitioning) {
    catalogPicker.focus({ preventScroll: true });
  }
});

select<HTMLButtonElement>("#catalog-product-choice-close").addEventListener(
  "click",
  closeCatalogProductChoices,
);
catalogProductChoiceDialog.addEventListener("click", (event) => {
  if (event.target === catalogProductChoiceDialog) {
    closeCatalogProductChoices();
  }
});
catalogProductChoiceDialog.addEventListener("cancel", (event) => {
  event.preventDefault();
  closeCatalogProductChoices();
});
catalogProductChoiceDialog.addEventListener("close", () => {
  activeCatalogProductGroup = [];
});

catalogViewerDialog.addEventListener("cancel", (event) => {
  event.preventDefault();
  closeCatalogViewer(true);
});
catalogViewerDialog.addEventListener("close", () => {
  if (catalogViewerDialog.open) return;
  const returnTarget = pendingCatalogViewerReturn;
  const parent = returnTarget ? catalogReturnFrames.pop() : undefined;
  if (!returnTarget) catalogReturnFrames.length = 0;
  catalogTap.cancel();
  hideCatalogStoreSavePrompt(true);
  catalogStorePromptCatalogId = null;
  catalogStorePromptVisitedPages.clear();
  pendingCatalogViewerReturn = null;
  catalogViewerReturn = null;
  activeCatalogViewerCatalog = null;
  activeCatalogHighlightKey = null;
  catalogSwipeStart = null;
  catalogPinchState = null;
  catalogPageScroll.classList.remove("is-pinching");
  setCatalogViewerScale(CATALOG_ZOOM_MIN);
  if (parent) {
    const previousCatalog = dataset?.catalogs.find((c) => c.id === parent.catalogId);
    if (previousCatalog) {
      activeCatalogViewerCatalog = previousCatalog;
      activeCatalogViewerPage = clampCatalogPage(parent.page, previousCatalog.pages);
      activeCatalogHighlightKey = parent.highlight;
      catalogViewerReturn = parent.returnTarget;
      catalogViewerDialog.showModal();
      // Restore geometry immediately: animating from the child width would clamp
      // scrollTop/Left to zero before the parent's zoomed page has its full size.
      catalogPageCanvas.style.transition = "none";
      catalogPageCanvas.style.aspectRatio = parent.aspectRatio;
      renderCatalogViewer();
      setCatalogViewerScale(parent.scale, undefined, false);
      // Restoring image dimensions can complete asynchronously after another issue.
      const restoreScroll = () => {
        if (activeCatalogViewerCatalog?.id !== parent.catalogId || activeCatalogViewerPage !== parent.page) return;
        catalogPageScroll.scrollTo({ top: parent.top, left: parent.left });
      };
      restoreScroll();
      requestAnimationFrame(() => {
        restoreScroll();
        catalogPageCanvas.style.transition = "";
      });
      if (!catalogPageImageElement.complete) {
        catalogPageImageElement.addEventListener("load", restoreScroll, { once: true });
      }
      if (parent.shopping) { openShoppingList(); shoppingListDialog.scrollTop = parent.shoppingTop; }
    } else catalogReturnFrames.length = 0;
  }
  if (returnTarget?.kind === "library") {
    openCatalogLibrary();
  } else if (returnTarget?.kind === "product") {
    productDialogHistory = [...returnTarget.productHistory];
    openProduct(returnTarget.product, {
      preserveHistory: true,
      trackActivity: false,
    });
  }
});

preferenceDialogClose.addEventListener("click", requestClosePreferenceDialog);
preferenceUnsavedDiscard.addEventListener("click", finishClosePreferenceDialog);
preferenceUnsavedSave.addEventListener("click", applyPreferenceSelection);
preferenceDialog.addEventListener("click", (event) => {
  if (event.target === preferenceDialog && preferenceUnsavedOverlay.hidden) {
    requestClosePreferenceDialog();
  }
});
preferenceDialog.addEventListener("cancel", (event) => {
  event.preventDefault();
  if (!preferenceUnsavedOverlay.hidden) {
    hidePreferenceUnsavedPrompt();
    preferenceDialogClose.focus({ preventScroll: true });
    return;
  }
  requestClosePreferenceDialog();
});
preferenceDialog.addEventListener("close", () => {
  const trigger = activePreferenceTrigger;
  hidePreferenceUnsavedPrompt();
  trigger?.setAttribute("aria-expanded", "false");
  activePreferenceKind = null;
  activePreferenceTrigger = null;
  draftPreferenceValues.clear();
  draftCatalogNotificationStores.clear();
  preferenceDialogOptions = [];
  trigger?.focus({ preventScroll: true });
});

select<HTMLButtonElement>("#shopping-list-close").addEventListener(
  "click",
  closeShoppingList,
);
shoppingListDialog.addEventListener("click", (event) => {
  if (event.target === shoppingListDialog) {
    closeShoppingList();
  }
});
shoppingListDialog.addEventListener("close", () => {
  if (shoppingListDialog.open) return;
  confirmExpiredShoppingRemoval = false;
  expiredShoppingRemovedCount = 0;
  shoppingListButton.setAttribute("aria-expanded", "false");
  catalogShoppingButton.setAttribute("aria-expanded", "false");
  const trigger = shoppingListFromCatalog && catalogViewerDialog.open ? catalogShoppingButton : shoppingListButton;
  shoppingListFromCatalog = false;
  trigger.focus({ preventScroll: true });
});

select<HTMLButtonElement>("#shared-list-close").addEventListener("click", () => {
  sharedListDialog.close();
});
sharedListDialog.addEventListener("click", (event) => {
  if (event.target === sharedListDialog) {
    sharedListDialog.close();
  }
});
sharedListContent.addEventListener("click", (event) => {
  const button = (event.target as Element).closest<HTMLButtonElement>(
    "button[data-shared-item-search]",
  );
  if (!button) {
    return;
  }
  hapticTap(telegram);
  findCurrentOffersForSharedItem();
});
sharedListImport.addEventListener("click", () => {
  hapticTap(telegram);
  importSharedShoppingList();
});

priceWatchButton.addEventListener("click", () => {
  hapticTap(telegram);
  void openPriceWatchList();
});

select<HTMLButtonElement>("#price-watch-list-close").addEventListener(
  "click",
  closePriceWatchList,
);
priceWatchListDialog.addEventListener("click", (event) => {
  if (event.target === priceWatchListDialog) {
    closePriceWatchList();
  }
});
priceWatchListDialog.addEventListener("cancel", (event) => {
  event.preventDefault();
  closePriceWatchList();
});
priceWatchListDialog.addEventListener("close", () => {
  priceWatchListOpenToken += 1;
  priceWatchButton.setAttribute("aria-expanded", "false");
  if (!priceWatchEditorDialog.open) {
    priceWatchButton.focus({ preventScroll: true });
  }
});
priceWatchListContent.addEventListener("click", (event) => {
  const target = event.target as Element;
  const alertButton = target.closest<HTMLButtonElement>(
    "button[data-alert-product-key]",
  );
  const alertProduct = alertButton?.dataset.alertProductKey
    ? productRelationshipIndex.byKey.get(alertButton.dataset.alertProductKey)
    : undefined;
  if (alertProduct) {
    hapticTap(telegram);
    closePriceWatchList();
    openProduct(alertProduct);
    return;
  }
  const editButton = target.closest<HTMLButtonElement>("button[data-watch-edit]");
  const editRule = editButton?.dataset.watchEdit
    ? priceWatchState.rules.find((rule) => rule.id === editButton.dataset.watchEdit)
    : undefined;
  if (editRule) {
    hapticTap(telegram);
    priceWatchReturnToList = { scrollTop: priceWatchListContent.scrollTop, ruleId: editRule.id };
    // Keep the list mounted underneath the native modal: closing settings must
    // not reopen a loading screen, reset scrolling, or repeat mark-read calls.
    showPriceWatchEditor(editRule);
    return;
  }
  const toggleButton = target.closest<HTMLButtonElement>(
    "button[data-watch-toggle]",
  );
  const ruleId = toggleButton?.dataset.watchToggle;
  const enabled = toggleButton?.dataset.watchEnabled === "true";
  const initData = telegram.webApp?.initData;
  if (!toggleButton || !ruleId || !initData || priceWatchMutationInProgress) {
    return;
  }
  hapticTap(telegram);
  priceWatchMutationInProgress = true;
  toggleButton.disabled = true;
  void togglePriceWatch(initData, ruleId, enabled)
    .then(applyPriceWatchState)
    .catch(() => window.alert(t("watchSaveFailed")))
    .finally(() => {
      priceWatchMutationInProgress = false;
    });
});

priceWatchForm.addEventListener("submit", (event) => {
  event.preventDefault();
  hapticTap(telegram);
  void submitPriceWatch();
});
window.visualViewport?.addEventListener("resize", syncPriceWatchViewport);
window.visualViewport?.addEventListener("scroll", syncPriceWatchViewport);
window.addEventListener("resize", syncPriceWatchViewport);
window.visualViewport?.addEventListener("resize", syncShoppingViewport);
window.visualViewport?.addEventListener("scroll", syncShoppingViewport);
window.addEventListener("resize", syncShoppingViewport);
priceWatchForm.addEventListener("change", (event) => {
  const input = event.target as HTMLInputElement;
  if (input === priceWatchDefaultStoresInput) priceWatchDefaultDirty = true;
  if (input === priceWatchDefaultStoresInput || input.name === "price-watch-stores") {
    updatePriceWatchDefaultOption();
  }
  if (input.name === "price-watch-price-mode") {
    updatePriceWatchEditorFields();
  }
  if (
    editingPriceWatch &&
    (input.name === "price-watch-match" || input.name === "price-watch-stores")
  ) {
    priceWatchTargetSummary.textContent = priceWatchRuleSummary({
      ...editingPriceWatch,
      matchMode:
        editingPriceWatch.targetKind === "query" ? "query" :
          radioValue("price-watch-match") === "analogues" ? "analogues" : "exact",
      stores:
        radioValue("price-watch-stores") === "personal"
          ? (personalPreferences.stores.length
              ? personalPreferences.stores : editingPriceWatch.stores)
          : [],
    });
  }
});
priceWatchDelete.addEventListener("click", () => {
  hapticTap(telegram);
  void removeEditingPriceWatch();
});
select<HTMLButtonElement>("#price-watch-editor-close").addEventListener(
  "click",
  closePriceWatchEditor,
);
priceWatchEditorDialog.addEventListener("click", (event) => {
  if (event.target === priceWatchEditorDialog) {
    closePriceWatchEditor();
  }
});
priceWatchEditorDialog.addEventListener("cancel", (event) => {
  event.preventDefault();
  closePriceWatchEditor();
});
priceWatchEditorDialog.addEventListener("close", () => {
  if (priceWatchEditorDialog.open) return;
  const returnProduct = priceWatchReturnProduct;
  const returnToList = priceWatchReturnToList;
  const returnToFeed = priceWatchReturnToFeed;
  priceWatchReturnProduct = null;
  priceWatchReturnToList = null;
  priceWatchReturnToFeed = null;
  editingPriceWatch = null;
  if (returnProduct) {
    productDialogHistory = [...returnProduct.history];
    openProduct(returnProduct.product, {
      preserveHistory: true,
      trackActivity: false,
    });
  } else if (returnToList && priceWatchListDialog.open) {
    const trigger = priceWatchListContent.querySelector<HTMLButtonElement>(
      `button[data-watch-edit="${CSS.escape(returnToList.ruleId)}"]`,
    ) ?? select<HTMLButtonElement>("#price-watch-list-close");
    trigger.focus({ preventScroll: true });
    priceWatchListContent.scrollTop = returnToList.scrollTop;
  } else if (returnToFeed) {
    productGrid.querySelector<HTMLButtonElement>(
      `button[data-price-watch-product="${CSS.escape(returnToFeed.key)}"]`,
    )?.focus({ preventScroll: true });
    window.scrollTo({ top: returnToFeed.scrollY, left: 0, behavior: "instant" });
  }
});

document.addEventListener("close", () => {
    scheduleFilterStabilityCheck();
    scheduleHomeScreenOffer();
}, true); // Includes lazy-created fuel dialogs.
document.addEventListener("focusout", scheduleHomeScreenOffer);

function discoveryHomeAtRest(): boolean {
  return !!discoveryFeedVisit && !!dataset && discoveryFeedContext(filters) &&
    !activeComparisonStarterFamily && window.scrollY <= 24 &&
    document.activeElement !== searchInput && !document.querySelector("dialog[open]");
}

function filterVisitAtRest(): boolean {
  return !__KILOPRICE_WEBSITE__ && !!dataset && !activeComparisonStarterFamily &&
    // A chat deep link is an explicit search task, not the discovery home.
    // Briefly minimizing Telegram must not silently erase its results. Clearing
    // the query restores the ordinary new-visit reset behaviour.
    !(chatSearchEntry && filters.query.trim()) &&
    document.activeElement !== searchInput && !document.querySelector("dialog[open]") &&
    !appShareInProgress && !shareInProgress && !productShareInProgress && !catalogShareInProgress;
}

function resumeDiscoveryFeed(source: DiscoveryLifecycleSource): void {
  const renewDiscovery = discoveryFeedLifecycle.activate(discoveryHomeAtRest(), source);
  const newFilterVisit = filterVisitLifecycle.activate(filterVisitAtRest(), source);
  let resetView = false;
  if (newFilterVisit) {
    if (homeScreenPrompt.hidden && !botAccessPrompt.classList.contains("is-visible")) {
      homeScreenPromptGeneration += 1;
      homeScreenReturningVisitor ||= hadUsefulHomeScreenVisit();
      homeScreenUsefulActionPending = false;
      homeScreenExperimentEvaluationStarted = false;
      homeScreenAutoStatus = undefined;
      retentionPromptClaimedThisSession = false;
      storePromptEvaluatedThisSession = false;
    }
    storeButtonGlow.beginVisit();
    visitFilterHydration.keepCurrent();
    const next = filtersForNewVisit(filters);
    resetView = next.store !== filters.store || next.categoryId !== filters.categoryId ||
      next.catalogId !== filters.catalogId || next.sort !== filters.sort || next.query !== filters.query;
    if (resetView) {
      window.clearTimeout(searchTimer);
      window.clearTimeout(searchActivityTimer);
      searchActivityTimer = undefined;
      lastTrackedSearch = "";
      searchInput.value = "";
      Object.assign(filters, next);
      updateSearchClearButton();
      setPersonalFilterMode("stores", filters.store === PERSONAL_STORES_FILTER ? "personal" : "all");
      setPersonalFilterMode("categories", filters.categoryId === PERSONAL_CATEGORIES_FILTER ? "personal" : "all");
      saveFilterSelection();
      saveSortMode(filters.sort);
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  }
  if (renewDiscovery || (resetView && discoveryFeedContext(filters))) {
    discoveryFeedVisit?.renew(newDiscoverySeed());
    comparisonShelfAll = false;
    comparisonShelfQuery = "";
    comparisonShelfLimit = COMPARISON_PAGE_SIZE;
  }
  if (resetView) resetAndRender();
  else if (renewDiscovery) {
    visibleCount = PAGE_SIZE;
    renderProducts();
  }
  if (newFilterVisit) updateStoreButtonGlow();
}

// Native activation also covers a minimized WebView that never emits a new
// document load. The shared gate coalesces duplicate native/document events.
telegram.webApp?.onEvent?.("deactivated", () => {
  discoveryFeedLifecycle.deactivate(discoveryHomeAtRest(), "telegram");
  filterVisitLifecycle.deactivate(filterVisitAtRest(), "telegram");
});
onTelegramActivated(telegram, () => {
  resumeDiscoveryFeed("telegram");
  scheduleHomeScreenOffer();
});

window.setInterval(() => refreshActiveCatalogs(), 60_000);
document.addEventListener("visibilitychange", () => {
  updateFuelDiscovery();
  if (document.visibilityState === "visible") {
    refreshActiveCatalogs();
    resumeDiscoveryFeed("document");
    refreshUserStateIfNeeded();
    scheduleHomeScreenOffer();
    scheduleFilterStabilityCheck();
  } else {
    discoveryFeedLifecycle.deactivate(discoveryHomeAtRest(), "document");
    filterVisitLifecycle.deactivate(filterVisitAtRest(), "document");
    if (pendingUserStateMutation || activityAccumulator.hasPending()) void flushUserStateSync();
  }
});
window.addEventListener("pageshow", () => {
  resumeDiscoveryFeed("document");
  scheduleFilterStabilityCheck();
});
window.addEventListener("pagehide", () => {
  discoveryFeedLifecycle.deactivate(discoveryHomeAtRest(), "document");
  filterVisitLifecycle.deactivate(filterVisitAtRest(), "document");
  if (pendingUserStateMutation || activityAccumulator.hasPending()) {
    void flushUserStateSync();
  }
});

async function bootstrap(): Promise<void> {
  await activateLocale(appLocale);
  applyDocumentTranslations();
  updateLanguageToggle();
  updateThemeControl();
  initializeShoppingSharing();
  updateShoppingUi();
  updatePriceWatchFab();
  applyGridMode(gridMode, false);
  scheduleFilterMeasurement();
  void document.fonts.ready.then(scheduleFilterMeasurement);
  await start();
}

void bootstrap().catch((error) => {
  console.error("KiloPrice bootstrap failed", error);
});
